import type { SedTerminalFailureProofV1 } from '../lib/sed/sedTerminalFailure';
import { ConfirmedSedBootstrapFailure, parseSedTerminalFailureProof } from '../lib/sed/sedTerminalFailure';
import { deriveIntentForSelectedAssessment, assertPlanningSedAssessmentIdentity } from '../lib/generationImpact/selectedAssessmentIntent';
import { sentimentQueryContext, selectedSentimentTechs, recordSentimentHistory } from '../lib/sentiment/publicSentimentScope';
import { PUBLIC_SENTIMENT_SCAN_MAX_AGE_DAYS } from '../lib/sentiment/sentimentScanAge';
import { applySiteStudyArea } from '../lib/siteStudyArea';
import { collectSedDocumentFacts } from '../lib/sed/documentFacts';
import { stampDocumentFieldSources } from '../lib/sed/documentFieldSources';
import { fillMissingWaterServiceLeaves, hasMissingWaterServiceLeaves } from '../lib/sed/waterServiceGapFill';
import { documentNonAdoptionReason, retainDocumentSourceStatement } from '../lib/sed/documentAdoption';
import { buildDocumentEvidenceDigest, computeDocumentEvidenceQuality, statedDateForEvidence } from '../lib/sed/documentEvidenceDigest';
/**
 * Site Evidence Document bootstrap + top-up pipeline.
 *
 * Flow:
 *   - createSite → bootstrapSiteSed (fast providers only, ~5-8s).
 *     Site doc stamped with sedStatus: 'partial', sedId, sedStoragePath.
 *   - generateSiteRagReport → topUpSiteSed (deep providers when the
 *     report's required sections are still 'unavailable').
 *
 * Persistence:
 *   - SED JSON: GCS at users/{uid}/sites/{siteId}/evidence/{sedId}.json
 *   - Pointer: site doc fields (sedId/sedVersion/sedCollectedAt/
 *     sedCompleteness/sedStoragePath/sedStatus).
 *
 * The site doc is the source of truth for "is the SED ready". GCS holds
 * the fat content; Firestore holds the pointer.
 *
 * Idempotency: bootstrap returns early when the site already has a
 * non-failed SED. Top-up returns the list of sections it actually
 * upgraded so callers can short-circuit when nothing changed.
 */
import type { SiteEntity } from '../types/site';
import { createSiteDocumentBodyLoader, documentBodyText, hydrateDocumentBodiesForEvidence, type DocumentBodyLoader } from '../lib/documentBody';
import { attributionFromSegment } from '../lib/sed/evidenceAttribution';
import { detectSlotDocConflicts, flattenLeafValues, mergeDocConflicts, type DocValueWitness } from '../lib/sed/docConflicts';
import { EXTRACTED_TEXT_MAX_CHARS } from '../lib/docExtractionLimits';
import { mergeIntoContractPayload } from '../lib/sed/sectionContractBase';
import type { SedDocContentIndexEntry } from '../types/siteEvidence';
import type {
  SiteEvidenceDocument,
  EvidenceSection,
  EvidenceSourceContribution,
  EvidenceStatus,
  SedSectionId,
} from '../types/siteEvidence';
import type { ReportTypeId } from '../config/reportTypes';
// UPE-1 — per-doc extraction status writeback at the tail of
// `applyDocAwareConfidence`. Imported as a type only; the value-level
// store import stays lazy (dynamic) inside the function to preserve
// the existing test-time mockability.
import type { SiteDocument, DocumentExtractionOutcomeV1 } from '../types/siteRag';
import { documentMappingCounts, documentMappingSummary } from '../lib/documentMappingOutcome';
import type { AggregatedSiteData } from './dataProviders';
import { docsEligibleForEvidence } from '../lib/evidenceDocEligibility';
// Content-identity mirror for decision-analysis currency. Pure/browser-safe.
// INVARIANT: every site update in this file that writes `sedVersion` (or
// re-points `sedId`) must also stamp {sedContentHash, sedContentHashVersion}
// from the EXACT bytes it persisted — consumers only trust the mirror while
// sedContentHashVersion === sedVersion.
import { computeSedContentHashV2,
  computeSedSectionHashMirrors } from '../lib/decisionAnalysis/sedContentIdentity';
// #18 — physical precipitation bounds. Pure constants/functions; safe to
// import statically. Used by the SELF-HEAL guard below so a stored SED whose
// climate carries a pre-fix non-physical precip total forces a fresh
// open-meteo refetch on rebuild instead of carrying the stale value forward.
import {
  isPhysicalAnnualPrecip,
  isPhysicalMonthlyPrecip,
} from './dataProviders/climate/precipitationBounds';
import { DOC_EXTRACTION_TIMEOUT_MS } from './knowledge/extractionTiming';
import { documentExtractionProviderBlocked } from './knowledge/documentExtractionFailure';
import { sedSectionIntentInputHash } from '../lib/sed/sedRefreshPlan';
import { hasUsableExtractedText } from './knowledge/extractedTextQuality';
import {
  NO_MACHINE_READABLE_TEXT_MESSAGE,
  resolveForcedScanStatus,
} from './knowledge/extractionStatusPolicy';
// #sed-owner-path — the SED object key is built in ONE place (see the module
// header for the Redhead 2026-07-27 fork incident that made it a shared rule).
import { isPublishedStagedSedRevisionPath, resolveSedSavePath } from '../lib/sed/sedStoragePath';
import { canonicalJson } from '../lib/reportGeneration/reportContractHash';
import {
  SedTopUpSuperseded,
  sameSedPointer,
  sedPointerOf,
  type SedTopUpRefusal,
} from '../lib/sed/sedPointerCas';
import { graftProviderSection } from '../lib/sed/graftProviderSection';
import { carryForwardContributedField, keepPriorHabitatEvidence, withRefreshedNaaqsDesignations } from '../lib/sed/sectionContributions';
import { EPA_TABLE_COVERAGE, epaDesignationPredates } from '../lib/sed/epaExportCoverage';
import { reapplyInBandAmendments, type InBandAmendmentConflict } from '../lib/sed/reapplyInBandAmendments';
import type { SedCheckpoint, SedBootstrapStage } from '../lib/sed/sedCheckpoint';
import {
  CHECKPOINT_STAGES,
  CHECKPOINT_SCHEMA_VERSION,
  clearSedCheckpoint,
  computeIntentAndUploadHashes,
  readValidCheckpoint,
  SED_CHECKPOINT_SCHEMA_VERSION,
  writeSedCheckpoint,
} from '../lib/sed/sedCheckpoint';
import { isNrcsSsurgoDefaultEnabled } from '../config/siteDiligenceProviders';
import { abortableDelay, abortReason, rethrowIfAborted, throwIfAborted } from '../lib/abortSignal';
import { loadAppStore, loadAuthMissingProviderNotifier } from '../lib/appStore/appStorePort';
import { qualifyFictionalDocumentEvidence } from '../lib/demonstration/provenance';

const RELIEF_PROXY_SOURCE =
  'Deterministic elevation-relief / characteristic parcel-length proxy';

/**
 * Keep the legacy slope value available while grading it as an estimate, not
 * government terrain truth. Per-fact metadata avoids downgrading the valid
 * boundary/elevation facts carried by the same composite siteProfile section.
 */
function stampReliefProxyEvidence(
  section: SiteEvidenceDocument['siteProfile'],
  retrievedAt: string,
): SiteEvidenceDocument['siteProfile'] {
  if (!section.data) return section;
  const contribution: EvidenceSourceContribution = {
    path: 'slopePercent',
    grade: 'inference',
    qualification: 'estimated',
    source: RELIEF_PROXY_SOURCE,
    retrievedAt,
  };
  return {
    ...section,
    data: { ...section.data, slopeMethod: 'relief-proxy' },
    sourceContributions: [
      ...(section.sourceContributions ?? []).filter(item => item.path !== 'slopePercent'),
      contribution,
    ],
  };
}

/* ── Bootstrap-wait constants ───────────────────────────────────────── */
/* Shared by the store's report-generation wait loop AND the              */
/* SiteLandingPage bootstrap-wait banner so the copy + timeout stay       */
/* in sync. The fast sweep is bounded at ~5-8s wall + a buffer for        */
/* network jitter, so 60s is a generous ceiling rather than a target.     */
export const MAX_BOOTSTRAP_WAIT_MS = 60_000;
/** Longer wait when the user explicitly queued Generate while evidence
 *  collection was still in flight (bootstrapping / retrying). */
export const QUEUED_EVIDENCE_WAIT_MS = 5 * 60_000;
/** Exact progressStage string the store sets while waiting on bootstrap.
 *  The banner matches on this prefix. Keep in sync with both call sites. */
export const BOOTSTRAP_WAIT_PROGRESS_MESSAGE = 'Waiting for site evidence to finish collecting…';

/**
 * RSF-2 — Wall-clock budget for a single `bootstrapSiteSed` invocation.
 *
 * Generous ceiling: a clean cold-cache rebuild on a Redhead-shape site
 * (~80 user docs × 18 sections, ~5s per Gemini call) completes in 6-10
 * min. Warm-cache rebuilds finish in <30s once RSF-1's `docMatching/*`
 * and `extractionCache/*` rules are deployed.
 *
 * The watchdog exists ONLY to prevent the doom-loop that bit Redhead
 * on 2026-05-18 (rules-denied cache + render-driven re-firing +
 * silent error-swallowing → indefinite hang). On timeout the
 * bootstrap parks `sedStatus='failed'` + a structured `sedFailure`
 * breadcrumb so the UI surfaces the failure cleanly instead of the
 * "appears stuck" banner blocking forever. The Error is then
 * re-thrown so the caller (typically the fire-and-forget dispatch in
 * `retrySEDBootstrap`) can route it to a toast.
 *
 * Tests pass `__setBootstrapWallClockForTests(ms)` to compress the
 * window — the underlying race uses this value at call time, not at
 * module-load, so the override is hot.
 */
export const BOOTSTRAP_WALL_CLOCK_MS = 15 * 60 * 1000; // 15 min

let bootstrapWallClockOverrideMs: number | null = null;
/** Test hook — install a tighter wall-clock budget. Pass `null` to clear. */
export function __setBootstrapWallClockForTests(ms: number | null): void {
  bootstrapWallClockOverrideMs = ms;
}
function getBootstrapWallClockMs(): number {
  return bootstrapWallClockOverrideMs ?? BOOTSTRAP_WALL_CLOCK_MS;
}

/**
 * Module-local cache of the last `logProgress` stage observed per
 * siteId. Used by `markSedFailed` to stamp a precise `sedFailure.stage`
 * breadcrumb so post-mortem readers can tell whether the timeout fired
 * during `fetch-fast`, `doc-aware`, `conflict-detect`, etc. Cleared
 * lazily — stale entries from a prior bootstrap don't matter because
 * `logProgress` rewrites on every new rebuild's `queued` tick.
 */
const lastStageBySiteId = new Map<string, string>();

/** Test hook — expose the last-stage cache for hermetic assertions. */
export function __getLastStageForTests(siteId: string): string | undefined {
  return lastStageBySiteId.get(siteId);
}
/** Test hook — wipe the per-site stage tracker between cases. */
export function __resetLastStageForTests(): void {
  lastStageBySiteId.clear();
}

/**
 * RSF-2 — Write a structured `sedFailure` breadcrumb + flip
 * `sedStatus='failed'` so the UI's preflight banner can surface a
 * specific cause ("Previous rebuild failed after 15 min — Retry will
 * try again with a fresh start.") instead of the stuck-watchdog
 * generic. Best-effort: a failure to persist the breadcrumb must not
 * shadow the underlying timeout error.
 */
async function markSedFailed(
  siteId: string,
  failure: { reason: string; elapsedMs: number; stage: string },
): Promise<void> {
  try {
    const useStore = await loadAppStore();
    await useStore.getState().updateSite(siteId, {
      sedStatus: 'failed',
      sedFailureReason: failure.reason,
      sedFailure: {
        reason: failure.reason,
        elapsedMs: failure.elapsedMs,
        stage: failure.stage,
        at: new Date().toISOString(),
      },
    } as Partial<import('../types/site').SiteEntity>);
  } catch (e) {
    console.warn(
      `[SED-BOOTSTRAP] RSF-2 markSedFailed write failed for ${siteId}:`,
      (e as Error)?.message,
    );
  }
}

/* ── Provider partitioning ──────────────────────────────────────────── */
/* Fast = lightweight government APIs + cheap SDC lookups, ~5-8s wall.   */
/* Deep = NREL + PeeringDB + NRC scan + Gemini siteResearch, 20-60s.     */

export const FAST_PROVIDER_IDS = [
  'open-meteo',     // climate normals
  'usgs-seismic',   // SDC + ASCE design params
  'fema-flood',     // flood zone lookup
  'nrcs-soil-survey', // SSURGO regional soils screening (not a professional geotech)
  'hifld-grid',     // nearest substation
  'usfws-env',      // critical habitat / wetlands
  'wri-aqueduct',   // water stress
  'eia-electricity',// retail rate
  'airnow-aqi',     // current AQI
  'regulatory-db',  // state stance companion for jurisdiction evidence
  'jurisdiction-boundaries', // city limits / ETJ screening
  'usgs-mrds',      // public mineral occurrences
  'nps-nrhp',       // unrestricted public National Register properties
] as const;

export const DEEP_PROVIDER_IDS = [
  'nrel-solar',     // GHI/DNI
  'nrel-wind',      // wind toolkit
  'nrc-reactors',   // reactor proximity
  'peeringdb-ix',   // connectivity
  'site-research',  // Gemini AI synthesis
  'usgs-3dep-topography', // parcel-grid terrain screening
] as const;

/** Map SED section key → provider id that fills it. Used by topUp to decide
 *  whether deep providers can satisfy a `requiredSedSections` entry.
 *
 *  EVERY `SED_SECTION_IDS` entry has a mapping here (the Prompt-5 gap-fill
 *  coverage invariant — see syntheticProviders.test.ts). Values fall into
 *  three kinds:
 *    - REAL fetchable providers (open-meteo, nrel-solar, fema-nri, …) —
 *      dispatched by `fetchAllSiteData` / stamped by a dedicated compile step.
 *    - Synthetic wrappers (regulatory-db, nrc-adams, site-profile,
 *      external-literature, public-sentiment) — small in-repo gap-fillers.
 *    - Non-fetching sentinels for sections grounded WITHOUT a live external
 *      provider: `project-intent` / `financial-assumptions` / `kmz-derived`
 *      (stamped from declared intent / financial assumptions / KMZ), and
 *      `honest-unavailable` for sections whose real public source is not yet
 *      wired (transmissionHeadroom / waterResource — figures are NEVER
 *      fabricated; the section is stamped HONEST `unavailable` until a real
 *      fetcher lands). These sentinels are intentionally absent from
 *      DEEP_PROVIDER_IDS so topUp's gate never dispatches them. */
const SECTION_TO_PROVIDER_ID: Record<string, string> = {
  climate: 'open-meteo',
  seismic: 'usgs-seismic',
  flood: 'fema-flood',
  geotechnical: 'nrcs-soil-survey',
  grid: 'hifld-grid',
  environmental: 'usfws-env',
  waterStress: 'wri-aqueduct',
  electricity: 'eia-electricity',
  airQuality: 'airnow-aqi',
  solar: 'nrel-solar',
  wind: 'nrel-wind',
  nuclearInfra: 'nrc-reactors',
  connectivity: 'peeringdb-ix',
  siteResearch: 'site-research',
  parcel: 'regrid-parcels',
  regulatory: 'regulatory-db',
  adamsDocuments: 'nrc-adams',
  siteProfile: 'site-profile',
  // KSD1 — KMZ-derived; populated by `compileOnSiteInfrastructure`, not an
  // external provider. Mapping here satisfies the SED_SECTION_IDS coverage
  // invariant; topUp's DEEP_PROVIDER_IDS gate filters it out at runtime.
  onSiteInfrastructure: 'kmz-derived',
  // LL1 — populated by the `externalLiteratureOrchestrator` synthetic
  // provider (Crossref + arXiv + PubMed). Same gap-fill pattern as the
  // synthetic providers shipped in Content-Source Fixes Prompt 5.
  externalLiterature: 'external-literature',
  // PSE-1 — populated by the `publicSentimentOrchestrator` synthetic
  // provider (GDELT + local-government minutes + federal dockets). Same
  // gap-fill pattern; orchestrator skeleton ships in PSE-1, real source
  // fetchers in PSE-2.
  publicSentiment: 'public-sentiment',
  // Intent-stamped from Project Intent; no live provider. Mapping here
  // satisfies the SED_SECTION_IDS coverage invariant; topUp skips it.
  technology: 'project-intent',
  // v1.2.1 HR-5 — cooling / power / firmCapacity / lcoe are intent-stamped
  // (cooling/power/firmCapacity via stampBtmGroundingFromIntent; lcoe derives
  // from financialAssumptions). fuelSupply IS provider-grounded by the EIA
  // natural-gas provider. Mappings satisfy the SED_SECTION_IDS coverage invariant.
  cooling: 'project-intent',
  power: 'project-intent',
  firmCapacity: 'project-intent',
  lcoe: 'financial-assumptions',
  fuelSupply: 'eia-natural-gas',
  // SED D2 — interconnection IS provider-grounded by the deterministic
  // iso-queue provider; incentives is intent/financial-stamped (no live
  // provider). Mappings satisfy the SED_SECTION_IDS coverage invariant.
  interconnection: 'iso-queue',
  incentives: 'project-intent',
  // SED D2 (H-6) — multiHazard IS provider-grounded by the deterministic
  // FEMA National Risk Index provider (`fetchFemaNri`, stamped via
  // `compileMultiHazardAndResources`); the id matches that builder's
  // `provider:fema-nri` provenance. transmissionHeadroom + waterResource have
  // NO real provider wired yet — ATC / hosting-capacity / nodal-LMP and
  // state water-rights / USGS-NWIS / EPA-ECHO have no clean point-queryable
  // public API in-repo today. They are stamped as HONEST `unavailable`
  // (every value null + a disclaimer naming the follow-up source), NEVER a
  // fabricated figure (zero-unverified North Star). The `'honest-unavailable'`
  // id is a documented non-fetching sentinel — it is intentionally NOT in
  // DEEP_PROVIDER_IDS, so topUp's gate filters it out at runtime and it is
  // never dispatched as a synthetic gap-fill provider. These mappings exist
  // solely to satisfy the SED_SECTION_IDS coverage invariant; when a real
  // source lands, swap the id for the new provider's id.
  multiHazard: 'fema-nri',
  transmissionHeadroom: 'honest-unavailable',
  // RD-1 (D) — flipped from 'honest-unavailable': the usgs-waters
  // refdata-only provider grounds watershed + aquifer identity.
  waterResource: 'usgs-waters',
  topography: 'usgs-3dep-topography',
  survey: 'professional-document',
  mineralResources: 'usgs-mrds',
  culturalResources: 'nps-nrhp',
  // Server-owned immutable workforce/screening/comparable snapshots. Revision
  // drift routes through the full server bootstrap; scoped top-up must not
  // dispatch a browser provider for this section.
  economicEvidence: 'economic-snapshots',
};

/* ── LLW — External literature wiring ───────────────────────────────── */

/**
 * LLW-1 — TTL for the external-literature SED slot. Days after which
 * `maybeFetchExternalLiterature` will re-fetch even if the prior slot
 * is `'ok'`. Mirrors `SED_SECTION_TTL_DAYS.externalLiterature` so
 * sedStaleness UI + the bootstrap refresh cadence agree on the same
 * 14-day window. Kept local to the wiring helper so changing the
 * constant in `sedStaleness.ts` is the single source of truth.
 */
const EXTERNAL_LITERATURE_TTL_DAYS = 14;
const MS_PER_DAY_LLW = 24 * 60 * 60 * 1000;

/**
 * LLW-4 — Soft rate-limit window for per-site Live Literature
 * re-fetches. Mitigates pack-risk R6 ("Refresh button hammering by
 * impatient users") for sites whose prior slot is stale or absent:
 * the 14d TTL guard above prevents re-fetches when the slot is fresh,
 * but a stale slot would otherwise admit every click. 60 seconds is
 * the same default `skipIfFreshWithinMs` budget topUpSiteSed uses for
 * its other scoped refreshes — keeps the surface consistent.
 *
 * Tracked per `(siteId, ttlBucket)` so a TTL-override path doesn't
 * starve a real refresh. Process-local in-memory only; cleared on
 * worker / page reload (acceptable — a fresh worker won't have the
 * rate-limit context anyway).
 */
const EXTERNAL_LITERATURE_RATE_LIMIT_MS = 60_000;
const externalLiteratureLastFetchAtMs = new Map<string, number>();

/** Test hook — clear the rate-limit map between cases. */
export function __resetExternalLiteratureRateLimitForTests(): void {
  externalLiteratureLastFetchAtMs.clear();
}

/**
 * Resolve the report type to pass into the orchestrator's
 * `LITERATURE_QUERY_PROFILES` lookup. Prefers the explicit caller
 * override; falls back to `site.latestReportType` (set by the prior
 * report-generation flow) and finally `'feasibility'` so the call
 * always lands on a valid profile.
 */
function resolveLiteratureReportType(
  site: SiteEntity | undefined,
  override?: ReportTypeId,
): ReportTypeId {
  if (override) return override;
  return site?.latestReportType ?? 'feasibility';
}

/**
 * LLW-1 — Wire the externalLiteratureOrchestrator into a SED build.
 *
 * Mutates `sed.externalLiterature` in place when the prior slot is
 * missing or older than EXTERNAL_LITERATURE_TTL_DAYS. The orchestrator
 * is total (never throws) — caller may invoke unconditionally.
 *
 * Cost guard: the orchestrator issues ≤6 queries × 1-3 providers per
 * call. The TTL gate below keeps repeat bootstraps within the same
 * 14-day window from re-firing the call.
 *
 * @param sed  In-progress SED (mutated). Must already have its
 *             regulatory / waterStress sections populated for
 *             seed-term derivation to yield useful narrowing.
 * @param site Owning site, read for `latestReportType` defaulting.
 * @param prior Optional prior literature section (typically read from
 *             `existing.externalLiterature` on the top-up path). When
 *             still inside the TTL and prior `status !== 'unavailable'`,
 *             skip the call and reuse the prior payload.
 * @param siteId Used only for the audit-log emit + console breadcrumb.
 * @param overrides Test seam — override the report type and inject
 *             custom provider callers (forwarded to the orchestrator
 *             so no real network calls fire in tests).
 */
export async function maybeFetchExternalLiterature(
  sed: SiteEvidenceDocument,
  site: SiteEntity | undefined,
  prior: import('../types/siteEvidence').ExternalLiteratureSection | undefined,
  siteId: string,
  overrides?: {
    reportType?: ReportTypeId;
    providers?: import('./externalLiteratureOrchestrator').OrchestrateArgs['providers'];
    /**
     * LLW-4 — explicit telemetry breadcrumb for the audit emit's
     * `metadata.source` field. Defaults to `'refresh'` when a prior
     * slot is supplied (the topUp scoped-refresh path) and
     * `'bootstrap'` when no prior slot (the bootstrap first-fill
     * path). The scheduled-rebuild path passes `'scheduled'` so
     * Admin Activity can distinguish unattended re-fetches from
     * user-initiated ones. `'manual-section'` is passed by the LLW-
     * FOLLOWUP-SECTION-REFRESH-BUTTON path (per-section "Refresh
     * Live Literature" click from the SED Variable Browser) so the
     * admin Provider Posture surface can separate one-click user
     * refreshes from broader rebuild paths.
     */
    source?: 'bootstrap' | 'refresh' | 'scheduled' | 'manual-section';
    /**
     * Override the rate-limit window. Defaults to
     * `EXTERNAL_LITERATURE_RATE_LIMIT_MS` (60s). Pass `0` to bypass —
     * useful for the scheduled-rebuild path which already gates on
     * the 14-day TTL upstream, and for tests.
     */
    rateLimitMs?: number;
    /**
     * LX-2 — forward to the orchestrator's `providerCacheTtlMs`. The
     * default is 24h per the LX-2 brief; tests that want to exercise
     * back-to-back orchestrator dispatches independent of the cache
     * pass `0` to bypass.
     */
    providerCacheTtlMs?: number;
    /** When true, skip TTL reuse and in-memory rate-limit short-circuit —
     *  used when the user explicitly scoped a refresh to this section. */
    forceRefresh?: boolean;
    /** Attempt-owned report cancellation/watchdog signal. */
    abortSignal?: AbortSignal;
  },
): Promise<void> {
  throwIfAborted(overrides?.abortSignal);
  const nowMs = Date.now();

  // TTL check — reuse prior result when it's fresh AND not a hard
  // failure. 'partial' counts as fresh-enough; the orchestrator's
  // partial-success path is still a successful call.
  if (!overrides?.forceRefresh && prior && prior.status !== 'unavailable' && prior.fetchedAt) {
    const t = Date.parse(prior.fetchedAt);
    if (Number.isFinite(t)) {
      const ageDays = (nowMs - t) / MS_PER_DAY_LLW;
      if (ageDays >= 0 && ageDays < EXTERNAL_LITERATURE_TTL_DAYS) {
        // Within TTL — reuse the prior section.
        sed.externalLiterature = prior;
        return;
      }
    }
  }

  // LLW-4 — soft rate-limit. Process-local in-memory; mitigates pack
  // risk R6 (refresh-button hammering on stale slots).
  const rateLimitMs = overrides?.forceRefresh
    ? 0
    : (overrides?.rateLimitMs ?? EXTERNAL_LITERATURE_RATE_LIMIT_MS);
  if (rateLimitMs > 0) {
    const lastAt = externalLiteratureLastFetchAtMs.get(siteId);
    if (lastAt && nowMs - lastAt < rateLimitMs) {
      // Recently fetched — keep the prior slot (may be undefined; the
      // caller already saw that and asked us to refresh). The slot
      // stays at its prior value; sedStaleness will continue to render
      // the same chip until the rate-limit window expires.
      if (prior) sed.externalLiterature = prior;
      return;
    }
  }

  const source: 'bootstrap' | 'refresh' | 'scheduled' | 'manual-section' =
    overrides?.source ?? (prior ? 'refresh' : 'bootstrap');
  const reportType = resolveLiteratureReportType(site, overrides?.reportType);
  const startedAt = nowMs;
  try {
    const { orchestrateExternalLiterature } = await import('./externalLiteratureOrchestrator');
    throwIfAborted(overrides?.abortSignal);
    const result = await orchestrateExternalLiterature({
      sed,
      reportType,
      providers: overrides?.providers,
      // LX-2 — forward the optional cache-TTL override.
      providerCacheTtlMs: overrides?.providerCacheTtlMs,
      signal: overrides?.abortSignal,
    });
    throwIfAborted(overrides?.abortSignal);
    sed.externalLiterature = result;
    externalLiteratureLastFetchAtMs.set(siteId, Date.now());
    // LLW-4 — telemetry emit (soft-fail). Composes with the existing
    // auditLogClient surface; the helper is total + logs a debug line
    // on transport failure so the SED rebuild itself is never blocked
    // on the audit endpoint.
    //
    // We `await` the emit (not void-fire it) so the audit-log
    // authedFetch finishes before the bootstrap's next authedFetch
    // (the GCS save POST). Voiding the emit leaves a sibling pending
    // `await import('../firebase')` in flight which — under vitest's
    // dynamic-import mock cache — can race the GCS-save's own
    // `await import('../firebase')` and intermittently read
    // `auth.currentUser === null`, dropping the Authorization header
    // on the save POST. Awaiting the emit costs at most the round-trip
    // to the audit endpoint (404/405 today; the soft-fail helper
    // returns quickly either way). Composes with TSE-3's auditLogService.
    try {
      const { emitAuditEvent } = await import('../lib/auditLogClient');
      // LLW-4 — per-provider rollup so the future admin Provider
      // Posture row can reconstruct "Crossref 1200 attempts / 980 ok"
      // from the audit log alone, without re-running the orchestrator.
      // Each attempted provider gets {id, ok} where `ok` is true iff
      // the aggregate status didn't list that provider as a per-task
      // error. Tier breakdown stays cheap (≤3 entries) so the audit
      // payload doesn't bloat.
      const perProviderErrors = (result.reason ?? '').split(';').map(s => s.trim());
      const perProviderRollup = result.providersAttempted.map(pid => ({
        id: pid,
        ok: !perProviderErrors.some(e => e.startsWith(`${pid}:`)),
      }));
      await emitAuditEvent({
        action: 'external-literature.refreshed',
        target: { type: 'site', id: siteId, label: sed.siteName },
        metadata: {
          reportType,
          status: result.status,
          providersAttempted: result.providersAttempted,
          perProviderRollup,
          entriesReturned: result.entries.length,
          queriesIssued: result.queriesIssued.length,
          durationMs: Date.now() - startedAt,
          reason: result.reason,
          source,
        },
      });
    } catch {
      /* audit emit is best-effort; do not affect the rebuild */
    }
  } catch (e) {
    rethrowIfAborted(overrides?.abortSignal, e);
    // Defence-in-depth — the orchestrator's contract is never-throws,
    // but a bug in the import path or a global Promise rejection must
    // still leave the bootstrap able to persist the rest of the SED.
    console.warn(
      `[external-literature] orchestrator dispatch failed (site=${siteId}):`,
      (e as Error)?.message,
    );
  }
}

/* ── PSE — Public Sentiment wiring ──────────────────────────────────── */

/**
 * PSE-3 — TTL for the public-sentiment SED slot. Mirrors
 * `SED_SECTION_TTL_DAYS.publicSentiment` so the sedStaleness UI and this
 * bootstrap helper agree on the same window.
 *
 * Both read THE shared sentiment-scan age limit (sentimentScanAge.ts, owner
 * decision 2026-09-30): a dated scan up to 7 days old is reused instead of
 * re-read, and every report states the scan's own collection date, so an
 * older scan is never presented as today's read. The 60s rate-limit and the
 * request-scoped cache still bound cost across rapid regenerations.
 */
const PUBLIC_SENTIMENT_TTL_DAYS = PUBLIC_SENTIMENT_SCAN_MAX_AGE_DAYS;
const MS_PER_DAY_PSE = 24 * 60 * 60 * 1000;

/**
 * PSE-3 — soft per-site rate-limit for re-fetches. Mirrors the LLW
 * `EXTERNAL_LITERATURE_RATE_LIMIT_MS` cadence (60s) so the user-
 * facing "Refresh Evidence" button can't hammer the upstream sources
 * under a stale-slot scenario. Process-local in-memory only.
 *
 * Cost defence — PSE-1's 6th scoring axis runs at most once per site-
 * report-rebuild cycle today; the cache + rate-limit add a second
 * layer that bounds expensive orchestrator calls across rebuilds.
 */
const PUBLIC_SENTIMENT_RATE_LIMIT_MS = 60_000;
const publicSentimentLastFetchAtMs = new Map<string, number>();

/** Test hook — clear the rate-limit map between cases. */
export function __resetPublicSentimentRateLimitForTests(): void {
  publicSentimentLastFetchAtMs.clear();
}

/**
 * PSE-3 Firestore cache TTL. The same shared window (derived from
 * PUBLIC_SENTIMENT_TTL_DAYS above); the difference is that the SED-section TTL gates whether
 * we reuse the prior `sed.publicSentiment` block in place, while this
 * Firestore cache gates whether the orchestrator fan-out re-runs at
 * all. A cache HIT short-circuits the orchestrator call entirely; a
 * MISS or stale cache forces a fresh fan-out, then writes the result
 * back through `stripUndefined()` (CDS-1).
 */
const PSE_FIRESTORE_CACHE_TTL_MS = PUBLIC_SENTIMENT_TTL_DAYS * MS_PER_DAY_PSE;

/**
 * Firestore cache collection name. One doc per `(siteId, btmTechSet)`
 * pair — see `psecCacheKey` below for the composition.
 */
const PSE_CACHE_COLLECTION = 'pse-cache';

/**
 * Compose the Firestore cache document id for `(siteId, btmTechs)`.
 *
 * The prompt brief uses `${siteId}_${tech}` as a one-doc-per-site-tech
 * key, but the orchestrator runs ALL 5 techs per call (the BTM bucket
 * set is fixed). Caching at the (site, btmTechSet) granularity keeps
 * the doc count tiny — typically one row per site — while still
 * allowing a future caller to pass a narrower tech subset and get a
 * separate cache row for that variant.
 *
 * The techs are sorted so that `['nuclear-smr', 'solar-bess']` and
 * `['solar-bess', 'nuclear-smr']` hash to the same key (cache
 * commutativity invariant).
 */
function psecCacheKey(siteId: string, btmTechs: ReadonlyArray<string>): string {
  const sorted = [...btmTechs].sort();
  // Firestore doc-ids are limited to ~1500 bytes and can't contain
  // slashes; the underscore separator + sorted-tech tail keeps us
  // well inside the limit (siteId ≤ 40 chars + 5 × ≤24 chars).
  return `${siteId}_${sorted.join('+')}`;
}

/**
 * Cached payload shape — mirrors `PublicSentimentSection` plus the
 * cache metadata. Stored under `pse-cache/{psecCacheKey}`.
 */
interface PsecCacheRecord {
  /** The section payload as emitted by the orchestrator. */
  section: import('../types/siteEvidence').PublicSentimentSection;
  /** Wall-clock fetch time for TTL math. */
  fetchedAtMs: number;
}

/**
 * Test hook — read a cache document directly. Exposed so the cache
 * test suite can pin "miss → fetch → write → hit → no second fetch"
 * without reaching into Firestore internals.
 */
export async function __peekPublicSentimentCacheForTests(
  siteId: string,
  btmTechs: ReadonlyArray<string>,
): Promise<PsecCacheRecord | undefined> {
  try {
    const { db } = await import('../firebase');
    const { doc, getDoc } = await import('firebase/firestore');
    const ref = doc(db, PSE_CACHE_COLLECTION, psecCacheKey(siteId, btmTechs));
    const snap = await getDoc(ref);
    if (!snap.exists()) return undefined;
    const data = snap.data() as Partial<PsecCacheRecord> | undefined;
    if (!data || !data.section || !Number.isFinite(data.fetchedAtMs)) return undefined;
    return { section: data.section, fetchedAtMs: data.fetchedAtMs as number };
  } catch {
    return undefined;
  }
}

/**
 * Read the PSE cache for `(siteId, btmTechs)` if present AND younger
 * than `ttlMs`. Returns `undefined` on miss / stale / read error.
 * Stale entries are NOT auto-deleted — the next successful fetch will
 * overwrite the doc.
 */
async function readPsecCache(
  siteId: string,
  btmTechs: ReadonlyArray<string>,
  ttlMs: number,
  nowMs: number,
  expectedFingerprint: string,
): Promise<PsecCacheRecord | undefined> {
  try {
    const { db } = await import('../firebase');
    const { doc, getDoc } = await import('firebase/firestore');
    const ref = doc(db, PSE_CACHE_COLLECTION, psecCacheKey(siteId, btmTechs));
    const snap = await getDoc(ref);
    if (!snap.exists()) return undefined;
    const data = snap.data() as Partial<PsecCacheRecord> | undefined;
    if (!data || !data.section || !Number.isFinite(data.fetchedAtMs)) return undefined;
    const fetchedAtMs = data.fetchedAtMs as number;
    // Both the cache envelope and the actual scan must be within the same-day
    // window. A rewritten envelope cannot make stale source evidence current.
    // Negative or NaN ttlMs is rejected by
    // the caller; defence-in-depth check here for the read path too.
    if (!Number.isFinite(ttlMs) || ttlMs <= 0) return undefined;
    const scanAtMs = Date.parse(data.section.fetchedAt ?? '');
    if (data.section.status === 'unavailable'
      || data.section.queryContext?.fingerprint !== expectedFingerprint
      || !Number.isFinite(scanAtMs)
      || [fetchedAtMs, scanAtMs].some(at => nowMs - at < 0 || nowMs - at >= ttlMs)) return undefined;
    return { section: data.section, fetchedAtMs };
  } catch (err) {
    // PH-A1.10 — surface what was previously a silent catch. Cache
    // misses on quota / permissions should be visible in Cloud Run logs
    // without blocking the live-fetch fallback.
    console.warn(`[pse-cache] read failed (site=${siteId}):`, (err as Error)?.message);
    return undefined;
  }
}

/**
 * Write the PSE cache for `(siteId, btmTechs)`. Fire-and-forget — a
 * write failure logs a warn and the caller's flow is unaffected. The
 * payload is scrubbed through `stripUndefined()` (CDS-1 invariant) so
 * any optional field a PSE-2 fetcher emits as `undefined` doesn't
 * throw on the Firestore SDK's `Unsupported field value: undefined`.
 */
async function writePsecCache(
  siteId: string,
  btmTechs: ReadonlyArray<string>,
  section: import('../types/siteEvidence').PublicSentimentSection,
  fetchedAtMs: number,
): Promise<void> {
  try {
    const { db } = await import('../firebase');
    const { doc, setDoc } = await import('firebase/firestore');
    const { stripUndefined } = await import('../lib/firestore/stripUndefined');
    const ref = doc(db, PSE_CACHE_COLLECTION, psecCacheKey(siteId, btmTechs));
    const payload: PsecCacheRecord = { section, fetchedAtMs };
    await setDoc(ref, stripUndefined(payload));
  } catch (err) {
    console.warn(`[pse-cache] write failed (site=${siteId}):`, (err as Error)?.message);
  }
}

/**
 * PSE-3 — Wire the `publicSentimentOrchestrator` into a SED build.
 *
 * Mutates `sed.publicSentiment` in place when the prior slot is missing
 * or older than `PUBLIC_SENTIMENT_TTL_DAYS`. The orchestrator is total
 * (never throws) — caller may invoke unconditionally.
 *
 * Cache layers (defence in depth, in evaluation order):
 *   1. Prior `sed.publicSentiment` (in-memory, this rebuild) — reused
 *      when present, status ≠ 'unavailable', and fetchedAt is within
 *      the shared sentiment-scan age window (PUBLIC_SENTIMENT_TTL_DAYS).
 *   2. Firestore `pse-cache/{siteId}_{btmTechs.sorted.join('+')}` —
 *      read on Step-1 miss. A fresh cache hit reseeds
 *      `sed.publicSentiment` AND short-circuits the orchestrator call.
 *   3. In-memory per-site rate-limit (60 s) — caps repeat clicks on a
 *      stale slot from hammering the upstream sources.
 *   4. Orchestrator fan-out — when none of the above hits, run the
 *      orchestrator and write the result back to Firestore.
 *
 * @param sed       In-progress SED (mutated). Provides locality
 *                  derivation seeds (regulatory.state, location.address)
 *                  for the orchestrator's query composer.
 * @param prior     Optional prior section (typically `existing.publicSentiment`
 *                  on the top-up path). Reused verbatim when still
 *                  inside the shared sentiment-scan age window.
 * @param siteId    Used for the cache doc-id and the audit-log emit.
 * @param overrides Test seam — inject mock source fetchers; override
 *                  the rate-limit window; bypass the Firestore cache.
 */
export async function maybeFetchPublicSentiment(
  sed: SiteEvidenceDocument,
  prior: import('../types/siteEvidence').PublicSentimentSection | undefined,
  siteId: string,
  overrides?: {
    btmTechs?: ReadonlyArray<import('../types/siteEvidence').PublicSentimentBtmTech>;
    sources?: import('./publicSentimentOrchestrator').OrchestratePublicSentimentArgs['sources'];
    /** Pass `0` to bypass the rate-limit (used by tests). */
    rateLimitMs?: number;
    /**
     * Override the Firestore cache TTL. Defaults to the shared scan age window.
     * Pass `0` to bypass the cache entirely (always-fetch, never-write).
     */
    cacheTtlMs?: number;
    source?: 'bootstrap' | 'refresh' | 'scheduled' | 'manual-section';
    /** When true, skip TTL / Firestore cache / rate-limit short-circuits —
     *  used when the user explicitly scoped a refresh to this section. */
    forceRefresh?: boolean;
    /** Attempt-owned report cancellation/watchdog signal. */
    abortSignal?: AbortSignal;
  },
): Promise<void> {
  throwIfAborted(overrides?.abortSignal);
  const nowMs = Date.now();
  // Resolve btmTechs early so cache key composition is deterministic.
  // Default to ALL five BTM buckets (matches the orchestrator's own
  // default when no btmTechs is passed) so a top-up that doesn't know
  // which tech the user picked still has a usable cache.
  const btmTechs = overrides?.btmTechs ?? selectedSentimentTechs(sed);
  const terms = (await import('./publicSentimentOrchestrator')).deriveScopedSeedTerms(sed);
  const context = sentimentQueryContext(sed, { techs: btmTechs, localityTerms: terms.site, stateTerms: terms.state });
  const cacheTechs = [...btmTechs, context.fingerprint];
  const rateKey = `${siteId}|${context.fingerprint}`;

  // ── Layer 1: prior in-memory section ──
  if (!overrides?.forceRefresh && prior && prior.queryContext?.fingerprint === context.fingerprint && prior.status !== 'unavailable' && prior.fetchedAt) {
    const t = Date.parse(prior.fetchedAt);
    if (Number.isFinite(t)) {
      const ageDays = (nowMs - t) / MS_PER_DAY_PSE;
      if (ageDays >= 0 && ageDays < PUBLIC_SENTIMENT_TTL_DAYS) {
        sed.publicSentiment = prior;
        return;
      }
    }
  }

  // ── Layer 2: Firestore cache (skip when explicitly bypassed) ──
  const cacheTtlMs = overrides?.forceRefresh
    ? 0
    : (overrides?.cacheTtlMs === undefined
      ? PSE_FIRESTORE_CACHE_TTL_MS
      : Math.max(0, overrides.cacheTtlMs));
  if (cacheTtlMs > 0) {
    const cached = await readPsecCache(siteId, cacheTechs, cacheTtlMs, nowMs, context.fingerprint);
    throwIfAborted(overrides?.abortSignal);
    if (cached) {
      sed.publicSentiment = cached.section;
      return;
    }
  }

  // ── Layer 3: in-memory rate-limit ──
  const rateLimitMs = overrides?.forceRefresh
    ? 0
    : (overrides?.rateLimitMs ?? PUBLIC_SENTIMENT_RATE_LIMIT_MS);
  if (rateLimitMs > 0) {
    const lastAt = publicSentimentLastFetchAtMs.get(rateKey);
    if (lastAt && nowMs - lastAt < rateLimitMs) {
      // Recently fetched but we don't have a fresh slot to reuse.
      // Preserve any prior slot to avoid wiping previous evidence.
      if (prior?.queryContext?.fingerprint === context.fingerprint) sed.publicSentiment = prior;
      return;
    }
  }

  // ── Layer 4: orchestrator fan-out ──
  const source: 'bootstrap' | 'refresh' | 'scheduled' | 'manual-section' =
    overrides?.source ?? (prior ? 'refresh' : 'bootstrap');
  const startedAt = nowMs;
  try {
    const { orchestratePublicSentiment } = await import('./publicSentimentOrchestrator');
    throwIfAborted(overrides?.abortSignal);
    // PSR tier-1 activation — `siteId` gates BOTH the tier-1 deep-fetcher
    // fan-out (census-acs / bls / fcc / local-public-data / local-gis /
    // news-aggregator → tier1Facts + tier1CitationRefs + tier1SourcesAttempted
    // stamps) AND the image-observation source (PSR-FOLLOWUP-IIA-INGEST)
    // inside the orchestrator. Omitting it left the entire tier-1 sentiment
    // layer dead in production builds.
    const result = await orchestratePublicSentiment({
      sed,
      siteId,
      btmTechs,
      sources: overrides?.sources,
      signal: overrides?.abortSignal,
    });
    throwIfAborted(overrides?.abortSignal);
    sed.publicSentiment = recordSentimentHistory(result, prior, sed.id);
    publicSentimentLastFetchAtMs.set(rateKey, Date.now());

    // Write to cache (fire-and-forget). Only cache 'ok' or 'partial' —
    // 'unavailable' shouldn't crowd out a future retry on the next
    // rebuild.
    if (result.status !== 'unavailable' && cacheTtlMs > 0) {
      await writePsecCache(siteId, cacheTechs, result, Date.now());
      throwIfAborted(overrides?.abortSignal);
    }

    // Audit emit — best-effort, mirrors the LLW pattern (await so the
    // dynamic-import auth race doesn't drop the Authorization header
    // on the next save POST).
    try {
      const { emitAuditEvent } = await import('../lib/auditLogClient');
      const perSourceErrors = (result.reason ?? '').split(';').map(s => s.trim());
      const perSourceRollup = result.sourcesAttempted.map(sid => ({
        id: sid,
        ok: !perSourceErrors.some(e => e.startsWith(`${sid}:`)),
      }));
      await emitAuditEvent({
        action: 'public-sentiment.refreshed',
        target: { type: 'site', id: siteId, label: sed.siteName },
        metadata: {
          status: result.status,
          sourcesAttempted: result.sourcesAttempted,
          perSourceRollup,
          entriesReturned: result.entries.length,
          queriesIssued: result.queriesIssued.length,
          durationMs: Date.now() - startedAt,
          reason: result.reason,
          source,
          btmTechs: [...btmTechs],
        },
      });
    } catch {
      /* audit emit is best-effort; do not affect the rebuild */
    }
  } catch (e) {
    rethrowIfAborted(overrides?.abortSignal, e);
    // Defence-in-depth — the orchestrator's contract is never-throws,
    // but a bug in the import path or a global Promise rejection must
    // still leave the bootstrap able to persist the rest of the SED.
    console.warn(
      `[public-sentiment] orchestrator dispatch failed (site=${siteId}):`,
      (e as Error)?.message,
    );
    // PRESERVE any previously-collected section. Without this a single
    // transient dispatch failure silently DROPPED evidence the site already
    // had: the section was left undefined, and `preflightCheck` then reported
    // it as "missing from this site's legacy evidence document — refresh the
    // data", which is both untrue on a current SED and a loop (the refresh
    // re-enters this same failing path). Mirrors the rate-limit branch above,
    // which already preserves `prior` for exactly this reason.
    if (prior?.queryContext?.fingerprint === context.fingerprint) sed.publicSentiment = prior;
  }
}

/* ── Public types ───────────────────────────────────────────────────── */

export interface BootstrapInput {
  siteId: string;
  /** R-98: optional checkpoint resume stage selected by coordinator. */
  resumeFromStage?: SedBootstrapStage;
  /** When true, bypass stale/fresh idempotency checks. */
  forceRefresh?: boolean;
  /** Optional explicit fields — if omitted, read from the store's site doc. */
  name?: string;
  address?: string;
  location?: SiteEntity['location'];
  siteBoundary?: SiteEntity['siteBoundary'];
  siteArea?: SiteEntity['siteArea'];
  sitePerimeter?: SiteEntity['sitePerimeter'];
  siteElevationRange?: SiteEntity['siteElevationRange'];
  /**
   * IDs of `site.documents` to feed into per-section evidence (Documents_On_Site
   * Prompt 5). Each chosen doc is stamped onto every section listed in its
   * `sedSectionsHelpful` field, so report-time prompt builders see the doc
   * text alongside provider data. Provider calls themselves are unchanged —
   * the linkage is metadata, the doc text is already on `site.documents`.
   */
  includeDocIds?: string[];
}

/**
 * DUH — Project `site.documents` into the shape `compileSiteEvidence`
 * expects on its `uploadedDocuments` option. Filters out library-index
 * AI docs and any doc the user has opted out of the SED, matching
 * `applyDocAwareConfidence`'s filter exactly. Returns `[]` for sites
 * with no docs so the SED carries an empty array rather than
 * `undefined` (the SED schema guarantees the field is always an
 * array — see types/siteEvidence.ts:622).
 *
 * The filter MUST stay in lock-step with `applyDocAwareConfidence`
 * (sedBootstrap.ts ~324) so the `linkedDocumentIds` set and the
 * `uploadedDocuments` set agree on which docs are user-content.
 */
export function projectDocsForSed(site: Pick<SiteEntity, 'documents' | 'evidenceMode'>): {
  fileName: string;
  summary: string;
  tags: string[];
  textContent?: string;
  priority?: boolean;
  pages?: { page: number; charStart: number; charEnd: number; text: string }[];
  contentIndex?: SedDocContentIndexEntry[];
  visualReadings?: SiteDocument['visualReadings'];
  evidenceDigest?: SiteDocument['evidenceDigest'];
  docId?: string;
  parentDocId?: string;
}[] {
  // The SED keeps its existing 10k evidence/prompt view independently of the
  // smaller site metadata preview. Full-body extraction hydrates first.
  return docsEligibleForEvidence(site).map(d => {
    // P4 — the content index (topic, figures, who said it, for which
    // project) reaches the prompt even though the body does not.
    const contentIndex = contentIndexForDocument(d);
    return {
      docId: d.id,
      evidenceDigest: d.evidenceDigest ?? buildDocumentEvidenceDigest(d),
      ...(d.parentDocId ? { parentDocId: d.parentDocId } : {}),
      ...(d.visualReadings?.length ? { visualReadings: d.visualReadings } : {}),
      fileName: d.fileName,
      summary: d.summary,
      tags: d.tags ?? [],
      textContent: documentBodyText(d).slice(0, EXTRACTED_TEXT_MAX_CHARS),
      priority: d.priority,
      ...(d.pages?.length ? { pages: d.pages.map(page => ({ ...page, text: '' })) } : {}),
      ...(contentIndex.length > 0 ? { contentIndex } : {}),
    };
  });
}

/** P4 — topics per document carried into the SED prompt's document block. */
const SED_CONTENT_INDEX_MAX_ENTRIES = 12;

/** P4 — the SED-prompt projection of a document's content index (segments live in the document JSON). */
export function contentIndexForDocument(d: Pick<SiteDocument, 'topicSegments'>): SedDocContentIndexEntry[] {
  return (d.topicSegments ?? []).slice(0, SED_CONTENT_INDEX_MAX_ENTRIES).map(seg => {
    const attribution = attributionFromSegment(seg.attribution);
    return {
      topic: seg.topic,
      summary: seg.summary,
      ...(typeof seg.page === 'number' ? { page: seg.page } : {}),
      ...(attribution ? { attribution } : {}),
    };
  });
}

/**
 * P4 — after body hydration the in-memory documents carry their content
 * index again; refresh the SED's document block so the whole-SED prompt
 * sees it on the reload path too (the site record keeps only the compact
 * union). Pure on its inputs; returns the same array when nothing changes.
 */
export function refreshSedDocumentContentIndex(
  uploaded: SiteEvidenceDocument['uploadedDocuments'],
  docs: readonly SiteDocument[],
): SiteEvidenceDocument['uploadedDocuments'] {
  if (!Array.isArray(uploaded) || uploaded.length === 0) return uploaded;
  // Only legacy rows without a document ID may resolve by a unique filename.
  // A missing explicit ID must never adopt another same-named source's body.
  const byName = new Map<string, SiteDocument | undefined>();
  for (const doc of docs) byName.set(doc.fileName, byName.has(doc.fileName) ? undefined : doc);
  let changed = false;
  const next = uploaded.map(u => {
    const doc = u.docId !== undefined ? docs.find(d => d.id === u.docId) : byName.get(u.fileName);
    if (!doc) return u;
    const contentIndex = u.contentIndex?.length ? [] : contentIndexForDocument(doc);
    const visualReadings = u.visualReadings?.length ? undefined : doc.visualReadings;
    const evidenceDigest = u.evidenceDigest ? undefined : doc.evidenceDigest;
    const textContent = documentBodyText(doc).slice(0, EXTRACTED_TEXT_MAX_CHARS);
    const restoresPreview = typeof doc.fullExtractedText === 'string' && textContent.length > (u.textContent?.length ?? 0);
    if (contentIndex.length === 0 && !visualReadings?.length && !evidenceDigest && !restoresPreview) return u;
    changed = true;
    return { ...u, docId: doc.id, ...(restoresPreview ? { textContent } : {}), ...(contentIndex.length ? { contentIndex } : {}), ...(visualReadings?.length ? { visualReadings } : {}), ...(evidenceDigest ? { evidenceDigest } : {}) };
  });
  return changed ? next : uploaded;
}

export interface BootstrapResult {
  sedId: string;
  completeness: number;
}

export interface TopUpResult {
  upgraded: string[];
  sedVersion: number;
  /**
   * What actually happened to the bytes — reported by the code that
   * performed (or skipped) the save, never inferred. The old caller-side
   * heuristic ("version advanced ⇒ persisted") conflated three distinct
   * truths: a genuinely failed save, a run that found nothing new to
   * persist, and an idempotent same-version rewrite — observed live
   * 2026-07-27 as a "could not be saved" toast over bytes that HAD landed.
   *   - 'saved'       → a GCS write succeeded in this run.
   *   - 'save-failed' → the run produced changes but the write failed
   *                     (saveFailReason carries the cause).
   *   - 'no-change'   → the run completed and there was nothing to save.
   *   - 'superseded'  → the run stopped without changing the site's evidence:
   *                     the live site pointer disagreed with this tab's copy
   *                     or moved during the run, a build owns the evidence,
   *                     or the stored bytes changed (supersededReason says
   *                     which). Nothing reached the site pointer.
   */
  outcome: 'saved' | 'save-failed' | 'no-change' | 'superseded' | 'sync-unresolved';
  saveFailReason?: string;
  /** True when pointer publication committed on the server even if post-commit reload failed. */
  publicationCommitted?: boolean;
  committedSedVersion?: number;
  committedStoragePath?: string;
  /** Present only on a 'superseded' result. */
  supersededReason?: SedTopUpRefusal;
  /**
   * Amendments a refreshed provider section overtook: the provider moved to a
   * value that is neither the amendment's original nor the user's correction,
   * so `graftProviderSection` kept the provider value rather than silently
   * re-asserting a stale human decision. Absent when nothing conflicted.
   */
  graftAmendmentConflicts?: InBandAmendmentConflict[];
}

/* ── Helpers ────────────────────────────────────────────────────────── */

/**
 * Stage milestones whose completion advances the user-visible progress
 * counter (SA4). Anything else only logs. Ordered so the banner's "N/4"
 * progresses monotonically: fetch-fast-done → compile → gcs-saved → done.
 */
const PROGRESS_MILESTONE_STAGES = new Set<string>([
  'fetch-fast-done',
  'compile',
  'gcs-saved',
  'done',
]);
const PROGRESS_TOTAL_MILESTONES = PROGRESS_MILESTONE_STAGES.size;

const CHECKPOINT_STAGE_RANK: Record<SedBootstrapStage, number> = {
  queued: 0,
  'fetch-fast': 1,
  'fetch-fast-done': 2,
  'compile-infra': 3,
  compile: 4,
  'doc-aware': 5,
  'conflict-detect': 6,
  'gcs-saved': 7,
  done: 8,
};

function isAtOrAfterStage(
  current: SedBootstrapStage | undefined,
  target: SedBootstrapStage,
): boolean {
  if (!current) return false;
  return CHECKPOINT_STAGE_RANK[current] >= CHECKPOINT_STAGE_RANK[target];
}

/**
 * KSD9 — optional per-doc heartbeat detail. When the doc-aware stage
 * is iterating per-doc, the caller passes `currentDoc` / `currentSection`
 * / `docIndex` / `docTotal` so the banner can surface
 * "Extracting from documents (47/81) — Redhead_BTM.pdf → grid section"
 * without exposing internal stage strings. All optional; pre-KSD9
 * callers omit them and the banner renders the same as before.
 */
interface LogProgressDetail {
  currentDoc?: string;
  currentSection?: string;
  docIndex?: number;
  docTotal?: number;
}

/** Throttle Firestore progress writes — local store updates every tick. */
const PROGRESS_FIRESTORE_PERSIST_INTERVAL_MS = 15_000;
const lastPersistMsBySiteId = new Map<string, number>();

/** Test hook — reset per-site Firestore persist throttle between cases. */
export function __resetProgressPersistThrottleForTests(): void {
  lastPersistMsBySiteId.clear();
}

function logProgress(
  siteId: string,
  stage: string,
  pct: number,
  detail?: LogProgressDetail,
): void {
  console.log(`[SED-BOOTSTRAP] siteId=${siteId} stage=${stage} pct=${pct}`);
  // RSF-2 — record the most recent stage seen for this site so a
  // wall-clock timeout can stamp a precise `sedFailure.stage`
  // breadcrumb. Synchronous map write so the value is current the
  // moment the watchdog fires — no async race with a Firestore write.
  lastStageBySiteId.set(siteId, stage);
  // SA4 — best-effort progress write. The banner reads sedRebuildProgress
  // off the site doc; we update `done` whenever a milestone stage lands,
  // and bump `lastProgressAt` on every call so the watchdog (SA5) can
  // distinguish a long-running stage from a silently-dead bootstrap.
  // Failures are swallowed — the rebuild itself must not block on
  // telemetry writes.
  void (async () => {
    try {
      const useStore = await loadAppStore();
      const store = useStore.getState();
      const site = store.siteRag.sites.find(s => s.id === siteId);
      if (!site) return;
      const prev = site.sedRebuildProgress;
      const wasMilestone = PROGRESS_MILESTONE_STAGES.has(stage);
      const done = wasMilestone
        ? Math.min((prev?.done ?? 0) + 1, PROGRESS_TOTAL_MILESTONES)
        : (prev?.done ?? 0);
      // KSD9 — when a per-doc heartbeat brings detail, a stage-string
      // match is no longer enough to skip the write: a same-stage tick
      // with a new doc/section IS new information for the banner.
      // The detail fields are carried via inline cast because
      // `src/types/site.ts` is out-of-scope (DR-NSW agent). The
      // runtime shape matches the doc-string in site.ts; the static
      // type extension is a stitch step after DR-NSW merge.
      const prevDetail = prev as (typeof prev & { currentDoc?: string; currentSection?: string; docIndex?: number }) | undefined;
      const detailChanged =
        !!detail &&
        (detail.currentDoc !== prevDetail?.currentDoc ||
          detail.currentSection !== prevDetail?.currentSection ||
          detail.docIndex !== prevDetail?.docIndex);
      // Skip the write when nothing changed AND we'd just be touching
      // lastProgressAt on a non-milestone stage — keeps the per-tick
      // Firestore noise down.
      if (!wasMilestone && prev && prev.stage === stage && !detailChanged) return;
      const progressPayload = {
        done,
        total: PROGRESS_TOTAL_MILESTONES,
        startedAt: prev?.startedAt ?? new Date().toISOString(),
        lastProgressAt: new Date().toISOString(),
        stage,
        ...(detail?.currentDoc ? { currentDoc: detail.currentDoc } : {}),
        ...(detail?.currentSection ? { currentSection: detail.currentSection } : {}),
        ...(typeof detail?.docIndex === 'number' ? { docIndex: detail.docIndex } : {}),
        ...(typeof detail?.docTotal === 'number' ? { docTotal: detail.docTotal } : {}),
      } as NonNullable<import('../types/site').SiteEntity['sedRebuildProgress']>;
      const nowMs = Date.now();
      const lastPersistMs = lastPersistMsBySiteId.get(siteId) ?? 0;
      const shouldPersist =
        wasMilestone ||
        nowMs - lastPersistMs >= PROGRESS_FIRESTORE_PERSIST_INTERVAL_MS;
      await store.patchSedRebuildProgressBestEffort(siteId, progressPayload, {
        persist: shouldPersist,
      });
      if (shouldPersist) {
        lastPersistMsBySiteId.set(siteId, nowMs);
      }
    } catch (e) {
      // Telemetry is best-effort; never abort the bootstrap on a write error.
      console.warn(`[SED-BOOTSTRAP] progress write failed for ${siteId} @ ${stage}:`, (e as Error)?.message);
    }
  })();
}

async function syncSedWithServerAuthority(
  targetSed: SiteEvidenceDocument,
  stagedPath: string,
  expectedVersion?: number,
  expectedHash?: string,
): Promise<{ ok: true; reloaded: boolean } | { ok: false; reason: string }> {
  if (
    typeof expectedVersion !== 'number' ||
    !Number.isFinite(expectedVersion) ||
    expectedVersion < 1 ||
    typeof expectedHash !== 'string' ||
    !expectedHash.trim()
  ) {
    return {
      ok: false,
      reason: 'Missing authoritative SED version or content hash for synchronization.',
    };
  }
  const trimmedExpectedHash = expectedHash.trim();
  const localHash = computeSedContentHashV2(targetSed);
  const needsReload =
    expectedVersion !== targetSed.version || trimmedExpectedHash !== localHash;
  if (!needsReload) {
    return { ok: true, reloaded: false };
  }
  try {
    const { authedFetch } = await import('../lib/authedFetch');
    const res = await authedFetch(`/api/storage/load?path=${encodeURIComponent(stagedPath)}`);
    if (!res.ok) {
      return {
        ok: false,
        reason: `Committed SED reload failed (HTTP ${res.status}) for ${stagedPath}.`,
      };
    }
    const rawText = typeof res.text === 'function' ? await res.text() : '';
    const loaded = (
      rawText
        ? JSON.parse(rawText)
        : typeof res.json === 'function'
          ? await res.json()
          : null
    ) as SiteEvidenceDocument | null;
    if (
      loaded &&
      typeof loaded === 'object' &&
      loaded.id === targetSed.id &&
      loaded.version === expectedVersion &&
      computeSedContentHashV2(loaded) === trimmedExpectedHash
    ) {
      for (const key of Object.keys(targetSed)) {
        delete (targetSed as unknown as Record<string, unknown>)[key];
      }
      Object.assign(targetSed, loaded);
      return { ok: true, reloaded: true };
    }
    return {
      ok: false,
      reason: 'Committed SED reload body failed identity/version/hash verification.',
    };
  } catch (e) {
    console.warn('[SED-BOOTSTRAP] failed to reload server-reapplied SED:', (e as Error)?.message);
    return {
      ok: false,
      reason: `Committed SED reload failed: ${(e as Error)?.message ?? 'invalid JSON'}.`,
    };
  }
}

function formatGcsSaveFailureReason(save: { status?: number; body?: string }): string {
  if (
    save.body &&
    /no metadata token|gcs_auth_missing|application default credentials/i.test(save.body)
    && (save.status === 401 || save.status === 503)
  ) {
    return 'Cloud storage unavailable in local dev. Run `gcloud auth application-default login`, then retry evidence collection.';
  }
  if (save.status) {
    return `Storage write failed (HTTP ${save.status}). ${save.body ? save.body.slice(0, 200) : ''}`.trim();
  }
  return 'Failed to write evidence document to storage.';
}

export async function triggerServerJobAndPoll(
  siteId: string,
  opts?: { forceRefresh?: boolean; abortSignal?: AbortSignal },
): Promise<BootstrapResult | null> {
  throwIfAborted(opts?.abortSignal);
  const { authedFetch } = await import('../lib/authedFetch');
  throwIfAborted(opts?.abortSignal);
  const triggerRes = await authedFetch('/api/sed/bootstrap', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      siteId,
      ...(opts?.forceRefresh ? { forceRefresh: true } : {}),
    }),
    signal: opts?.abortSignal,
  });
  throwIfAborted(opts?.abortSignal);
  if (!triggerRes.ok) {
    const body = await triggerRes.text().catch(() => '');
    let terminalFailure: SedTerminalFailureProofV1 | null = null;
    try { terminalFailure = parseSedTerminalFailureProof(JSON.parse(body)?.terminalFailure); } catch { /* Unconfirmed response. */ }
    if (terminalFailure?.siteId === siteId) {
      throw new ConfirmedSedBootstrapFailure(`SED bootstrap failed (${triggerRes.status}).`, terminalFailure);
    }
    throw new Error(`Failed to enqueue server SED bootstrap (${triggerRes.status}): ${body.slice(0, 240)}`);
  }

  type BootstrapEnqueueResponse = {
    status?: string;
    sedId?: string;
    error?: string;
    executionName?: string;
    ownerUid?: string;
    attemptCount?: number;
    terminalFailure?: unknown;
  };
  const enqueuePayload = (await triggerRes.json().catch(() => ({}))) as BootstrapEnqueueResponse;
  const enqueueStatus = enqueuePayload.status ?? 'queued';

  if (enqueueStatus === 'failed') {
    const proof = parseSedTerminalFailureProof(enqueuePayload.terminalFailure);
    if (proof?.siteId === siteId) throw new ConfirmedSedBootstrapFailure(enqueuePayload.error ?? 'SED bootstrap failed', proof);
    throw new Error(enqueuePayload.error ?? 'SED bootstrap job trigger failed');
  }

  if (enqueueStatus === 'skipped-fresh') {
    const { auth, db } = await import('../firebase');
    const user = auth.currentUser;
    if (user?.uid && enqueuePayload.sedId) {
      const { doc, getDoc } = await import('firebase/firestore');
      const siteSnap = await getDoc(doc(db, 'users', user.uid, 'sites', siteId));
      const site = siteSnap.data() as SiteEntity | undefined;
      return {
        sedId: enqueuePayload.sedId,
        completeness: site?.sedCompleteness ?? 0,
      };
    }
    return null;
  }

  if (enqueueStatus === 'already-completed') {
    throw new Error(
      'SED bootstrap was not enqueued: a prior run for this site is marked complete. '
      + 'Retry with force refresh or reset the jobExecutions claim.',
    );
  }

  // `queued` and `already-running` — observe Firestore until terminal sedStatus.
  const { auth, db } = await import('../firebase');
  const user = auth.currentUser;
  if (!user?.uid) {
    throw new Error('Not signed in — cannot observe server SED bootstrap');
  }

  const { doc, onSnapshot } = await import('firebase/firestore');
  const wallClockMs = getBootstrapWallClockMs();
  const ownerUid = enqueuePayload.ownerUid ?? user.uid;
  const siteRef = doc(db, 'users', ownerUid, 'sites', siteId);

  return new Promise<BootstrapResult | null>((resolve, reject) => {
    let settled = false;
    let unsub: (() => void) | null = null;
    let failureTimer: ReturnType<typeof setTimeout> | null = null;
    let checkingFailure = false;
    const settle = (fn: () => void): void => {
      if (settled) return;
      settled = true;
      if (unsub) unsub();
      clearTimeout(timer);
      if (failureTimer) clearTimeout(failureTimer);
      opts?.abortSignal?.removeEventListener('abort', onAbort);
      fn();
    };
    const onAbort = () => settle(() => reject(abortReason(opts?.abortSignal)));
    const timer = setTimeout(() => {
      settle(() => reject(new Error('Timed out waiting for server SED bootstrap completion')));
    }, wallClockMs);
    opts?.abortSignal?.addEventListener('abort', onAbort, { once: true });

    const confirmFailure = async (): Promise<void> => {
      if (settled || checkingFailure) return;
      if (!Number.isSafeInteger(enqueuePayload.attemptCount) || Number(enqueuePayload.attemptCount) < 1) {
        settle(() => reject(new Error('Failed SED status has no confirmed current job attempt. Reload evidence before retrying.')));
        return;
      }
      checkingFailure = true;
      try {
        const response = await authedFetch('/api/sed/bootstrap/terminal-failure', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ siteId, ownerUid, attemptCount: enqueuePayload.attemptCount }), signal: opts?.abortSignal,
        });
        if (!response.ok) throw new Error('The terminal SED job could not be confirmed. Reload evidence before retrying.');
        const payload: unknown = await response.json();
        const value = payload && typeof payload === 'object' && 'terminalFailure' in payload ? payload.terminalFailure : null;
        const proof = parseSedTerminalFailureProof(value);
        if (proof && proof.siteId === siteId && proof.ownerUid === ownerUid && proof.attemptCount === enqueuePayload.attemptCount) {
          settle(() => reject(new ConfirmedSedBootstrapFailure('The current server SED job failed.', proof)));
        } else if (!settled) {
          // The executor releases the site lease before finalizing its job row.
          // Observe that bounded ordering gap; do not relabel it as success/failure.
          failureTimer = setTimeout(() => { void confirmFailure(); }, 1000);
        }
      } catch (error) { settle(() => reject(error)); }
      finally { checkingFailure = false; }
    };

    unsub = onSnapshot(
      siteRef,
      (snap) => {
        if (!snap.exists()) return;
        const site = snap.data() as SiteEntity;
        if (site.sedStatus === 'bootstrapping') return;
        if (site.sedStatus === 'failed') { void confirmFailure(); return; }
        settle(() => {
          if (site.sedId) {
            resolve({
              sedId: site.sedId,
              completeness: site.sedCompleteness ?? 0,
            });
            return;
          }
          resolve(null);
        });
      },
      (error) => {
        settle(() => reject(error));
      },
    );
  });
}

/**
 * SED version monotonicity guard. A rebuild / top-up must NEVER produce a
 * version below what the site already persisted — otherwise the coverage-card
 * badge regresses (live bug: a 12h-ago rebuild showed "SED v2" after a 5d-ago
 * "SED v3" because the loaded SED's `.version` floor disagreed with the site
 * doc's denormalized `sedVersion`). We anchor the next version to the MAX of
 * every known source so a stale or re-compiled SED can't roll the counter back.
 * A brand-new site (no `sedVersion`, no loaded SED) maps to v1.
 */
function nextSedVersion(
  site: { sedVersion?: number } | null | undefined,
  loadedVersion?: number | null,
): number {
  const floor = Math.max(
    typeof site?.sedVersion === 'number' && isFinite(site.sedVersion) ? site.sedVersion : 0,
    typeof loadedVersion === 'number' && isFinite(loadedVersion) ? loadedVersion : 0,
  );
  return floor + 1;
}

/** Optional guards for an in-place SED save (the client top-up). */
interface SaveSedOptions {
  /**
   * Awaited before every attempt, outside the request's error handling, so a
   * throw (the live pointer moved, a build took the site) stops the save.
   */
  beforeAttempt?: () => Promise<void>;
  /**
   * The GCS generation the caller loaded. The server refuses the overwrite
   * with 412 when the object has changed (or vanished) since.
   */
  ifGenerationMatch?: string;
  operationId?: string;
}

async function saveSedToGcs(
  uid: string,
  siteId: string,
  sed: SiteEvidenceDocument,
  abortSignal?: AbortSignal,
  canonicalStoragePath?: string | null,
  opts?: SaveSedOptions,
): Promise<{
  ok: boolean;
  path: string;
  stagedPath?: string;
  operationId?: string;
  generation?: string;
  sedVersion?: number;
  sedContentHash?: string;
  sectionHashes?: Record<string, string>;
  status?: number;
  body?: string;
  code?: string;
}> {
  throwIfAborted(abortSignal);
  const path = resolveSedSavePath(uid, siteId, sed.id, canonicalStoragePath);
  const { authedFetch } = await import('../lib/authedFetch');
  const {
    RETRYABLE_HTTP_STATUS,
    resolveRetryWaitMs,
  } = await import('../lib/httpRetry');
  // Strip non-finite numbers / undefined / functions so the JSON wire encode
  // doesn't blow up on the server side. Mirrors siteRagService:890.
  const cleanSed = JSON.parse(JSON.stringify(sed, (_, v) => {
    if (typeof v === 'number' && !isFinite(v)) return null;
    if (v === undefined) return null;
    if (typeof v === 'function') return null;
    return v;
  }));
  const operationId =
    opts?.operationId?.trim() ||
    `sed-stage-${siteId}-${sed.id}-v${sed.version ?? 1}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`.replace(/[^A-Za-z0-9._:-]/g, '-');
  const MAX_ATTEMPTS = 5;
  let earlierAttemptAmbiguous = false;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    throwIfAborted(abortSignal);
    if (opts?.beforeAttempt) await opts.beforeAttempt();
    let res: Response;
    try {
      res = await authedFetch('/api/storage/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          path,
          data: cleanSed,
          operationId,
          ...(opts?.ifGenerationMatch ? { ifGenerationMatch: opts.ifGenerationMatch } : {}),
        }),
        signal: abortSignal,
      });
    } catch (e) {
      rethrowIfAborted(abortSignal, e);
      earlierAttemptAmbiguous = true;
      const msg = (e as Error)?.message ?? String(e);
      if (attempt < MAX_ATTEMPTS) {
        const waitMs = resolveRetryWaitMs(attempt, 503, null);
        console.warn(`[SED-BOOTSTRAP] GCS save threw (attempt ${attempt}/${MAX_ATTEMPTS}) — retrying in ${waitMs}ms: ${msg}`);
        await abortableDelay(waitMs, abortSignal);
        continue;
      }
      console.error(`[SED-BOOTSTRAP] GCS save failed after ${MAX_ATTEMPTS} attempts: ${msg}`);
      return { ok: false, path, body: msg.slice(0, 500) };
    }

    if (res.ok) {
      let parsed: {
        path?: unknown;
        stagedPath?: unknown;
        operationId?: unknown;
        generation?: unknown;
        sedId?: unknown;
        sedVersion?: unknown;
        sedContentHash?: unknown;
        sectionHashes?: unknown;
      } | null = null;
      try {
        const rawText = typeof res.text === 'function' ? await res.text() : '';
        parsed = rawText
          ? JSON.parse(rawText)
          : typeof res.json === 'function'
            ? await res.json()
            : null;
      } catch {
        try {
          parsed = typeof res.json === 'function' ? await res.json() : null;
        } catch {
          parsed = null;
        }
      }
      const stagedPath =
        typeof parsed?.stagedPath === 'string' && parsed.stagedPath.trim()
          ? parsed.stagedPath.trim()
          : typeof parsed?.path === 'string' && parsed.path.trim()
            ? parsed.path.trim()
            : null;
      const returnedOperationId =
        typeof parsed?.operationId === 'string' && parsed.operationId.trim()
          ? parsed.operationId.trim()
          : null;
      const returnedGeneration =
        typeof parsed?.generation === 'string' && /^[1-9][0-9]*$/.test(parsed.generation.trim())
          ? parsed.generation.trim()
          : null;
      const returnedSedId =
        typeof parsed?.sedId === 'string' && parsed.sedId.trim()
          ? parsed.sedId.trim()
          : null;
      const stagedSedVersion =
        typeof parsed?.sedVersion === 'number' &&
        Number.isFinite(parsed.sedVersion) &&
        parsed.sedVersion >= 1
          ? parsed.sedVersion
          : null;
      const stagedContentHash =
        typeof parsed?.sedContentHash === 'string' && parsed.sedContentHash.trim()
          ? parsed.sedContentHash.trim()
          : null;
      if (
        !stagedPath ||
        !returnedOperationId ||
        returnedOperationId !== operationId ||
        !returnedGeneration ||
        returnedSedId !== sed.id ||
        stagedSedVersion === null ||
        !stagedContentHash
      ) {
        const invalidMsg = 'Malformed or incomplete staging receipt from /api/storage/save.';
        console.error(`[SED-BOOTSTRAP] ${invalidMsg}`);
        return { ok: false, path, status: 502, body: invalidMsg };
      }
      const stagedSectionHashes =
        parsed?.sectionHashes &&
        typeof parsed.sectionHashes === 'object' &&
        !Array.isArray(parsed.sectionHashes)
          ? (parsed.sectionHashes as Record<string, string>)
          : undefined;
      return {
        ok: true,
        path: stagedPath,
        stagedPath,
        operationId: returnedOperationId,
        generation: returnedGeneration,
        sedVersion: stagedSedVersion,
        sedContentHash: stagedContentHash,
        ...(stagedSectionHashes ? { sectionHashes: stagedSectionHashes } : {}),
      };
    }
    const body = await res.text().catch(() => '');
    if (res.status === 409 || res.status === 412) {
      if (res.status === 412 && earlierAttemptAmbiguous) {
        const storedWithGen = await loadSedFromGcsWithGeneration(path);
        if (
          storedWithGen?.sed &&
          canonicalJson(storedWithGen.sed) === canonicalJson(cleanSed)
        ) {
          return {
            ok: true,
            path,
            stagedPath: path,
            operationId,
            ...(storedWithGen.generation ? { generation: storedWithGen.generation } : {}),
            sedVersion: storedWithGen.sed.version,
            sedContentHash: computeSedContentHashV2(storedWithGen.sed),
            sectionHashes: computeSedSectionHashMirrors(storedWithGen.sed).sedSectionContentHashes,
          };
        }
      }
      let code: string | undefined;
      try {
        const parsed = JSON.parse(body) as { code?: unknown };
        if (typeof parsed?.code === 'string') code = parsed.code;
      } catch { /* not JSON */ }
      console.error(`[SED-BOOTSTRAP] GCS save failed (${res.status}): ${body.slice(0, 500)}`);
      return { ok: false, path, status: res.status, body: body.slice(0, 500), ...(code ? { code } : {}) };
    }
    if (attempt < MAX_ATTEMPTS && RETRYABLE_HTTP_STATUS.has(res.status)) {
      if (res.status >= 500) earlierAttemptAmbiguous = true;
      const waitMs = resolveRetryWaitMs(attempt, res.status, res.headers.get('Retry-After'));
      console.warn(`[SED-BOOTSTRAP] GCS save failed (${res.status}) attempt ${attempt}/${MAX_ATTEMPTS} — retrying in ${waitMs}ms`);
      await abortableDelay(waitMs, abortSignal);
      continue;
    }
    console.error(`[SED-BOOTSTRAP] GCS save failed (${res.status}): ${body.slice(0, 500)}`);
    return { ok: false, path, status: res.status, body: body.slice(0, 500) };
  }
  return { ok: false, path, body: 'GCS save exhausted retries' };
}

let warnedMissingStorageGeneration = false;

/**
 * Load a SED together with the GCS generation of the object that was read,
 * so an in-place save can require that the object has not changed since.
 * The generation is null when the runtime cannot report it (the admin read
 * used headless and in Node tests, or a storage emulator without the header);
 * the save then carries no precondition, exactly as before.
 */
async function loadSedFromGcsWithGeneration(
  path: string,
): Promise<{ sed: SiteEvidenceDocument; generation: string | null } | null> {
  if (typeof window === 'undefined') {
    const sed = await loadSedFromGcs(path);
    return sed ? { sed, generation: null } : null;
  }
  try {
    const { authedFetch } = await import('../lib/authedFetch');
    const res = await authedFetch(`/api/storage/load?path=${encodeURIComponent(path)}`);
    if (!res.ok) {
      console.warn(`[SED-BOOTSTRAP] GCS load failed (${res.status}) for ${path}`);
      return null;
    }
    const header = res.headers.get('X-Storage-Generation');
    const generation = header && /^[1-9][0-9]*$/.test(header) ? header : null;
    if (!generation && !warnedMissingStorageGeneration) {
      warnedMissingStorageGeneration = true;
      console.warn('[SED-BOOTSTRAP] storage load returned no object generation; in-place saves run without a generation precondition');
    }
    return { sed: (await res.json()) as SiteEvidenceDocument, generation };
  } catch (e) {
    console.warn(`[SED-BOOTSTRAP] GCS load threw: ${(e as Error)?.message}`);
    return null;
  }
}

async function loadSedFromGcs(path: string): Promise<SiteEvidenceDocument | null> {
  // BUG-1 FIX — headless (Cloud Run job) has no window/origin and no
  // firebase.auth.currentUser, so the relative-URL authedFetch path throws
  // "Failed to parse URL from /api/storage/load?path=..." in undici. Read
  // directly from GCS with the runtime SA, mirroring the admin SAVE path
  // (saveGcsJsonAdmin) the job already uses. The object key is identical to the
  // one /api/storage/load?path= resolves against, so there is no path drift.
  // The admin import MUST stay a dynamic import() behind this guard so the
  // Node-only deps (google-auth-library) are never pulled into the browser
  // bundle; the browser path below is preserved byte-for-byte.
  if (typeof window === 'undefined') {
    try {
      const { loadGcsJsonAdmin } = await import('../server/reportGeneration/adminStorage');
      const doc = await loadGcsJsonAdmin(path);
      return (doc as SiteEvidenceDocument | null) ?? null;
    } catch (e) {
      console.warn(`[SED-BOOTSTRAP] admin GCS load threw: ${(e as Error)?.message}`);
      return null;
    }
  }
  try {
    const { authedFetch } = await import('../lib/authedFetch');
    const res = await authedFetch(`/api/storage/load?path=${encodeURIComponent(path)}`);
    if (!res.ok) {
      console.warn(`[SED-BOOTSTRAP] GCS load failed (${res.status}) for ${path}`);
      return null;
    }
    return (await res.json()) as SiteEvidenceDocument;
  } catch (e) {
    console.warn(`[SED-BOOTSTRAP] GCS load threw: ${(e as Error)?.message}`);
    return null;
  }
}

/**
 * DCO-3 — read the SED's resolved two-letter state code (when known) so
 * the Data Completion Orchestrator can decide whether to dispatch
 * state-scoped research templates.
 *
 * Probes in order:
 *   - `electricity.data.state`      (EIA / state-scoped electricity)
 *   - `regulatory.data.state`       (regulatoryDb resolved code)
 *
 * Returns `null` when neither is populated — outside-US coverage or a
 * provider failure. The orchestrator skips state-scoped templates in
 * that case.
 */
function inferStateCodeFromSed(sed: SiteEvidenceDocument): string | null {
  const electricityState = (sed.electricity?.data as { state?: unknown } | null)?.state;
  if (typeof electricityState === 'string' && /^[A-Z]{2}$/i.test(electricityState.trim())) {
    return electricityState.trim().toUpperCase();
  }
  const regulatoryState = (sed.regulatory?.data as { state?: unknown } | null)?.state;
  if (typeof regulatoryState === 'string' && /^[A-Z]{2}$/i.test(regulatoryState.trim())) {
    return regulatoryState.trim().toUpperCase();
  }
  return null;
}

/** Count of fast sections that came back as 'verified' / total fast sections.
 *  Mirrors the `completeness` semantic: 0..1, fraction of "we have data".
 *
 *  GAP 3 (field-architecture wiring) — additively reflects the NEW 3-axis BTM
 *  model (cooling axes + power-basis + ELCC / firm-capacity) in the
 *  completeness fraction. BACK-COMPAT INVARIANT: a SED that carries NONE of the
 *  new-model fields (legacy SED — no `fullProjectIntent` and no new sparse
 *  cooling axes) is BYTE-STABLE with the pre-gap provider-only fraction (the
 *  intent axes are only added to BOTH numerator and denominator when the SED
 *  has opted into the new model). For a new-model SED, each declared axis (the
 *  cold-end heat-rejection SINK that drives water withdrawal, the BTM cooling
 *  MACHINE, and the firm-capacity / power-basis spine) counts toward
 *  completeness, so an opted-in site that left an axis undeclared reads as
 *  genuinely less complete rather than silently 100% on provider data alone. */
function fastSectionCompleteness(sed: SiteEvidenceDocument): number {
  const fastSectionKeys: Array<keyof SiteEvidenceDocument> = [
    'climate', 'seismic', 'flood', 'grid', 'environmental',
    'waterStress', 'electricity', 'airQuality',
  ];
  let verified = 0;
  let total = fastSectionKeys.length;
  for (const key of fastSectionKeys) {
    const sec = sed[key] as EvidenceSection<unknown> | undefined;
    if (sec && (sec.status === 'verified' || sec.status === 'anomalous')) verified++;
  }

  // GAP 3 — opt-in BTM-model completeness. Only engages when the SED carries the
  // new model (so legacy SEDs are unchanged). Each axis adds 1 to the
  // denominator and 1 to the numerator iff declared.
  const intentCompleteness = btmModelCompleteness(sed);
  if (intentCompleteness) {
    verified += intentCompleteness.declared;
    total += intentCompleteness.total;
  }

  return verified / total;
}

/** GAP 3 — declared/total count for the NEW 3-axis BTM model on a SED.
 *  Returns null when the SED carries NONE of the new-model fields (legacy →
 *  completeness untouched). The axes:
 *    - heat-rejection SINK (`bopHeatRejection`) — drives WATER withdrawal,
 *    - BTM cooling MACHINE (`coolingGeneration`),
 *    - power / firm-capacity spine — a declared `powerRequirementMW` AND an
 *      ELCC accreditation intent (`capacityCreditIntent.target` != 'none'). */
function btmModelCompleteness(sed: SiteEvidenceDocument): { declared: number; total: number } | null {
  const full = sed.fullProjectIntent;
  const sparse = sed.projectIntent;
  // Cold-end sink + BTM machine can come from either the full intent or the
  // (now sink-aware) sparse view.
  const sink = full?.bopHeatRejection ?? sparse?.bopHeatRejection;
  const machine = full?.coolingGeneration ?? sparse?.coolingGeneration;
  const powerMW = full?.powerRequirementMW ?? sparse?.powerRequirementMW;
  const elcc = full?.capacityCreditIntent?.target;

  const isSet = (v: unknown): boolean =>
    v != null && v !== '' && v !== 'undecided' && v !== 'unknown' && v !== 'none';

  // Opt-in gate: a SED with NO new-model signal at all stays on the legacy
  // provider-only fraction (byte-stable). `fullProjectIntent` presence OR any
  // declared new-model axis opts the SED in.
  const optedIn =
    full != null || isSet(sink) || isSet(machine) || isSet(elcc);
  if (!optedIn) return null;

  let declared = 0;
  const total = 3; // sink, machine, power+firm-capacity spine.
  if (isSet(sink)) declared++;
  if (isSet(machine)) declared++;
  // Power / firm-capacity spine: a sized facility load AND a firm-capacity
  // (ELCC) accreditation intent — the two halves of the power basis the
  // feasibility verdict reasons over.
  if (isSet(powerMW) && isSet(elcc)) declared++;

  return { declared, total };
}

/* ── Doc-aware confidence (Documents_On_Site Prompt 11) ─────────────── */

/**
 * Walk every SED section and apply doc-aware confidence:
 *
 *   - For an `unavailable` section, find the best matching uploaded
 *     doc (sedSectionsHelpful + relevance ≥ EXTRACTION_RELEVANCE_FLOOR
 *     (0.4) for the workload's primary report type, or 'feasibility' as
 *     fallback). If one exists, run extractFromDocument. On success,
 *     stamp the section as `verified-from-doc` with confidence=0.7 +
 *     supportingDocIds.
 *   - For a `verified` / `anomalous` section, find every uploaded doc
 *     whose sedSectionsHelpful covers this section AND relevance ≥
 *     EXTRACTION_RELEVANCE_FLOOR (0.4) for the report type. Stamp
 *     adjustedConfidence (provider + 0.1 × strongest doc) AND —
 *     SED D0.3 — ALSO attempt an extraction so a matched doc can
 *     GAP-FILL a provider-null/undefined/NaN field. Provider precedence
 *     is strict: a field present on BOTH provider and doc is routed
 *     through the existing `sedDocResolver` (provider wins unless the
 *     doc is a registered authoritative override); the doc value is
 *     written ONLY to fields the provider left empty. A verified slot is
 *     NEVER blanket-overwritten. See `mergeExtractedIntoSlot`.
 *
 * SED D0.3 — the extraction-attempt floor is the SAME number as the DCT
 * match floor (`EXTRACTION_RELEVANCE_FLOOR` = `SUPPORTING_SCORE_THRESHOLD`
 * = 0.4), closing the [0.4, 0.6) "matched but not extracted" band. When
 * an attempt is made but yields nothing usable for a still-empty slot,
 * the slot records a non-silent `extractionAttempt` breadcrumb instead
 * of dropping the doc silently.
 *
 * Mutates `sed` in place. A missing captured source body aborts before
 * extraction; per-section extraction failures still log and continue.
 */
export async function applyDocAwareConfidence(
  sed: SiteEvidenceDocument,
  // UAE-4 — widened to also accept `siteContext` (optional) so the
  // authored-evidence tier can flip `unavailable` slots BEFORE the
  // doc-fallback path. Back-compat: callers that already pass a
  // `Pick<SiteEntity, 'id' | 'documents'>` still satisfy this — the
  // `siteContext` field is optional.
  site: Pick<SiteEntity, 'id' | 'documents' | 'evidenceMode'> & { siteContext?: SiteEntity['siteContext'] },
  optsOrSiteId?: string | {
    siteId?: string;
    reportType?: ReportTypeId;
    abortSignal?: AbortSignal;
    /** Maintainer document-only Retry; ordinary rebuilds retain cache reuse. */
    bypassCache?: boolean;
    /** P1.4 — loads a document's whole body (server callers pass an admin loader). */
    bodyLoader?: DocumentBodyLoader;
    /** Headless callers persist compact status metadata through the owner-scoped admin path. */
    onDocumentStatusPatches?: (patches: { docId: string; patch: Partial<SiteDocument> }[]) => Promise<void>;
    onDocumentProgress?: (detail: LogProgressDetail) => Promise<void> | void;
  },
): Promise<void> {
  // PL2 — accept reportType so the doc-aware boost lookup uses the
  // actual `relevance[reportType]` slot. The previous hard-coded
  // 'feasibility' default silently degraded the other 5 report types
  // (their relevance slots were never consulted). Back-compat shim:
  // string arg keeps the legacy `siteId`-only call shape working so
  // existing sedBootstrap call sites still compile until they're
  // ported. Object arg is the new canonical form.
  const opts = typeof optsOrSiteId === 'string'
    ? { siteId: optsOrSiteId, reportType: 'feasibility' as ReportTypeId }
    : {
      siteId: optsOrSiteId?.siteId,
      reportType: optsOrSiteId?.reportType ?? 'feasibility',
      abortSignal: optsOrSiteId?.abortSignal,
      bypassCache: optsOrSiteId?.bypassCache,
      bodyLoader: optsOrSiteId?.bodyLoader,
      onDocumentStatusPatches: optsOrSiteId?.onDocumentStatusPatches,
      onDocumentProgress: optsOrSiteId?.onDocumentProgress,
    };
  throwIfAborted(opts.abortSignal);
  const siteId = opts.siteId;
  const reportType: ReportTypeId = opts.reportType;
  // DAT-FOLLOWUP-1 — site-state prior + site name for the cross-state
  // guard in `extractFromDocument`. `inferSiteStateFromSed` probes
  // electricity → regulatory → coords for a 2-letter USPS code; the
  // extractor's locus-binding instruction is conditioned on this
  // value when it is non-null. Lazy-imported to avoid a top-level
  // cycle (the lib pulls in regulatoryDb for `estimateStateFromCoords`).
  const { inferSiteStateFromSed } = await import('../lib/inferSiteState');
  throwIfAborted(opts.abortSignal);
  const siteState = inferSiteStateFromSed(sed);
  const siteName = sed.siteName;
  let userDocs = docsEligibleForEvidence(site);
  // The site record carries a preview. Never complete document extraction
  // or a SED rebuild from it when the source receipt says more was captured.
  userDocs = await hydrateDocumentBodiesForEvidence(userDocs, { loader: opts.bodyLoader });
  throwIfAborted(opts.abortSignal);
  // P4 — hydration also restored each document's content index; carry it
  // into the SED's document block for the prompt.
  sed.uploadedDocuments = refreshSedDocumentContentIndex(sed.uploadedDocuments, userDocs);
  // Generation screening extraction shares the same hash/version cache as the
  // Generation-tab loader. This pass is deliberately bounded: future uploads
  // are extracted before persistence, while legacy libraries converge over
  // successive SED/Generation visits without multiplying document calls.
  try {
    const { backfillGenerationScreeningExtractions } = await import(
      './knowledge/generationScreeningExtraction'
    );
    const backfill = await backfillGenerationScreeningExtractions(userDocs, {
      evidenceEnabled: site.evidenceMode !== 'ai-only',
      maxDocuments: 3,
      abortSignal: opts.abortSignal,
    });
    if (backfill.updatedDocumentIds.length > 0) {
      const updatedById = new Map(backfill.documents.map(document => [document.id, document]));
      site.documents = (site.documents ?? []).map(
        document => updatedById.get(document.id) ?? document,
      );
      userDocs = backfill.documents;
    }
  } catch (error) {
    console.warn(
      '[generation-screening] legacy document backfill deferred:',
      (error as Error)?.message,
    );
  }

  // Share every quoted fact and validated generation candidate through the SED.
  // Consumers still own explicit scenario/design selection; evidence never silently changes assumptions.
  sed.documentFacts = collectSedDocumentFacts(userDocs);
  sed.generationDocumentEvidence = userDocs.flatMap(doc => doc.generationScreeningExtraction ? [{ documentId: doc.id, documentName: doc.fileName, extraction: doc.generationScreeningExtraction }] : []);

  // UAE-4 — authored-evidence tier. Applied BEFORE doc-fallback so a
  // user-authored claim with `requirementId` can flip an `unavailable`
  // section to `verified-from-context` without a Gemini call. The
  // function is a no-op when the site has no claims with
  // `source: 'user-typed'` + `requirementId`.
  const authoredClaims = site.siteContext?.claims;
  if (authoredClaims && authoredClaims.length > 0) {
    try {
      const { applyAuthoredEvidenceToSed } = await import('./sedAuthoredEvidence');
      const { applyAuthoredStructuredFieldsToSed } = await import('../lib/coverage/authoredSedOverlay');
      const flipped = applyAuthoredEvidenceToSed(sed, authoredClaims, reportType);
      const patched = applyAuthoredStructuredFieldsToSed(sed, authoredClaims);
      if (siteId && (flipped > 0 || patched > 0)) {
        console.debug(`[UAE-4] applied ${flipped} authored-evidence flips, ${patched} field overlays on ${siteId}`);
      }
    } catch (e) {
      // Authored-evidence is a best-effort enrichment — never block
      // bootstrap on a projection error.
      console.warn('[UAE-4] applyAuthoredEvidenceToSed failed:', (e as Error)?.message);
    }
  }

  const envPrefs = (site as { typedPreferencesDefaults?: { environmental?: unknown } }).typedPreferencesDefaults?.environmental;
  if (envPrefs) {
    try {
      const { applyEnvironmentalPreferencesToSed } = await import('../lib/coverage/applyTypedPreferencesToSed');
      applyEnvironmentalPreferencesToSed(sed, envPrefs as never);
    } catch (e) {
      console.warn('[UAE-4] applyEnvironmentalPreferencesToSed failed:', (e as Error)?.message);
    }
  }

  // IIA-2 / IIA-4 — surface any iiaResult.observation onto the SED's
  // `onSiteInfrastructure` notes / siteContext.claims[] surface. The
  // pipeline already ran at upload time (`processUploadedFile`); this
  // bootstrap step is the dispatcher that takes the persisted result
  // (`siteDoc.iiaResult`) and threads its free-text observation into
  // the SED as an `image-observation` claim with `userApproved: false`
  // so it doesn't leak into the prompt until the user approves it.
  //
  // No Gemini call here — purely a projection of already-stamped
  // results. Cost: zero. The only side-effect is appending to the
  // in-memory `site.siteContext.claims[]` so the next save persists
  // them. Defensive — failures are logged and never block bootstrap.
  try {
    const imageDocs = userDocs.filter(d => d.iiaResult);
    if (imageDocs.length > 0 && site.siteContext) {
      const { applyIiaObservationsToSiteContext } = await import('./knowledge/iiaSiteContextBridge');
      const added = applyIiaObservationsToSiteContext(site.siteContext, imageDocs);
      if (siteId && added > 0) {
        console.debug(`[IIA-2] surfaced ${added} image-observation claims on ${siteId}`);
      }
    }
  } catch (e) {
    console.warn('[IIA-2] applyIiaObservationsToSiteContext failed:', (e as Error)?.message);
  }

  // Doc Layout Intelligence — map trusted structured IIA payloads into
  // deterministic SED fields consumed by Test Fit fallbacks.
  try {
    const imageDocs = userDocs.filter(d => d.iiaResult?.extracted);
    if (imageDocs.length > 0) {
      const { applyStructuredDocSignalsToSed } = await import('./knowledge/docLayoutIntelligence');
      const patched = applyStructuredDocSignalsToSed(sed, imageDocs);
      if (siteId && patched > 0) {
        console.debug(`[doc-layout] projected ${patched} structured field(s) from image/PDF docs on ${siteId}`);
      }
    }
  } catch (e) {
    console.warn('[doc-layout] applyStructuredDocSignalsToSed failed:', (e as Error)?.message);
  }

  if (userDocs.length === 0) return;

  const { SED_SECTION_IDS } = await import('../types/siteEvidence');
  const {
    pickBestFallbackDoc,
    rankFallbackDocs,
    sectionSchemaFieldNames,
    sectionExtractionTimeoutMs,
    computeAdjustedConfidence,
    DOC_FALLBACK_CONFIDENCE,
    // SED D0.3 — shared "is this a match?" floor (= 0.4) so the
    // extraction-attempt gate matches the DCT match floor + the boost
    // floor, closing the [0.4, 0.6) "matched but not extracted" band.
    EXTRACTION_RELEVANCE_FLOOR,
    isExtractionRejection,
  } = await import('./knowledge/extractFromDocument');
  // QP-2 — secondary docs consulted per section when the primary fill
  // leaves known schema fields empty. Cache-memoised, so the marginal
  // cost repeats only when a doc's content actually changes.
  const MAX_FILL_GAP_DOCS_PER_SECTION = 2;
  // KSD6 — Use the memoised wrapper so the (docId, sectionId,
  // contentHash, modelVersion) cache short-circuits a Gemini call we
  // already paid for in this rebuild (or a prior rebuild that
  // populated the Firestore-persistent layer). Behaviour identical to
  // the raw `extractFromDocument` on cache miss: same signature, same
  // null semantics, same timeout shape.
  // SED D0.3 — both doc-aware extraction paths (unavailable fill +
  // verified/anomalous gap-fill) use the discriminated-outcome wrapper
  // so a rejected extraction records a non-silent reason.
  const { memoisedExtractWithOutcome: cachedExtractWithOutcome } =
    await import('./knowledge/extractionCache');
  // SED D0.3 — resolver used for the provider+doc conflict path on a
  // verified slot's gap-fill (provider wins unless the doc is a
  // registered authoritative override). Imported once here so the
  // per-section helper doesn't re-import per call.
  const { findOverrideFields, mergeProviderAndDocEvidence, applyMergeResult, classifyAgreement } =
    await import('./sedDocResolver');
  // SD2 — per-section Gemini timeout. extractFromDocument hides a
  // runGeminiJSON call; on a hung response the unbounded await parks the
  // whole bootstrap between `compile` (75%) and `gcs-saved` (90%). Wrap
  // each call in a 30s budget so a single stalled section can't sink
  // the rebuild. On timeout we keep the section's provider-derived
  // confidence intact (no boost, no downgrade — the timeout is silent
  // on the user-visible side) and move on to the next section.
  const { withTimeout, isTimeoutError } = await import('../lib/withTimeout');

  // KSD9 — pre-scan the (section → fallback-doc) pairs so the heartbeat
  // banner can render `docIndex / docTotal` as a deterministic counter
  // ("Extracting from documents (3 / 7)…"). pickBestFallbackDoc is
  // cheap (linear in userDocs); the prescan adds milliseconds.
  let docTotal = 0;
  if (siteId || opts.onDocumentProgress) {
    for (const sid of SED_SECTION_IDS) {
      if (sectionSchemaFieldNames(sid).length === 0) continue;
      const s = (sed as unknown as Record<string, EvidenceSection<unknown> | undefined>)[sid];
      if (!s) continue;
      if (s.status === 'unavailable' || (opts.bypassCache && s.status === 'verified-from-doc'
        && Object.keys(s.provenanceDetail?.fieldReviews ?? {}).length === 0)) {
        if (pickBestFallbackDoc(userDocs, sid, reportType)) docTotal += 1;
      } else if (s.status === 'verified' || s.status === 'anomalous') {
        // SED D0.3 — verified/anomalous sections now ALSO attempt
        // extraction (gap-fill), so the heartbeat counter must count
        // them too or the banner under-counts. A section qualifies when
        // at least one supporting doc clears the shared match floor.
        const hasSupporting = userDocs.some(d =>
          (d.sedSectionsHelpful ?? []).includes(sid) &&
          typeof d.relevance?.[reportType] === 'number' &&
          (d.relevance![reportType] as number) >= EXTRACTION_RELEVANCE_FLOOR,
        );
        if (hasSupporting) docTotal += 1;
      }
    }
  }
  let docIndex = 0;

  // ── UPE-1 — per-doc extraction status accumulators ────────────────
  // Track success / partial / failure outcomes per doc as we walk the
  // sections. At the end of the loop we stamp each doc's
  // `extractionStatus` + counters and write the patches back to the
  // store. Map key = docId. `attempts` counts sections where this doc
  // was the chosen fallback or chosen override; `successes` counts
  // sections where the extractor returned a non-null payload.
  // `lastError` carries the most recent failure code so the chip
  // tooltip can render a helpful retry copy. The doc is considered
  // 'extracting' for the duration of this function; we flip it to a
  // success-* / failed-* terminal status before return.
  type DocStatusAcc = {
    attempts: number;
    successes: number;
    lastError?: { code: 'failed-quota' | 'failed-parse' | 'failed-timeout' | 'failed-unknown'; msg: string };
  };
  const docStatusByDocId = new Map<string, DocStatusAcc>();
  const getAcc = (docId: string): DocStatusAcc => {
    let acc = docStatusByDocId.get(docId);
    if (!acc) {
      acc = { attempts: 0, successes: 0 };
      docStatusByDocId.set(docId, acc);
    }
    return acc;
  };
  const classifyErrorCode = (e: unknown): DocStatusAcc['lastError'] => {
    const msg = (e instanceof Error ? e.message : String(e)).toLowerCase();
    if (isTimeoutError(e) || msg.includes('timeout') || msg.includes('timed out')) {
      return { code: 'failed-timeout', msg: `Extraction timed out at ${DOC_EXTRACTION_TIMEOUT_MS / 1000}s` };
    }
    if (msg.includes('quota') || msg.includes('429') || msg.includes('rate')) {
      return { code: 'failed-quota', msg: 'Gemini quota exceeded' };
    }
    if (msg.includes('parse') || msg.includes('json') || msg.includes('schema')) {
      return { code: 'failed-parse', msg: 'Could not parse extractor response' };
    }
    return { code: 'failed-unknown', msg: (e instanceof Error ? e.message : String(e)).slice(0, 120) };
  };

  type TargetOutcome = DocumentExtractionOutcomeV1['targets'][number];
  const targetsByDoc = new Map<string, Map<string, TargetOutcome>>();
  const recordTarget = (docId: string, channel: TargetOutcome['channel'], targetId: string,
    status: TargetOutcome['status'], reason?: string, attempted = true) => {
    let targets = targetsByDoc.get(docId);
    if (!targets) { targets = new Map(); targetsByDoc.set(docId, targets); }
    const key = `${channel}:${targetId}`;
    targets.set(key, { channel, targetId, status, attempts: (targets.get(key)?.attempts ?? 0) + (attempted ? 1 : 0),
      ...(reason ? { reason } : {}) });
  };
  const memoisedExtractWithOutcome = async (input: Parameters<typeof cachedExtractWithOutcome>[0]) => {
    try {
      // The inner deadline records the actual failure before the caller keeps
      // its existing provider value; a late model result cannot change this row.
      const outcome = await withTimeout(cachedExtractWithOutcome({ ...input,
        ...(opts.bypassCache ? { bypassCache: true } : {}) }),
        sectionExtractionTimeoutMs(input.doc, input.section), `doc-mapping:${input.section}`);
      if (isExtractionRejection(outcome)) {
        const status = outcome.reason === 'no-schema' ? 'unsupported'
          : outcome.reason === 'all-required-missing' ? 'empty'
            : outcome.reason === 'call-failed' ? 'failed' : 'rejected';
        recordTarget(input.doc.id, 'section', input.section, status, outcome.reason);
      } else {
        const reason = documentNonAdoptionReason(input.section, outcome);
        recordTarget(input.doc.id, 'section', input.section, reason ? 'rejected' : 'extracted', reason);
      }
      return outcome;
    } catch (error) {
      rethrowIfAborted(opts.abortSignal, error);
      recordTarget(input.doc.id, 'section', input.section, 'failed', classifyErrorCode(error)?.code);
      throw error;
    }
  };

  // ── SED D0.3 — provider-precedence-aware doc merge ────────────────
  //
  // The single helper that encodes the precedence rule for writing a
  // matched doc's extraction into a slot. Behaviour by slot status:
  //
  //  - `'unavailable'` slot: fill as the legacy doc-fallback path did —
  //    the doc IS the source (caller handles status/provenance/refs).
  //    This helper is NOT used on that path (kept identical for the
  //    REF-LINEAGE contract); it is the verified/anomalous path's tool.
  //
  //  - `'verified'` / `'anomalous'` slot (provider-filled): GAP-FILL
  //    ONLY. For each extracted field:
  //      * provider field is null/undefined/NaN  → write the doc value
  //        (DOC_FALLBACK_CONFIDENCE-level evidence on that field only).
  //      * provider field has a value            → DO NOT overwrite.
  //        Route through the existing `sedDocResolver`
  //        (findOverrideFields/mergeProviderAndDocEvidence/applyMergeResult):
  //        the provider value stands UNLESS the doc is a registered
  //        authoritative override, exactly as today's verified-section
  //        override path. A pure gap-fill leaves status='verified' and
  //        the provider-derived confidence untouched (NEVER calls
  //        applyMergeResult, which would clobber status/provenance).
  //
  // Returns whether any field was written (gap-filled or overridden) so
  // the caller can decide whether the slot still counts as "nothing
  // landed" for the extractionAttempt breadcrumb.
  const isMissingFieldValue = (v: unknown): boolean =>
    v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v));

  const mergeExtractedIntoSlot = (
    slotToMerge: EvidenceSection<unknown>,
    extractedData: Record<string, unknown>,
    doc: SiteDocument,
    sectionId: SedSectionId,
  ): { gapFilled: string[]; overridden: boolean; adoptedFields: string[]; agreedField: string | null; conflictedField: string | null } => {
    const providerData = (slotToMerge.data as Record<string, unknown> | null) ?? {};
    const beforeValues = new Map(Object.entries(providerData).map(([key, value]) => [key, JSON.stringify(value)]));
    const gapFilled: string[] = [];
    const conflicting: string[] = [];
    let agreedField: string | null = null;
    let conflictedField: string | null = null;
    // Partition extracted fields into provider-empty (gap-fill) vs
    // provider-has-value (conflict → resolver).
    for (const [field, value] of Object.entries(extractedData)) {
      if (value === null || value === undefined) continue;
      if (isMissingFieldValue(providerData[field])) {
        gapFilled.push(field);
      } else {
        conflicting.push(field);
        const cmp = classifyAgreement(providerData[field], value);
        if (cmp === 'conflict' && !conflictedField) {
          conflictedField = field;
        } else if (cmp === 'agreement' && !agreedField) {
          agreedField = field;
        }
      }
    }
    if (conflictedField) {
      agreedField = null;
    }

    // Conflict path FIRST: only when the doc carries an authoritative
    // override for this section does the resolver replace a provider
    // value. Otherwise the resolver returns the provider value unchanged
    // (provider precedence) — we still let it run so an override doc
    // flips the field + stamps the conflict/provenance via the existing
    // path. Guard so a pure gap-fill (no conflicting fields) NEVER calls
    // applyMergeResult — that would overwrite status/provenance/
    // adjustedConfidence wholesale and could flip a verified slot.
    let overridden = false;
    if (conflicting.length > 0) {
      const hasOverride = findOverrideFields(sectionId, doc).length > 0;
      if (hasOverride) {
        const merge = mergeProviderAndDocEvidence(
          slotToMerge,
          extractedData,
          doc,
          sectionId,
        );
        applyMergeResult(slotToMerge, merge, doc);
        overridden = merge.provenance === 'doc-override';
        // applyMergeResult re-assigns slot.data wholesale (provider data
        // + overridden fields). Re-read for the subsequent gap-fill so we
        // don't clobber the merge.
      }
    }

    // Gap-fill: write doc values into provider-empty fields directly.
    // This does NOT touch slot.status / provenance / confidence — the
    // slot stays provider-derived; only the previously-empty fields gain
    // doc-supplied values (lower-trust DOC_FALLBACK_CONFIDENCE, which is
    // already < provider confidence, so the slot's confidence is honest).
    if (gapFilled.length > 0) {
      const target = (slotToMerge.data as Record<string, unknown> | null) ?? {};
      for (const field of gapFilled) {
        if (isMissingFieldValue(target[field])) {
          target[field] = extractedData[field];
        }
      }
      slotToMerge.data = target as never;
      slotToMerge.supportingDocIds = Array.from(
        new Set([...(slotToMerge.supportingDocIds ?? []), doc.id]),
      );
    }

    const after = (slotToMerge.data ?? {}) as Record<string, unknown>;
    const nestedFilled = sectionId === 'waterResource' ? fillMissingWaterServiceLeaves(after, extractedData,
      new Set(Object.keys(slotToMerge.provenanceDetail?.fieldReviews ?? {}))) : [];
    if (nestedFilled.length > 0) slotToMerge.data = after as never;
    gapFilled.push(...nestedFilled);
    const adoptedFields = Object.keys(extractedData).filter(field =>
      beforeValues.get(field) !== JSON.stringify(after[field])
      && JSON.stringify(after[field]) === JSON.stringify(extractedData[field]));
    return { gapFilled, overridden, adoptedFields: [...adoptedFields, ...nestedFilled], agreedField, conflictedField };
  };

  for (const sectionId of SED_SECTION_IDS) {
    throwIfAborted(opts.abortSignal);
    const slot = (sed as unknown as Record<string, EvidenceSection<unknown> | undefined>)[sectionId];
    if (!slot) continue;
    if (sectionSchemaFieldNames(sectionId).length === 0) {
      // A missing channel-A schema is not a failed extraction or a promise to
      // retry. Requirement channel B still runs for these documents below.
      for (const doc of rankFallbackDocs(userDocs, sectionId, reportType)) {
        recordTarget(doc.id, 'section', sectionId, 'unsupported', 'no-schema', false);
      }
      continue;
    }

    // Explicit Retry can revisit an unreviewed document-derived slot. Human
    // rulings remain untouched; channel B below retains its per-field review
    // guard. Ordinary rebuild and verified-provider precedence are unchanged.
    const retryDocumentSlot = opts.bypassCache && slot.status === 'verified-from-doc'
      && Object.keys(slot.provenanceDetail?.fieldReviews ?? {}).length === 0;
    if (slot.status === 'unavailable' || retryDocumentSlot) {
      const fallbackCandidates = rankFallbackDocs(userDocs, sectionId, reportType).slice(0, 3);
      let fallback = fallbackCandidates.shift();
      if (!fallback) continue;
      docIndex += 1;
      // KSD9 — surface "currently: {filename} → {section}" on the banner.
      if (opts.onDocumentProgress) {
        await opts.onDocumentProgress({ currentDoc: fallback.fileName, currentSection: sectionId, docIndex, docTotal });
      } else if (siteId) {
        logProgress(siteId, 'doc-aware', 80, {
          currentDoc: fallback.fileName,
          currentSection: sectionId,
          docIndex,
          docTotal,
        });
      }
      try {
        // UPE-1 — record an extraction attempt against this doc.
        getAcc(fallback.id).attempts += 1;
        // SED D0.3 — use the discriminated-outcome wrapper so a rejected
        // extraction (matched doc, nothing landed) records a non-silent
        // reason instead of vanishing.
        let outcome = await withTimeout(
          memoisedExtractWithOutcome({
            doc: fallback,
            section: sectionId,
            siteId: site.id,
            // DAT-FOLLOWUP-1 — pass the site-state prior so the
            // extractor's locus-binding guard fires for sections
            // with a `state` field (regulatory, electricity). The
            // memoiser invalidates stale cache entries via the
            // EXTRACTION_MODEL_VERSION bump.
            siteState,
            siteName,
            abortSignal: opts.abortSignal,
          }),
          sectionExtractionTimeoutMs(fallback, sectionId),
          `doc-aware:${sectionId}`,
        );
        // An extraction can be correct about another project. Keep that source
        // statement, then try the next ranked source for this site's gap.
        while (!isExtractionRejection(outcome)) {
          const reason = documentNonAdoptionReason(sectionId, outcome);
          if (!reason) break;
          retainDocumentSourceStatement(slot, fallback, outcome, reason);
          getAcc(fallback.id).successes += 1;
          const next = fallbackCandidates.shift();
          if (!next) break;
          fallback = next;
          getAcc(fallback.id).attempts += 1;
          outcome = await withTimeout(memoisedExtractWithOutcome({
            doc: fallback, section: sectionId, siteId: site.id, siteState, siteName,
            abortSignal: opts.abortSignal,
          }), sectionExtractionTimeoutMs(fallback, sectionId), `doc-aware:${sectionId}`);
        }
        if (!isExtractionRejection(outcome) && documentNonAdoptionReason(sectionId, outcome)) continue;
        throwIfAborted(opts.abortSignal);
        if (isExtractionRejection(outcome)) {
          // SED D0.3 — matched doc, extraction attempted, nothing
          // usable landed. Stamp the structured breadcrumb so the
          // Coverage Matrix can show WHY the matched doc yielded
          // nothing. The slot stays 'unavailable' (NOT counted as
          // populated by `isSlotPopulatedByDoc`).
          slot.extractionAttempt = {
            attempted: true,
            docId: fallback.id,
            reason: outcome.reason,
            ...(outcome.fieldRejections ? { fieldRejections: outcome.fieldRejections } : {}),
            ...(outcome.crossStateRejection ? { crossStateRejection: outcome.crossStateRejection } : {}),
            at: new Date().toISOString(),
          };
          continue;
        }
        const extracted = outcome;
        const adoptedFallbackId = fallback.id;
        // UPE-1 — record a success.
        getAcc(fallback.id).successes += 1;
        // SED D0.3 — a prior pass may have stamped a rejection on this
        // slot; clear it now that the doc filled the slot.
        delete slot.extractionAttempt;
        slot.status = 'verified-from-doc';
        // P6 — contract sections (waterResource) keep their typed-null base and
        // basis/provenance/disclaimer keys; every other section is replaced as before.
        const replacement = mergeIntoContractPayload(
          sectionId,
          slot.data as Record<string, unknown> | null,
          extracted.data as Record<string, unknown>,
        );
        slot.data = (retryDocumentSlot ? graftProviderSection({
          sectionId, existingSlot: slot, providerSlot: { ...slot, data: replacement },
        }).slot.data : replacement) as never;
        slot.source = extracted.source;
        slot.confidence = extracted.confidence;
        stampDocumentFieldSources(slot, fallback, extracted);
        slot.supportingDocIds = Array.from(new Set([...(slot.supportingDocIds ?? []), fallback.id]));
        slot.docContributionScore = 1;
        slot.adjustedConfidence = Math.min(DOC_FALLBACK_CONFIDENCE, extracted.confidence);
        // SSM-1 — stamp provenance so the scorer (SSM-3) and Score
        // Provenance Panel (SSM-6) know this slot's value came from a
        // doc, not a provider. The legacy `'verified-from-doc'`
        // status stays as the existing readers' signal; provenance is
        // the new structured surface.
        slot.provenance = 'doc';
        slot.provenanceDetail = {
          // Retry's section channel does not erase separately source-bound
          // requirement fields. The normal requirement pass validates/prunes
          // those witnesses and honors any human reviews.
          ...(retryDocumentSlot ? slot.provenanceDetail : {}),
          docId: fallback.id,
          mergeReason: 'doc-fills-gap',
          docValue: extracted.data,
          ...(extracted.attribution ? { attribution: extracted.attribution } : {}),
        };
        const statedDate = statedDateForEvidence(extracted.attribution?.statedOn);
        if (statedDate) { slot.dataAsOf = statedDate; slot.dataAsOfBasis = 'publication'; }
        slot.linkedDocumentIds = Array.from(new Set([...(slot.linkedDocumentIds ?? []), fallback.id]));
        // REF-LINEAGE REF-2 — also stamp the ranked ref so the
        // [Ref N] click path (REF-3) can find the page/section and
        // open DocViewerDialog at the recorded page. Idempotent: if
        // a prior pass left a ref for this same doc, replace it
        // in-place rather than appending — re-running
        // applyDocAwareConfidence on the same SED must not double up.
        // The legacy `linkedDocumentIds` write above stays as the
        // back-compat projection for readers that still consume the
        // id-list shape (see siteEvidence.ts:60-71).
        const existingRefs = slot.linkedDocumentRefs ?? [];
        const otherRefs = existingRefs.filter(r => r.docId !== adoptedFallbackId);
        slot.linkedDocumentRefs = [...otherRefs, {
          docId: fallback.id,
          role: 'primary',
          score: 1,
          rationale: `Doc-fallback extraction for ${sectionId}`,
          evidenceQuotes: extracted.excerptPreview ? [extracted.excerptPreview] : [],
          // P4 — who said it, in what role, when, for which project.
          ...(extracted.attribution ? { attribution: extracted.attribution } : {}),
          pageNumber: extracted.pageNumber,
          sectionHeading: extracted.sectionHeading,
          excerptPreview: extracted.excerptPreview,
          // H-4 (passage-offset citation deep-links) — propagate the
          // verbatim-excerpt char span onto the ref so the [Ref N] click
          // path (resolveLinkedDocDeepLink → RefLink) can deep-link to the
          // exact passage. Only present when the excerpt matched verbatim;
          // absent ⇒ the resolver degrades to page → filename exactly as
          // before. The ref is scoped to one doc, so PassageOffset carries
          // no docId of its own (siteEvidence.ts:117-127).
          ...(extracted.sourceOffset ? { sourceOffset: extracted.sourceOffset } : {}),
        }];

        // QP-2 (quality pass) — multi-doc fill-gaps. One doc rarely holds
        // every field of a section (a utility letter has the capacity, the
        // interconnection study has the queue time). When the primary doc
        // left known schema fields empty AND other qualifying docs exist,
        // extract up to MAX_FILL_GAP_DOCS_PER_SECTION more (memoised, so a
        // rebuild pays once per doc) and fill ONLY the still-missing
        // fields. The primary doc's values are never overwritten and the
        // slot's confidence/provenance stay anchored to the primary —
        // secondary docs are recorded as supporting contributors.
        try {
          const knownFields = sectionSchemaFieldNames(sectionId);
          const slotData = (slot.data ?? {}) as Record<string, unknown>;
          const protectedWaterFields = new Set(Object.keys(slot.provenanceDetail?.fieldReviews ?? {}));
          const stillMissing = () => [
            ...knownFields.filter(f => isMissingFieldValue(slotData[f])),
            ...(sectionId === 'waterResource' && hasMissingWaterServiceLeaves(slotData, protectedWaterFields) ? ['municipal-service-leaves'] : []),
          ];
          // P4 — a second document whose CONTENT index names this section is
          // consulted even when nothing is missing: a competing figure is
          // recorded on `slot.docConflicts` (with who said it, for which
          // project) instead of being dropped because the primary got there
          // first. Field ownership is tracked so a value a secondary doc
          // supplied is attributed to that doc, not the primary.
          const contentNamesSection = (d: SiteDocument): boolean =>
            (d.topics?.sedSections ?? []).includes(sectionId);
          const primaryWitness: DocValueWitness = {
            docId: fallback.id,
            docName: fallback.fileName,
            data: slotData,
            ...(extracted.attribution ? { attribution: extracted.attribution } : {}),
            ...(typeof extracted.pageNumber === 'number' ? { pageNumber: extracted.pageNumber } : {}),
            ...(extracted.excerptPreview ? { excerptPreview: extracted.excerptPreview } : {}),
          };
          const fieldOwner = new Map<string, DocValueWitness>();
          if (knownFields.length > 0) {
            const alsoRanked = rankFallbackDocs(userDocs, sectionId, reportType)
              .filter(d => d.id !== adoptedFallbackId)
              .filter(d => stillMissing().length > 0 || contentNamesSection(d))
              .slice(0, MAX_FILL_GAP_DOCS_PER_SECTION);
            for (const extraDoc of alsoRanked) {
              if (stillMissing().length === 0 && !contentNamesSection(extraDoc)) continue;
              throwIfAborted(opts.abortSignal);
              getAcc(extraDoc.id).attempts += 1;
              const extraOutcome = await withTimeout(
                memoisedExtractWithOutcome({
                  doc: extraDoc,
                  section: sectionId,
                  siteId: site.id,
                  siteState,
                  siteName,
                  abortSignal: opts.abortSignal,
                }),
                sectionExtractionTimeoutMs(extraDoc, sectionId),
                `doc-aware-fillgap:${sectionId}`,
              );
              if (isExtractionRejection(extraOutcome)) continue;
              getAcc(extraDoc.id).successes += 1;
              const extraData = extraOutcome.data as Record<string, unknown>;
              const extraWitness: DocValueWitness = {
                docId: extraDoc.id,
                docName: extraDoc.fileName,
                data: extraData,
                ...(extraOutcome.attribution ? { attribution: extraOutcome.attribution } : {}),
                ...(typeof extraOutcome.pageNumber === 'number' ? { pageNumber: extraOutcome.pageNumber } : {}),
                ...(extraOutcome.excerptPreview ? { excerptPreview: extraOutcome.excerptPreview } : {}),
              };
              // Competing values first, against whoever owns each field now.
              // Nested municipal leaves can belong to different documents.
              // Compare the same admitted schema roots, using their exact retained leaf owners.
              const waterLeaves = sectionId === 'waterResource'
                ? flattenLeafValues(Object.fromEntries(knownFields.map(field => [field, slotData[field]]))) : undefined;
              const conflicts = waterLeaves ? detectSlotDocConflicts(
                Object.keys(waterLeaves), waterLeaves,
                field => {
                  const source = slot.docFieldSources?.[field];
                  return source && source.value === waterLeaves[field]
                    ? { docId: source.docId, docName: source.docName, data: waterLeaves,
                        attribution: source.attribution, pageNumber: source.pageNumber, excerptPreview: source.excerptPreview }
                    : fieldOwner.get(field) ?? primaryWitness;
                },
                { ...extraWitness, data: flattenLeafValues(extraData) },
              ) : detectSlotDocConflicts(
                knownFields, slotData, field => fieldOwner.get(field) ?? primaryWitness, extraWitness,
              );
              if (conflicts.length > 0) {
                slot.docConflicts = mergeDocConflicts(slot.docConflicts, conflicts);
                console.info(`[doc-fillgap] section=${sectionId}: ${conflicts.length} competing value(s) between "${fallback.fileName}" and "${extraDoc.fileName}" recorded`);
              }
              const nonAdoptionReason = documentNonAdoptionReason(sectionId, extraOutcome);
              if (nonAdoptionReason) {
                retainDocumentSourceStatement(slot, extraDoc, extraOutcome, nonAdoptionReason);
                continue;
              }
              const filled: string[] = [];
              for (const field of knownFields.filter(f => isMissingFieldValue(slotData[f]))) {
                if (!isMissingFieldValue(extraData[field])) {
                  slotData[field] = extraData[field];
                  fieldOwner.set(field, extraWitness);
                  filled.push(field);
                }
              }
              if (sectionId === 'waterResource') {
                const nestedFilled = fillMissingWaterServiceLeaves(slotData, extraData, protectedWaterFields);
                for (const field of nestedFilled) { fieldOwner.set(field, extraWitness); filled.push(field); }
              }
              if (filled.length === 0 && conflicts.length === 0) continue;
              if (filled.length > 0) slot.data = slotData as never;
              stampDocumentFieldSources(slot, extraDoc, extraOutcome, filled);
              if (filled.length > 0) slot.adjustedConfidence = Math.min(slot.adjustedConfidence ?? slot.confidence, extraOutcome.confidence);
              slot.supportingDocIds = Array.from(new Set([...(slot.supportingDocIds ?? []), extraDoc.id]));
              slot.linkedDocumentIds = Array.from(new Set([...(slot.linkedDocumentIds ?? []), extraDoc.id]));
              const priorRefs: NonNullable<EvidenceSection<unknown>['linkedDocumentRefs']> =
                (slot.linkedDocumentRefs ?? []).filter(r => r.docId !== extraDoc.id);
              const conflictNote = conflicts.length > 0 ? `competing values for ${conflicts.map(c => c.field).join(', ')}` : '';
              const rationale = filled.length > 0
                ? `Fill-gaps extraction for ${sectionId}: ${filled.join(', ')}${conflictNote ? `; ${conflictNote}` : ''}`
                : `Second document for ${sectionId}: ${conflictNote}`;
              slot.linkedDocumentRefs = [...priorRefs, {
                docId: extraDoc.id,
                role: 'supporting',
                score: extraOutcome.confidence,
                rationale,
                evidenceQuotes: extraOutcome.excerptPreview ? [extraOutcome.excerptPreview] : [],
                pageNumber: extraOutcome.pageNumber,
                sectionHeading: extraOutcome.sectionHeading,
                excerptPreview: extraOutcome.excerptPreview,
                ...(extraOutcome.attribution ? { attribution: extraOutcome.attribution } : {}),
                ...(extraOutcome.sourceOffset ? { sourceOffset: extraOutcome.sourceOffset } : {}),
              }];
            }
          }
        } catch (e) {
          rethrowIfAborted(opts.abortSignal, e);
          // Best-effort: the primary fill above already landed; a fill-gaps
          // failure must never take the section down with it.
          console.warn(`[doc-fillgap] section=${sectionId} failed:`, (e as Error)?.message);
        }
      } catch (e) {
        rethrowIfAborted(opts.abortSignal, e);
        // UPE-1 — record the failure code so the doc chip can show
        // "Click to retry" with the right copy.
        const err = classifyErrorCode(e);
        if (err) getAcc(fallback.id).lastError = err;
        if (isTimeoutError(e)) {
          console.warn(`[sed-bootstrap] doc-aware timeout: section=${sectionId} — keeping provider value`);
          continue;
        }
        console.warn(`[doc-fallback] section=${sectionId} doc=${fallback.id} failed:`, (e as Error)?.message);
      }
      continue;
    }

    // Verified provider section — boost when relevant docs exist AND
    // (SED D0.3) gap-fill any provider-empty field from a matched doc.
    if (slot.status === 'verified' || slot.status === 'anomalous') {
      const supporting = userDocs.filter(d =>
        (d.sedSectionsHelpful ?? []).includes(sectionId) &&
        typeof d.relevance?.[reportType] === 'number' &&
        // SED D0.3 — boost/extract floor aligned to the shared match
        // floor (0.4). Was a hard-coded 0.3.
        (d.relevance![reportType] as number) >= EXTRACTION_RELEVANCE_FLOOR,
      );
      if (supporting.length === 0) continue;
      const providerBeforeDocSupport = { ...slot };

      // SSM-1 / SED D0.3 — provider-precedence-aware doc merge.
      // Only supported agreement or an authoritative override may boost
      // confidence; a rejected or non-confirming extraction preserves the
      // regional provider values and their actual qualifiers.
      const docCandidates = [...supporting].sort((a, b) => {
        const aOverride = findOverrideFields(sectionId, a).length > 0 ? 1 : 0;
        const bOverride = findOverrideFields(sectionId, b).length > 0 ? 1 : 0;
        if (aOverride !== bOverride) return bOverride - aOverride;
        return (b.relevance?.[reportType] ?? 0) - (a.relevance?.[reportType] ?? 0);
      });
      const chosen = docCandidates[0];
      let chosenForStatus: SiteDocument | undefined = chosen;
      try {
        // UPE-1 — record an attempt against the chosen doc.
        getAcc(chosen.id).attempts += 1;
        // SED D0.3 — discriminated outcome so a rejected extraction on a
        // verified slot still records a non-silent breadcrumb.
        const outcome = await withTimeout(
          memoisedExtractWithOutcome({
            doc: chosen,
            section: sectionId,
            siteId: site.id,
            // DAT-FOLLOWUP-1 — the cross-state guard applies here too: a
            // cross-attributed Title doc must not gap-fill / override a
            // verified provider `regulatory.state`.
            siteState,
            siteName,
            abortSignal: opts.abortSignal,
          }),
          sectionExtractionTimeoutMs(chosen, sectionId),
          `doc-aware-merge:${sectionId}`,
        );
        throwIfAborted(opts.abortSignal);
        if (isExtractionRejection(outcome)) {
          // Matched doc, attempted, nothing usable. The provider value
          // and its original qualifiers stay untouched; record only the
          // extractionAttempt breadcrumb.
          slot.extractionAttempt = {
            attempted: true,
            docId: chosen.id,
            reason: outcome.reason,
            ...(outcome.fieldRejections ? { fieldRejections: outcome.fieldRejections } : {}),
            ...(outcome.crossStateRejection ? { crossStateRejection: outcome.crossStateRejection } : {}),
            at: new Date().toISOString(),
          };
        } else {
          getAcc(chosen.id).successes += 1;
          const nonAdoptionReason = documentNonAdoptionReason(sectionId, outcome);
          if (nonAdoptionReason) {
            // Context from another project or a customer's request cannot
            // increase provider confidence or count as fulfilled evidence.
            for (const key of Object.keys(slot)) {
              if (!(key in providerBeforeDocSupport)) delete (slot as unknown as Record<string, unknown>)[key];
            }
            Object.assign(slot, providerBeforeDocSupport);
            retainDocumentSourceStatement(slot, chosen, outcome, nonAdoptionReason);
            continue;
          }
          // SED D0.3 — provider-precedence-aware merge. Gap-fills
          // provider-empty fields; routes conflicting fields through the
          // resolver (provider wins unless authoritative override).
          const { gapFilled, overridden, adoptedFields, agreedField, conflictedField } = mergeExtractedIntoSlot(
            slot,
            outcome.data as Record<string, unknown>,
            chosen,
            sectionId,
          );
          stampDocumentFieldSources(slot, chosen, outcome, adoptedFields);
          if (overridden) {
            // The resolver flipped an authoritative-override field and
            // (via applyMergeResult) stamped 'doc-override' provenance —
            // nothing more to do.
          } else if (agreedField && !conflictedField) {
            const { adjustedConfidence, docContributionScore } = computeAdjustedConfidence(
              slot.confidence,
              [chosen],
              reportType,
            );
            slot.adjustedConfidence = adjustedConfidence;
            slot.docContributionScore = docContributionScore;
            slot.supportingDocIds = Array.from(new Set([
              ...(providerBeforeDocSupport.supportingDocIds ?? []),
              ...(slot.supportingDocIds ?? []),
              chosen.id,
            ]));
            slot.provenance = 'provider+doc';
            slot.provenanceDetail = {
              ...(providerBeforeDocSupport.provenanceDetail ?? {}),
              provider: slot.source,
              docId: chosen.id,
              mergeReason: 'agreement-boost',
              providerValue: (providerBeforeDocSupport.data as Record<string, unknown> | null)?.[agreedField],
              docValue: (outcome.data as Record<string, unknown>)[agreedField],
              ...(outcome.attribution ? { attribution: outcome.attribution } : {}),
            };
            if (gapFilled.length > 0) {
              slot.linkedDocumentIds = Array.from(
                new Set([...(slot.linkedDocumentIds ?? []), chosen.id]),
              );
            }
          } else if (conflictedField) {
            // Retain any gap-filled field values and their docFieldSources
            // while recording the conflict/review state on the contradicted
            // provider field (without overwriting the provider fact).
            const conflictMerge = mergeProviderAndDocEvidence(
              slot,
              outcome.data as Record<string, unknown>,
              chosen,
              sectionId,
            );
            applyMergeResult(slot, conflictMerge, chosen);
            slot.provenanceDetail = {
              ...(providerBeforeDocSupport.provenanceDetail ?? {}),
              ...conflictMerge.provenanceDetail,
              ...(outcome.attribution ? { attribution: outcome.attribution } : {}),
            };
            if (gapFilled.length > 0) {
              slot.linkedDocumentIds = Array.from(
                new Set([...(slot.linkedDocumentIds ?? []), chosen.id]),
              );
            }
          } else if (gapFilled.length > 0) {
            // Pure gap-fill — record the doc contribution + provenance
            // without disturbing the provider-derived status/confidence.
            slot.provenance = slot.provenance ?? 'provider+doc';
            slot.provenanceDetail = {
              ...(providerBeforeDocSupport.provenanceDetail ?? {}),
              provider: slot.source,
              docId: chosen.id,
              mergeReason: 'doc-fills-gap',
              // P4 — who said it, for which project.
              ...(outcome.attribution ? { attribution: outcome.attribution } : {}),
            };
            slot.linkedDocumentIds = Array.from(
              new Set([...(slot.linkedDocumentIds ?? []), chosen.id]),
            );
          }
          // A successful extraction supersedes any prior rejection stamp.
          delete slot.extractionAttempt;
        }
      } catch (e) {
        rethrowIfAborted(opts.abortSignal, e);
        // UPE-1 — attribute the failure to the chosen doc when known.
        if (chosenForStatus) {
          const err = classifyErrorCode(e);
          if (err) getAcc(chosenForStatus.id).lastError = err;
        }
        if (isTimeoutError(e)) {
          console.warn(`[sed-bootstrap] doc-aware merge timeout: section=${sectionId} — keeping provider value`);
        } else {
          console.warn(`[sed-bootstrap] doc-aware merge: section=${sectionId} failed:`, (e as Error)?.message);
        }
        // Best-effort: leave the existing boost in place.
      }
    }
  }

  // ── Requirement-scoped authoredSchema doc extract (channel B) ─────
  const requirementDeferredDocs = new Set<string>();
  try {
    const { runRequirementDocExtractionContinuation } = await import('./knowledge/requirementDocSweep');
    const { memoisedExtractRequirementFields } = await import('./knowledge/extractionCache');
    const sweep = await runRequirementDocExtractionContinuation({
      sed, documents: userDocs, siteId: site.id, abortSignal: opts.abortSignal,
      ...(opts.bypassCache ? { extract: (input: Parameters<typeof memoisedExtractRequirementFields>[0]) =>
        memoisedExtractRequirementFields({ ...input, bypassCache: true }) } : {}),
      onPair: (pair, doc) => {
        recordTarget(doc.id, 'requirement', pair.requirementId,
          pair.status === 'ineligible' ? 'unsupported' : pair.status,
          pair.status === 'ineligible' ? 'No applicable schema or readable source.'
            : documentExtractionProviderBlocked(pair.error) ? pair.error
            : pair.status === 'failed' ? classifyErrorCode(pair.error)?.code : pair.error,
          pair.status !== 'deferred' && pair.status !== 'ineligible');
        if (pair.status === 'deferred') { requirementDeferredDocs.add(doc.id); return; }
        requirementDeferredDocs.delete(doc.id);
        if (pair.status === 'ineligible') return;
        const acc = getAcc(doc.id);
        acc.attempts++;
        if (pair.status === 'extracted' || pair.status === 'empty' || pair.status === 'rejected') acc.successes++;
        else acc.lastError = classifyErrorCode(pair.error ?? 'Requirement extraction did not complete.');
      },
    });
    if (!sweep.complete) console.warn(`[req-doc-extract] ${sweep.completedPairs}/${sweep.pairs.length} pairs complete; ${sweep.deferredPairs} deferred, ${sweep.failedPairs} retryable failures`);
  } catch (error) {
    rethrowIfAborted(opts.abortSignal, error);
    userDocs.forEach(doc => {
      requirementDeferredDocs.add(doc.id);
      // The caller did not receive a completed sweep receipt (including a
      // failed cache flush). Do not present an earlier per-pair result as a
      // completed document pass. This is a process target, not fulfillment.
      recordTarget(doc.id, 'requirement', 'pending-sweep', 'deferred', 'sweep-not-completed', false);
    });
    console.warn('[req-doc-extract] pass failed:', (error as Error)?.message);
  }

  // Municipal operating facts already have literal quotes and scope in the
  // full source index. Project them after both model channels so their typed
  // fields and Populate metadata share the same per-field witnesses.
  const { projectWaterDocumentFacts } = await import('../lib/sed/waterDocumentProjection');
  projectWaterDocumentFacts(sed, userDocs);
  const { projectSiteResourceDocumentFacts } = await import('../lib/sed/siteResourceDocumentProjection');
  projectSiteResourceDocumentFacts(sed, userDocs);
  const { projectUtilityPipelineDocumentFacts } = await import('../lib/sed/utilityPipelineDocumentProjection');
  projectUtilityPipelineDocumentFacts(sed, userDocs);

  // ── UPE-1 — stamp the per-doc extraction status & persist ─────────
  // Walk every userDoc we considered. A doc with zero attempts during
  // this pass either (a) had no SED sections it was a fallback for /
  // had no override rules — stays 'pending', or (b) was already
  // verified in an earlier pass — we leave its existing status alone
  // by only stamping if the doc was actually touched in this run OR
  // had no prior status (first-ever pass). The end goal is: after a
  // SED rebuild, every doc in the library has SOME status chip.
  try {
    const now = Date.now();
    const patches: Array<{ docId: string; patch: Partial<SiteDocument> }> = [];
    // Pre-compute fulfilled requirement ids per doc so the Tier-2
    // path in `computeRequirementStatus` has it ready as a cache.
    // For each doc, walk SED_SECTION_IDS and check which sections it
    // ended up on `linkedDocumentIds`; reverse-lookup those sections
    // to requirement ids via REQUIREMENT_TO_SECTION_MAP.
    const { requirementsForSection } = await import('../config/requirementToSection');
    const populatedByDoc = new Map<string, { slotCount: number; requirementIds: Set<string> }>();
    for (const sid of SED_SECTION_IDS) {
      const slot = (sed as unknown as Record<string, EvidenceSection<unknown> | undefined>)[sid];
      if (!slot) continue;
      const linked = slot.linkedDocumentIds ?? [];
      if (linked.length === 0) continue;
      const reqs = requirementsForSection(sid);
      for (const docId of linked) {
        let agg = populatedByDoc.get(docId);
        if (!agg) {
          agg = { slotCount: 0, requirementIds: new Set<string>() };
          populatedByDoc.set(docId, agg);
        }
        agg.slotCount += 1;
        for (const r of reqs) agg.requirementIds.add(r);
      }
    }

    for (const doc of userDocs) {
      const acc = docStatusByDocId.get(doc.id);
      const pop = populatedByDoc.get(doc.id);
      const counts = documentMappingCounts(sed, doc.id);
      const slotCount = counts.populatedSlotCount;
      const fulfilled = pop ? Array.from(pop.requirementIds) : [];
      if (opts.bypassCache && (targetsByDoc.get(doc.id)?.size ?? 0) === 0) {
        recordTarget(doc.id, 'section', 'eligible-targets', 'unsupported', 'no-applicable-source-or-schema', false);
      }
      const targets = [...(targetsByDoc.get(doc.id)?.values() ?? [])];
      const extractionOutcome: DocumentExtractionOutcomeV1 = { version: 1, at: now,
        retainedFieldCount: counts.retainedFieldCount, linkedSectionCount: counts.linkedSectionCount, targets };
      const outcomes = documentMappingSummary(extractionOutcome);

      let status: import('../types/siteRag').ExtractionStatus;
      let errorMsg: string | undefined;
      if (!hasUsableExtractedText(doc.textContent)) {
        status = 'failed-no-text';
        errorMsg = NO_MACHINE_READABLE_TEXT_MESSAGE;
      } else if (outcomes.deferred > 0 || requirementDeferredDocs.has(doc.id)) {
        status = acc?.successes || slotCount > 0 ? 'success-partial' : 'pending';
        errorMsg = 'Some document requirements remain for the next bounded extraction pass.';
      } else if (outcomes.failed > 0) {
        status = slotCount > 0 ? 'success-partial' : acc?.lastError?.code ?? 'failed-unknown';
        errorMsg = `${outcomes.failed} mapping target${outcomes.failed === 1 ? '' : 's'} failed after bounded attempts; retry remains available.`;
      } else if (targets.length > 0) {
        // Processing every eligible target is separate from finding a retained
        // field. Valid empty/rejected/unsupported sources never fulfill a slot.
        status = slotCount > 0 && outcomes.empty === 0 && outcomes.rejected === 0 ? 'success-full' : 'success-partial';
      } else if (acc && acc.attempts > 0) {
        if (acc.successes > 0 && acc.successes === acc.attempts) status = 'success-full';
        else if (acc.successes > 0) status = 'success-partial';
        else {
          status = acc.lastError?.code ?? 'failed-unknown';
          errorMsg = acc.lastError?.msg;
        }
      } else if (slotCount > 0) {
        // Doc was linked to a slot but not via the fallback/override path
        // (likely a boost-only contribution) — treat as success-full.
        status = 'success-full';
      } else if (!opts.bypassCache && doc.extractionStatus && doc.extractionStatus !== 'pending' && doc.extractionStatus !== 'extracting') {
        // Untouched this pass and already has a meaningful prior status —
        // leave it (idempotent across reruns).
        continue;
      } else {
        status = 'pending';
      }

      const patch: Partial<SiteDocument> = {
        extractionStatus: status,
        extractionStatusAt: now,
        populatedSlotCount: slotCount,
        extractionOutcome,
        fulfilledRequirementIds: fulfilled,
      };
      if (errorMsg) {
        patch.extractionStatusError = errorMsg;
      } else if (doc.extractionStatusError) {
        // Clear stale error on success.
        patch.extractionStatusError = undefined;
      }

      // Mutate the in-memory doc so callers that read `site.documents`
      // immediately after this function (without waiting for the
      // Firestore write) see the up-to-date status. The persisted copy
      // lands via `patchSiteDocument` below.
      Object.assign(doc, patch);
      patches.push({ docId: doc.id, patch });
    }

    if (opts.onDocumentStatusPatches && patches.length > 0) {
      await opts.onDocumentStatusPatches(patches);
    } else if (siteId && patches.length > 0) {
      // Persist the per-doc patches. CW-3 — fan out through the shared
      // `writeLimiter` semaphore so we
      //   (a) get real parallelism (faster than the prior strict
      //       sequential loop on a Redhead-scale site with 80+ docs),
      //   (b) never blow past the SDK's in-memory write queue when a
      //       second rebuild on this process is already mid-fan-out,
      //   (c) share the cap with the documentUpload save path so a
      //       bulk-upload + rebuild interleave can't independently
      //       saturate the SDK queue.
      // Cap default = 8 (writeLimiter.DEFAULT_MAX_CONCURRENT). Each
      // call acquires/releases via `withSemaphore`, which guarantees
      // the slot is released on both success and error.
      const useStore = await loadAppStore();
      const store = useStore.getState();
      const { withSemaphore } = await import('../lib/firestore/writeLimiter');
      await Promise.all(patches.map(({ docId, patch }) =>
        withSemaphore(async () => {
          try {
            await store.patchSiteDocument(siteId, docId, patch);
          } catch (e) {
            console.warn(`[UPE-1] patchSiteDocument failed for ${docId}:`, (e as Error)?.message);
          }
        }),
      ));
    }
  } catch (e) {
    // Best-effort — a status-write failure must NEVER break the SED
    // bootstrap. The next rebuild will retry.
    console.warn('[UPE-1] extraction-status stamping failed:', (e as Error)?.message);
  }
  sed.documentEvidenceQuality = computeDocumentEvidenceQuality(sed, userDocs);
  qualifyFictionalDocumentEvidence(sed, userDocs);
}

/* ── bootstrapSiteSed ───────────────────────────────────────────────── */

/**
 * Public entry point for the SED bootstrap pipeline.
 *
 * Wraps the inner `doBootstrap` body in a wall-clock budget
 * (`BOOTSTRAP_WALL_CLOCK_MS`, default 15 min). On timeout, the site doc
 * is parked with `sedStatus='failed'` + a structured `sedFailure`
 * breadcrumb (RSF-2) and the Error is re-thrown so the fire-and-forget
 * caller in the store can surface it via toast.
 *
 * Why the watchdog exists: on 2026-05-18 Redhead's rebuild looped for
 * 30+ minutes because (a) the `docMatching` and `extractionCache`
 * Firestore rules were missing → every cache write hit the catch-all
 * deny → progress never persisted, and (b) the cache helpers swallow
 * permission errors as "best-effort cache miss". RSF-1 closed the
 * rules gap; RSF-2 (this wrapper) guarantees a clean failure on the
 * NEXT genuine hang instead of an indefinite silent loop.
 *
 * Idempotency / soft-failure semantics are unchanged inside
 * `doBootstrap` — it still returns `null` when the site already has a
 * non-failed SED, and the per-section soft-failure paths inside
 * `applyDocAwareConfidence` etc. still log + continue. The wall-clock
 * is a HARD CEILING on top, not a replacement.
 */
export async function bootstrapSiteSed(input: BootstrapInput): Promise<BootstrapResult | null> {
  const { siteId } = input;
  return triggerServerJobAndPoll(siteId, { forceRefresh: input.forceRefresh });
}

/**
 * Legacy client bootstrap pipeline (kept internal while top-up and doc-aware
 * helpers are still hosted in this module).
 */
async function bootstrapSiteSedLegacy(input: BootstrapInput): Promise<BootstrapResult | null> {
  const { siteId } = input;
  const startMs = Date.now();
  const wallClockMs = getBootstrapWallClockMs();
  // Clear any stale stage from a prior run so `markSedFailed`'s
  // breadcrumb cannot pick up a stage that belonged to an earlier
  // bootstrap (e.g. the rebuild that landed before this retry).
  lastStageBySiteId.delete(siteId);

  let watchdogTimer: ReturnType<typeof setTimeout> | null = null;
  const wallClockPromise = new Promise<never>((_, reject) => {
    watchdogTimer = setTimeout(() => {
      reject(
        new Error(
          `bootstrapSiteSed exceeded ${Math.round(wallClockMs / 60_000)}-min wall clock`,
        ),
      );
    }, wallClockMs);
  });

  try {
    const result = await Promise.race([doBootstrap(input), wallClockPromise]);
    return result;
  } catch (err) {
    const reason = (err as Error)?.message ?? 'unknown error';
    const elapsedMs = Date.now() - startMs;
    const stage = lastStageBySiteId.get(siteId) ?? 'unknown';
    await markSedFailed(siteId, { reason, elapsedMs, stage });
    // Re-throw so the caller (typically the fire-and-forget catch in
    // `retrySEDBootstrap` at store/index.ts:2432) can route the error
    // to a toast / log. doBootstrap's own internal soft-failure paths
    // already returned `null` instead of throwing; reaching this catch
    // means a genuine hard failure (wall clock, network blow-up, etc.).
    throw err;
  } finally {
    if (watchdogTimer !== null) clearTimeout(watchdogTimer);
  }
}

/**
 * Run the fast-provider sweep, build a partial SED, persist to GCS, and
 * stamp the site doc with the pointer + status='partial'. Idempotent:
 * returns null when the site already has a non-failed SED.
 *
 * Internal — callers should use `bootstrapSiteSed` so the wall-clock
 * watchdog applies. This name is unexported because the contract
 * "wraps in wall-clock + writes sedFailure breadcrumb on timeout"
 * IS the public contract; bypassing it would re-open the RSF doom-loop
 * surface area.
 *
 * Soft-failure paths (GCS write failed, not signed in, site missing)
 * still set `sedStatus='failed'` directly and return null without
 * throwing — those are caller-visible failures that don't need the
 * structured breadcrumb (the caller already has a specific reason).
 */
async function doBootstrap(input: BootstrapInput): Promise<BootstrapResult | null> {
  const { siteId, resumeFromStage } = input;

  // Read the site from the store so we have all boundary/elevation fields
  // without the caller having to thread them. Resolve the store BEFORE the
  // first logProgress call — the SA4 progress-write IIFE also resolves it
  // (`loadAppStore()` → the store's dynamic import in the browser and under
  // vitest), and a concurrent dynamic import race against an unprimed mock
  // cache can make the IIFE win the cache slot with the real (unmocked)
  // module, leaving the bootstrap reading the real Zustand store. Awaiting
  // first primes the mock; subsequent imports get the same cached module.
  const useStore = await loadAppStore();
  logProgress(siteId, 'queued', 0);
  const store = useStore.getState();
  const sourceSite = store.siteRag.sites.find(s => s.id === siteId);
  const site = sourceSite ? { ...sourceSite } : undefined;
  if (!site) {
    console.warn(`[SED-BOOTSTRAP] site ${siteId} not in store; aborting`);
    return null;
  }

  // ── Idempotency ──
  // If the site already has a non-failed SED, do nothing. The site doc is
  // the source of truth — even if a stale local state thinks otherwise,
  // a refetched site will have the persisted fields.
  if (!input.forceRefresh && site.sedId && site.sedStatus && site.sedStatus !== 'failed') {
    logProgress(siteId, 'skip-idempotent', 100);
    return null;
  }

  const { auth, db } = await import('../firebase');
  const user = auth.currentUser;
  if (!user) {
    console.warn('[SED-BOOTSTRAP] not signed in; aborting');
    await store.updateSite(siteId, {
      sedStatus: 'failed',
      sedFailureReason: 'Not signed in — refresh and try again.',
    } as Partial<typeof site>);
    return null;
  }

  // Canonical evidence projection always reads the `/layers` subcollection.
  // The site-doc mirror is retained only as the transactional acknowledgement
  // token maintained by saveSiteLayersBatch.
  let canonicalSiteLayers: import('../types/site').SiteLayer[];
  let landGateReviewSignature: string;
  const { siteOwnerUid } = await import('../lib/siteOwnerPath');
  const siteLayerOwnerUid = siteOwnerUid(site, user.uid);
  try {
    const [persistence, promotion] = await Promise.all([
      import('./siteLayers/siteLayerPersistence'),
      import('../lib/siteLayers/landGatePromotion'),
    ]);
    const canonical = await persistence.listSiteLayers(db, siteLayerOwnerUid, siteId);
    const { resolveCanonicalSiteLayers } = await import('../lib/siteLayers/canonicalLayerResolution');
    const resolved = resolveCanonicalSiteLayers(
      canonical,
      site.siteLayers,
      site.siteLayersCanonicalVersion,
    );
    if (resolved.requiresMigration) {
      console.warn('[SED-BOOTSTRAP] legacy non-LandGate layer fallback active; canonical migration required');
    }
    canonicalSiteLayers = resolved.layers;
    Object.assign(site, applySiteStudyArea({ ...site, siteLayers: canonicalSiteLayers }));
    landGateReviewSignature = promotion.reviewedLandGateEvidenceInputSignature(canonicalSiteLayers);
  } catch (error) {
    const reason = `Could not load canonical site layers: ${(error as Error)?.message ?? 'unknown error'}`;
    await store.updateSite(siteId, { sedStatus: 'failed', sedFailureReason: reason } as Partial<typeof site>);
    return null;
  }

  // R-98: hash run intent/uploads ONCE at queue time; every checkpoint in this
  // run uses the same invariants so resume validation is deterministic.
  const { intentHash, uploadSnapshotHash } = computeIntentAndUploadHashes(site);
  // R-98: optional checkpoint load for resume; invalid/expired checkpoints are
  // ignored by readValidCheckpoint so bootstrap safely falls back to full run.
  const checkpoint = resumeFromStage
    ? await readValidCheckpoint(
      user.uid,
      siteId,
      intentHash,
      uploadSnapshotHash,
      landGateReviewSignature,
      SED_CHECKPOINT_SCHEMA_VERSION,
    )
    : null;
  const effectiveResumeStage = checkpoint?.stage;
  const resumeSed = checkpoint?.partialSed as SiteEvidenceDocument | undefined;
  const canResumeFromCompiledSed =
    resumeSed != null
    && typeof resumeSed.id === 'string'
    && isAtOrAfterStage(effectiveResumeStage, 'compile');
  let sed!: SiteEvidenceDocument;
  let fastData: AggregatedSiteData | null = null;
  const lat = input.location?.lat ?? site.location.lat;
  const lng = input.location?.lng ?? site.location.lng;
  // R-98: helper for best-effort milestone checkpoint writes.
  const maybeWriteCheckpoint = async (stage: SedBootstrapStage): Promise<void> => {
    if (process.env.VITEST) return;
    if (!CHECKPOINT_STAGES.has(stage)) return;
    const partialSed = sed
      ? sed
      : (stage === 'fetch-fast-done' ? {} as Partial<SiteEvidenceDocument> : null);
    if (!partialSed) return;
    await writeSedCheckpoint(user.uid, siteId, {
      schemaVersion: CHECKPOINT_SCHEMA_VERSION,
      uid: user.uid,
      siteId,
      stage,
      intentHash,
      uploadSnapshotHash,
      landGateReviewSignature,
      sedSchemaVersion: SED_CHECKPOINT_SCHEMA_VERSION,
      writtenAt: new Date().toISOString(),
      partialSed,
    });
  };

  // ── Fast sweep ──
  // Layer the workload-profile gating from the feasibility path (the same
  // recommendProviders pipeline that filters out NREL ATB for enterprise /
  // colocation profiles, etc.) on top of the static FAST set. Workloads
  // that prioritise connectivity / power get those moved up into the
  // bootstrap (peeringdb-ix + eia-electricity boot early instead of
  // waiting for the deep top-up).
  if (!canResumeFromCompiledSed) {
    logProgress(siteId, 'fetch-fast', 10);
    const { fetchAllSiteData, recommendProviders } = await import('./dataProviders');
    const recommended = site.projectIntent
      ? new Set(recommendProviders(site.projectIntent, lat))
      : null;
    const profileId = site.projectIntent
      ? (await import('../lib/workloadProfile')).effectiveWorkloadProfile(site.projectIntent)
      : null;
    const earlyConnectivity = profileId === 'ai-training' || profileId === 'ai-inference' || profileId === 'hpc';
    const fastSet: string[] = (FAST_PROVIDER_IDS as readonly string[])
      .filter(id => !recommended || recommended.has(id))
      .filter(id => id !== 'nrcs-soil-survey' || isNrcsSsurgoDefaultEnabled())
      .slice();
    if (earlyConnectivity) {
      fastSet.push('peeringdb-ix', 'eia-electricity');
    }
    // v1.3.1 HR-5 review fix — 'eia-natural-gas' belongs to NEITHER provider
    // partition, and the sweep above INTERSECTS FAST_PROVIDER_IDS with the
    // recommendation set, so recommendProviders pushing it for gas intents
    // never reached an actual fetch: stampFuelSupplyAndLcoe always received
    // undefined and every gas site stamped fuelSupply 'unavailable'. Pull it
    // into the fast sweep directly when the intent declares a gas pathway
    // (cheap: FCC census lookup + 2 EIA series).
    {
      const { intentHasGasPathway } = await import('../lib/groundGasFuelCost');
      if (intentHasGasPathway(site.projectIntent)) fastSet.push('eia-natural-gas');
    }
    const fastUnique = Array.from(new Set(fastSet));
    // Pass the boundary centroid + polygon when the site has one drawn — the
    // spatially-varying providers query the parcel centre, not the geocoded
    // address. `fetchAllSiteData` defaults centroid → address for legacy sites.
    fastData = await fetchAllSiteData(
      {
        lat,
        lng,
        studyAreaScope: site.studyAreaScope,
        centroid: site.siteCentroid,
        boundary: site.siteBoundary,
        boundaries: site.siteBoundaries?.map(parcel => ({
          id: parcel.id,
          polygon: parcel.polygon,
          ...(parcel.holes?.length ? { holes: parcel.holes } : {}),
        })),
      },
      store.userApiKeys,
      fastUnique,
    );
    logProgress(siteId, 'fetch-fast-done', 60);
  } else {
    // R-98: resume path skips expensive pre-compile work when checkpoint
    // already carries a compiled partial SED.
    logProgress(siteId, 'fetch-fast-done', 60);
  }
  // R-98: checkpoint write is best-effort and follows milestone progress logging.
  await maybeWriteCheckpoint('fetch-fast-done');

  if (!canResumeFromCompiledSed) {
    // ── XO-1 — Polygon-grid NREL solar resource sampler ──
  //
  // EX-1 shipped `samplePolygonSolarResource` as an auxiliary provider
  // but explicitly deferred orchestrator wiring; XO-1 closes that gap.
  // When the site has a drawn boundary (≥3 vertices), run the 3×3 grid
  // sampler once per bootstrap so the SED's siteProfile carries the
  // per-component spread (GHI/DNI avg + stdev across the polygon)
  // instead of the centroid-only claim. Cost envelope: 1 NREL call per
  // boundary update (the helper caps at SOLAR_MAX_SAMPLES = 9).
  //
  // PA-2 ATTRIBUTION CONTRACT: the polygon sampler always stamps
  // `attemptedProvider: 'nrel-solar'` and `attemptResult` on its return
  // shape; we forward verbatim so the producer's section() helper sees
  // both fields and PA-2's `attemptedProvider`/`attemptResult: 'success'`
  // stamping on the verified `solar` section is preserved on the
  // back-compat path.
  let polygonSolar:
    | Awaited<ReturnType<typeof import('./dataProviders/solar/polygonSolarSampler').samplePolygonSolarResource>>
    | undefined;
  if (site.siteBoundary && site.siteBoundary.length >= 3) {
    try {
      const { samplePolygonSolarResource } = await import(
        './dataProviders/solar/polygonSolarSampler'
      );
      polygonSolar = await samplePolygonSolarResource(site.siteBoundary);
    } catch (e) {
      // Defensive: the sampler already swallows per-point failures and
      // returns an empty-attempt result; reaching this catch means a
      // genuine module-import / programmer error. Log + continue so the
      // bootstrap still produces a SED (auxiliary providers must NEVER
      // block the primary pipeline).
      console.warn(
        '[XO-1] samplePolygonSolarResource threw — continuing without solar polygon survey:',
        (e as Error)?.message,
      );
    }
  }

  // ── XO-2 — NOAA NCEI Climate Normals 1991-2020 centroid fetcher ──
  //
  // EX-2 shipped `fetchNoaaClimateNormals` as an auxiliary provider but
  // explicitly deferred orchestrator wiring; XO-2 closes that gap. When
  // a polygon centroid is known (either `site.siteCentroid` directly or
  // derivable from `site.siteBoundary` via arithmetic-mean centroid),
  // run ONE NCEI search-with-data call per bootstrap so the SED's
  // `climate` section can layer the canonical 30-year monthly normals
  // + a wet-bulb screening estimate (assumed RH; not a design
  // condition) on top of the Open-Meteo primary claim. Cost
  // envelope: 1 NCEI HTTP per boundary update (climate normals are
  // regional — sampling a grid would return identical values from the
  // same station).
  //
  // Honest empty: `fetchNoaaClimateNormals` returns `null` on every
  // failure mode (no stations, parse error, network error, …) and we
  // map `null` → a structured empty-attempt option so the producer can
  // stamp PRE attempt fields on the climate section ("we asked NOAA,
  // nothing came back") without polluting the Open-Meteo primary claim.
  //
  // PA-2 attribution: the option payload always carries
  // `attemptedProvider: 'noaa-ncei-normals'` (success AND empty
  // branches) so the producer's PRE chip sees both fields regardless
  // of upstream outcome.
  //
  // Inline the option shape so we don't import provider-specific code
  // into the sedBootstrap module surface — mirrors how the
  // `compileSiteEvidence` options declare `noaaNormals` (no provider
  // imports leak through). The shape MUST match the producer's
  // expected union exactly.
  type NoaaNormalsOption =
    | {
        attemptedProvider: 'noaa-ncei-normals';
        attemptResult: 'success';
        source: 'noaa-ncei-normals:centroid';
        stages: ReadonlyArray<{
          stage: 'centroid';
          pointsRequested: number;
          pointsReturned: number;
          durationMs: number;
          error?: string;
        }>;
        data: import('./dataProviders/climate/noaaNormalsFetch').NoaaNormalsResponse;
      }
    | {
        attemptedProvider: 'noaa-ncei-normals';
        attemptResult: 'empty';
        source: 'noaa-ncei-normals:none';
        emptyReason: string;
        stages: ReadonlyArray<{
          stage: 'centroid';
          pointsRequested: number;
          pointsReturned: number;
          durationMs: number;
          error?: string;
        }>;
      };
  let noaaNormals: NoaaNormalsOption | undefined = undefined;
  const noaaCentroid: { lat: number; lng: number } | null = (() => {
    if (
      site.siteCentroid &&
      Number.isFinite(site.siteCentroid.lat) &&
      Number.isFinite(site.siteCentroid.lng)
    ) {
      return { lat: site.siteCentroid.lat, lng: site.siteCentroid.lng };
    }
    if (site.siteBoundary && site.siteBoundary.length >= 3) {
      // Local arithmetic-mean centroid — same convention as
      // `polygonCentroid` in `lib/geometry/sampleGrid.ts`. Inlined here
      // to avoid pulling the geometry module into the bootstrap's
      // synchronous load graph (the file already dynamic-imports
      // almost everything to keep cold-start fast).
      let sLat = 0;
      let sLng = 0;
      for (const v of site.siteBoundary) {
        sLat += v.lat;
        sLng += v.lng;
      }
      return {
        lat: sLat / site.siteBoundary.length,
        lng: sLng / site.siteBoundary.length,
      };
    }
    return null;
  })();
  if (noaaCentroid) {
    try {
      const { fetchNoaaClimateNormals } = await import(
        './dataProviders/climate/noaaNormalsFetch'
      );
      const t0 = Date.now();
      const noaaResult = await fetchNoaaClimateNormals(noaaCentroid);
      const durationMs = Date.now() - t0;
      if (noaaResult) {
        noaaNormals = {
          attemptedProvider: 'noaa-ncei-normals',
          attemptResult: 'success',
          source: 'noaa-ncei-normals:centroid',
          stages: [
            {
              stage: 'centroid',
              pointsRequested: 1,
              pointsReturned: 1,
              durationMs,
            },
          ],
          data: noaaResult,
        };
      } else {
        noaaNormals = {
          attemptedProvider: 'noaa-ncei-normals',
          attemptResult: 'empty',
          source: 'noaa-ncei-normals:none',
          emptyReason:
            'NOAA NCEI Climate Normals returned no data for this centroid.',
          stages: [
            {
              stage: 'centroid',
              pointsRequested: 1,
              pointsReturned: 0,
              durationMs,
            },
          ],
        };
      }
    } catch (e) {
      // Defensive — the fetcher itself never throws (it catches all
      // failure modes and returns null), but a module-import error
      // would land here. Same auxiliary-provider rule as XO-1: log +
      // continue so the primary pipeline still completes.
      console.warn(
        '[XO-2] fetchNoaaClimateNormals threw — continuing without NOAA normals:',
        (e as Error)?.message,
      );
    }
  }

  // ── Build SED ──
  // compileSiteEvidence reads providerStatuses; deep providers that weren't
  // requested are NOT in providerStatuses, so the section() helper renders
  // them with status='unavailable' (good — that's exactly what we want).
  if (!fastData) {
    throw new Error('[sed-bootstrap] fast data unavailable before compile');
  }
  const { compileSiteEvidence } = await import('./siteEvidence');
  sed = compileSiteEvidence(
    input.name ?? site.name,
    {
      lat: input.location?.lat ?? site.location.lat,
      lng: input.location?.lng ?? site.location.lng,
      address: input.address ?? site.address,
    },
    fastData,
    {
      siteArea: input.siteArea ?? site.siteArea,
      sitePerimeter: input.sitePerimeter ?? site.sitePerimeter,
      siteElevationRange: input.siteElevationRange ?? site.siteElevationRange,
      // CA6 — record `queryPoint` per evidence section.
      siteBoundary: site.siteBoundary,
      siteCentroid: site.siteCentroid,
      // XO-1 — polygon-grid solar survey from `samplePolygonSolarResource`.
      // Undefined when the site has no polygon (legacy sites, address-only).
      ...(polygonSolar ? { polygonSolar } : {}),
      // XO-2 — NOAA NCEI Climate Normals at the polygon centroid.
      // Undefined when no centroid is derivable (legacy sites without a
      // polygon AND without `siteCentroid`).
      ...(noaaNormals ? { noaaNormals } : {}),
      // DUH — surface user-uploaded docs to the SED so sedToText()
      // emits the ## USER-UPLOADED DOCUMENTS block at report time.
      // Without this the LLM only saw filenames + structured extracted
      // values; the document body text never reached the prompt.
      uploadedDocuments: projectDocsForSed(site),
      // SOC — channel the site-overview inputs into the SED so it
      // becomes a true single-source-of-truth snapshot. The gen-time
      // prompt continues to read live values via buildInputLayersBlock
      // (IC4) / buildSiteContextBlock (SC8); these fields are
      // historical records for the rerun-diff layer (RDF) and the
      // audit trail.
      fullProjectIntent: site.projectIntent,
      promotedGenerationScenarioSnapshot: site.promotedGenerationScenarioSnapshot,
      siteContext: site.siteContext,
      financialAssumptions: site.financialAssumptions,
    },
  );

  const { stampTechnologyFromIntent } = await import('../lib/stampTechnologyFromIntent');
  stampTechnologyFromIntent(sed, site.projectIntent);

  // v1.2.1 HR-5 P2-2 / P2-3 — stamp the BTM-grounding intent sections (cooling
  // 3-axis + resolved PUE + water-by-sink + waste-heat credit; basis-explicit
  // power; firm-capacity / ELCC). Intent-stamped, ADDITIVE — legacy SEDs without
  // these fields are unaffected. Runs AFTER technology so the same intent is in
  // scope; reads grid.isoRegion (compiled above) for the firm-capacity section.
  const { stampBtmGroundingFromIntent } = await import('../lib/stampBtmGroundingFromIntent');
  stampBtmGroundingFromIntent(sed, site.projectIntent);

  // R-33 — stamp intent fingerprint + builtAt for shouldBootstrap readers.
  const { hashProjectIntent } = await import('../lib/sed/shouldBootstrap');
  sed.builtAt = sed.collectedAt;
  sed.builtForIntentHash = hashProjectIntent(site.projectIntent);
  if (site.studyAreaScope) sed.studyAreaScope = site.studyAreaScope;

  // ── MP4 / MP Phase 3 — Multi-Parcel SED awareness + per-parcel providers ──
  try {
    const { materializeSiteBoundaries } = await import('../lib/siteBoundaryGeometry');
    const parcels = materializeSiteBoundaries({
      siteBoundaries: site.siteBoundaries,
      siteBoundary: site.siteBoundary,
      siteArea: site.siteArea,
      sitePerimeter: site.sitePerimeter,
      siteCentroid: site.siteCentroid,
    });
    if (parcels.length >= 1) {
      sed.parcelCount = parcels.length;
      sed.isDisjoint = site.siteBoundaryAggregate?.isDisjoint ?? false;
      if (parcels.length > 1) {
        sed.parcels = parcels.map((p, i) => ({
          id: p.id,
          label: p.label ?? `Parcel ${i + 1}`,
          area: p.area,
          centroid: p.centroid,
          intendedUse: p.intendedUse,
          elevationAvg: null,
          floodZone: null,
          seismicSDC: null,
          zoning: null,
        }));

        const {
          fetchSiteElevation,
          fetchSiteFlood,
          fetchSiteSeismic,
          fetchSiteHabitat,
          fetchSiteZoning,
          buildParcelSedFields,
        } = await import('./providers/multiParcelProviders');
        const { logAuditEvent } = await import('../lib/telemetry/auditTelemetry');

        const [elevR, floodR, seismicR, habitatR, zoningR] = await Promise.all([
          fetchSiteElevation(site),
          fetchSiteFlood(site),
          fetchSiteSeismic(site),
          fetchSiteHabitat(site),
          fetchSiteZoning(site, store.userApiKeys),
        ]);

        const fieldMap = new Map(
          buildParcelSedFields(elevR, floodR, seismicR, zoningR).map(f => [f.id, f]),
        );
        sed.parcels = sed.parcels!.map(p => {
          const fields = fieldMap.get(p.id);
          return fields
            ? {
                ...p,
                elevationAvg: fields.elevationAvg ?? null,
                floodZone: fields.floodZone ?? null,
                seismicSDC: fields.seismicSDC ?? null,
                zoning: fields.zoning ?? null,
              }
            : p;
        });

        const stampPerParcel = (
          section: {
            perParcel?: Record<string, unknown>;
            parcelAggregate?: import('../types/siteEvidence').EvidenceSection<unknown>['parcelAggregate'];
          },
          perParcelItems: Array<{ parcelId: string; data: unknown }>,
          parcelMeta: import('./providers/multiParcelProviders').MultiParcelProviderResult<unknown>['parcelMeta'],
          providerKey: string,
        ) => {
          const perParcel: Record<string, unknown> = {};
          for (const item of perParcelItems) {
            if (item.data != null) perParcel[item.parcelId] = item.data;
          }
          if (Object.keys(perParcel).length > 0) section.perParcel = perParcel;
          if (parcelMeta?.varianceFlagged) {
            section.parcelAggregate = {
              varianceFlagged: true,
              meanTier: parcelMeta.meanTier,
              worstTier: parcelMeta.worstTier,
            };
            logAuditEvent({
              type: 'per-parcel-variance-flagged',
              siteId,
              payload: { providerId: providerKey },
            });
          }
        };

        if (sed.flood?.data && floodR.aggregate) {
          const aggregateFlood = floodR.aggregate;
          const aggregateQualification = aggregateFlood.analysisMethod === 'sample-sweep'
            ? 'estimated' as const
            : 'screening' as const;
          // Remove first-pass single-parcel optionals before stamping the
          // multi-parcel aggregate. If one parcel failed, retaining the main
          // parcel's exact areaAnalysis would falsely describe the whole site.
          const baseFlood = { ...sed.flood.data };
          delete baseFlood.zoneOverlap;
          delete baseFlood.sampledPoints;
          delete baseFlood.analysisMethod;
          delete baseFlood.fallbackReason;
          delete baseFlood.areaAnalysis;
          delete baseFlood.staticBfe;
          delete baseFlood.staticBfeFt;
          delete baseFlood.verticalDatum;
          delete baseFlood.firmPanelId;
          delete baseFlood.effectiveDate;
          delete baseFlood.regulatoryContext;
          const baseFloodSection = { ...sed.flood };
          delete baseFloodSection.dataAsOf;
          delete baseFloodSection.dataAsOfBasis;
          // The single reader-facing citation is the primary parcel's; when the
          // aggregate carries a geometry fallback tier the citation must say so
          // and must not call the site-wide intersection exact.
          const aggregateTier = aggregateFlood.areaAnalysis?.geometryTier;
          const aggregateSource = aggregateTier && typeof baseFloodSection.source === 'string'
            ? `${baseFloodSection.source} Multi-parcel aggregate: at least one parcel was computed with a disclosed ${aggregateTier} geometry fallback after an exact-precision clipping failure; the site-wide intersection is not exact and complete coverage is not claimed.`
            : baseFloodSection.source;
          sed.flood = {
            ...baseFloodSection,
            ...(aggregateSource !== undefined ? { source: aggregateSource } : {}),
            sourceGrade: 'authoritative-government',
            qualification: aggregateQualification,
            ...(aggregateFlood.effectiveDate ? {
              dataAsOf: aggregateFlood.effectiveDate,
              dataAsOfBasis: 'source-effective-date' as const,
            } : {}),
            sourceContributions: [{
              path: '*',
              grade: 'authoritative-government',
              qualification: aggregateQualification,
              source: aggregateSource,
              sourceUrl: baseFloodSection.sourceUrl,
              retrievedAt: baseFloodSection.retrievedAt,
              ...(aggregateFlood.effectiveDate ? { dataAsOf: aggregateFlood.effectiveDate } : {}),
            }],
            data: {
              ...baseFlood,
              floodZone: aggregateFlood.femaZone,
              sfha: aggregateFlood.isSpecialFloodHazard,
              floodRisk: aggregateFlood.floodRisk,
              ...(aggregateFlood.regulatoryContext ? { regulatoryContext: aggregateFlood.regulatoryContext } : {}),
              ...(aggregateFlood.screeningCoverage ? { screeningCoverage: aggregateFlood.screeningCoverage } : {}),
              ...(aggregateFlood.zoneOverlap ? {
                zoneOverlap: aggregateFlood.zoneOverlap.map(overlap => ({
                  ...overlap,
                  ...(aggregateFlood.areaAnalysis
                    ? {
                        acres: overlap.zone === 'UNMAPPED'
                          ? aggregateFlood.areaAnalysis.unmappedAreaAcres
                          : aggregateFlood.areaAnalysis.zoneAreas.find(zone => zone.zone === overlap.zone)?.acres,
                      }
                    : {}),
                })),
              } : {}),
              ...(aggregateFlood.sampledPoints != null ? { sampledPoints: aggregateFlood.sampledPoints } : {}),
              ...(aggregateFlood.analysisMethod ? { analysisMethod: aggregateFlood.analysisMethod } : {}),
              ...(aggregateFlood.fallbackReason ? { fallbackReason: aggregateFlood.fallbackReason } : {}),
              ...(aggregateFlood.areaAnalysis ? {
                areaAnalysis: {
                  ...aggregateFlood.areaAnalysis,
                  parcelAcres: aggregateFlood.areaAnalysis.parcelAreaAcres,
                  mapped: {
                    acres: aggregateFlood.areaAnalysis.mappedAreaAcres,
                    fraction: aggregateFlood.areaAnalysis.mappedFraction,
                  },
                  unmapped: {
                    acres: aggregateFlood.areaAnalysis.unmappedAreaAcres,
                    fraction: aggregateFlood.areaAnalysis.unmappedFraction,
                  },
                  onePercentAnnualChance: {
                    acres: aggregateFlood.areaAnalysis.onePercentAnnualChanceAcres,
                    fraction: aggregateFlood.areaAnalysis.onePercentAnnualChanceFraction,
                  },
                  zeroPointTwoPercentAnnualChance: {
                    acres: aggregateFlood.areaAnalysis.zeroPointTwoPercentAnnualChanceAcres,
                    fraction: aggregateFlood.areaAnalysis.zeroPointTwoPercentAnnualChanceFraction,
                  },
                  regulatoryFloodway: {
                    acres: aggregateFlood.areaAnalysis.floodwayAcres,
                    fraction: aggregateFlood.areaAnalysis.floodwayFraction,
                  },
                },
              } : {}),
              ...(aggregateFlood.staticBfeFt != null ? {
                staticBfe: aggregateFlood.staticBfeFt,
                staticBfeFt: aggregateFlood.staticBfeFt,
              } : {}),
              ...(aggregateFlood.verticalDatum ? { verticalDatum: aggregateFlood.verticalDatum } : {}),
              ...(aggregateFlood.firmPanelId ? { firmPanelId: aggregateFlood.firmPanelId } : {}),
              ...(aggregateFlood.effectiveDate ? { effectiveDate: aggregateFlood.effectiveDate } : {}),
            },
          };
          stampPerParcel(sed.flood, floodR.perParcel.map(i => ({ parcelId: i.parcelId, data: i.data })), floodR.parcelMeta, 'fema-flood');
        }
        if (sed.seismic?.data && seismicR.aggregate) {
          sed.seismic = { ...sed.seismic, data: { ...sed.seismic.data, ...seismicR.aggregate } };
          stampPerParcel(sed.seismic, seismicR.perParcel.map(i => ({ parcelId: i.parcelId, data: i.data })), seismicR.parcelMeta, 'usgs-seismic');
        }
        if (elevR.aggregate && sed.siteProfile?.data) {
          sed.siteProfile = {
            ...sed.siteProfile,
            data: {
              ...sed.siteProfile.data,
              elevationAvg: elevR.aggregate.elevationAvg ?? sed.siteProfile.data.elevationAvg,
              elevationMin: elevR.aggregate.elevationMin ?? sed.siteProfile.data.elevationMin,
              elevationMax: elevR.aggregate.elevationMax ?? sed.siteProfile.data.elevationMax,
            },
          };
          stampPerParcel(
            sed.siteProfile,
            elevR.perParcel.map(i => ({ parcelId: i.parcelId, data: i.data?.avg ?? null })),
            elevR.parcelMeta,
            'elevation',
          );
        }
        if (zoningR.aggregate?.primaryZone && sed.parcel?.data) {
          sed.parcel = {
            ...sed.parcel,
            data: { ...sed.parcel.data, zoning: zoningR.aggregate.primaryZone },
          };
          stampPerParcel(sed.parcel, zoningR.perParcel.map(i => ({ parcelId: i.parcelId, data: i.data })), zoningR.parcelMeta, 'regrid-parcels');
        }
        if (habitatR.aggregate && sed.environmental?.data) {
          sed.environmental = {
            ...sed.environmental,
            data: {
              ...sed.environmental.data,
              hasCriticalHabitat: habitatR.aggregate.hasCriticalHabitat,
              hasWetlands: habitatR.aggregate.hasWetlands,
              wetlandAcres: habitatR.aggregate.wetlandAcreage,
              wetlandAcreageBasis: habitatR.aggregate.wetlandAcreageBasis,
              wetlandSourceAcres: habitatR.aggregate.wetlandSourceAcres,
              wetlandSourceAcreageConflicts: habitatR.aggregate.wetlandSourceAcreageConflicts,
              wetlandAcreageIssues: habitatR.aggregate.wetlandAcreageIssues,
              wetlandMappingCoverage: habitatR.aggregate.wetlandMappingCoverage,
            },
          };
          stampPerParcel(sed.environmental, habitatR.perParcel.map(i => ({ parcelId: i.parcelId, data: i.data })), habitatR.parcelMeta, 'usfws-env');
        }
      }
    }
  } catch (e) {
    console.warn('[SED-BOOTSTRAP] MP Phase 3 per-parcel providers failed:', (e as Error)?.message);
  }

  // ── USGS 3DEP parcel topography ──────────────────────────────────
  // Auxiliary and non-blocking: process each parcel on its own local
  // east/north grid so disjoint holdings never share a gradient. Tests call
  // the provider directly with recorded fixtures; bootstrap unit tests skip
  // live network I/O.
  if (!process.env.VITEST && (site.siteBoundaries?.length || site.siteBoundary?.length)) {
    const retrievedAt = new Date().toISOString();
    try {
      const { materializeSiteBoundaries } = await import('../lib/siteBoundaryGeometry');
      const { fetchParcelTopography, isTopographyProviderEnabled } = await import('./dataProviders/topography');
      if (isTopographyProviderEnabled()) {
        const parcels = materializeSiteBoundaries({
          siteBoundaries: site.siteBoundaries,
          siteBoundary: site.siteBoundary,
          siteArea: site.siteArea,
          sitePerimeter: site.sitePerimeter,
          siteCentroid: site.siteCentroid,
        });
        if (parcels.length > 0) {
          const topography = await fetchParcelTopography(
            parcels.map(parcel => ({
              id: parcel.id,
              polygon: parcel.polygon,
              areaAcres: parcel.area,
            })),
            { requestedSpacingM: 20 },
          );
          const topographyLabel = topography.source === 'usgs-3dep'
            ? 'USGS 3D Elevation Program (3DEP)'
            : 'SRTM 1 arc-second (~30 m source resolution)';
          const topographySourceUrl = topography.source === 'usgs-3dep'
            ? 'https://elevation.nationalmap.gov/arcgis/rest/services/3DEPElevation/ImageServer'
            : 'https://lpdaac.usgs.gov/products/srtmgl1v003/';
          const topographySource = `${topographyLabel} — parcel-clipped terrain grid`;
          sed.topography = {
            status: 'verified',
            data: topography,
            source: topographySource,
            sourceUrl: topographySourceUrl,
            retrievedAt,
            sourceGrade: 'authoritative-government',
            qualification: 'screening',
            sourceContributions: [{
              path: '*',
              grade: 'authoritative-government',
              qualification: 'screening',
              source: topographySource,
              sourceUrl: topographySourceUrl,
              retrievedAt,
            }],
            confidence: 0.9,
            anomalies: [...topography.warnings],
            queryPoint: {
              lat: site.siteCentroid?.lat ?? lat,
              lng: site.siteCentroid?.lng ?? lng,
              source: 'boundary-sample',
            },
          };

          // Replace the legacy relief proxy only when neighbour-based gradients
          // exist. Otherwise retain and label the proxy honestly.
          if (sed.siteProfile?.data && topography.slopePercent) {
            const slopeSource = `${topographyLabel} ${topography.analysisSpacingM.toFixed(1)} m analysis grid`;
            sed.siteProfile = {
              ...sed.siteProfile,
              source: `User-drawn boundary + ${topographyLabel} parcel-clipped terrain grid`,
              sourceUrl: sed.topography.sourceUrl,
              retrievedAt,
              confidence: Math.max(sed.siteProfile.confidence, 0.9),
              sourceContributions: [
                ...(sed.siteProfile.sourceContributions ?? [])
                  .filter(item => item.path !== 'slopePercent'),
                {
                  path: 'slopePercent',
                  grade: 'authoritative-government',
                  qualification: 'screening',
                  source: slopeSource,
                  sourceUrl: sed.topography.sourceUrl,
                  retrievedAt,
                },
              ],
              data: {
                ...sed.siteProfile.data,
                elevationMin: topography.elevationM.min,
                elevationMax: topography.elevationM.max,
                elevationAvg: topography.elevationM.mean,
                elevationStdev: topography.elevationM.stdev,
                elevationPointsSampled: topography.pointsReturned,
                slopePercent: topography.slopePercent.mean,
                slopeMethod: 'topography-grid',
                slopeSource,
              },
            };
          } else if (sed.siteProfile?.data && !sed.siteProfile.data.slopeMethod) {
            sed.siteProfile = stampReliefProxyEvidence(sed.siteProfile, retrievedAt);
          }
        }
      }
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      sed.topography = {
        status: 'unavailable',
        data: null,
        source: `Topography unavailable — ${reason}`,
        sourceUrl: 'https://elevation.nationalmap.gov/arcgis/rest/services/3DEPElevation/ImageServer',
        retrievedAt,
        sourceGrade: 'authoritative-government',
        qualification: 'unavailable',
        sourceContributions: [{
          path: '*',
          grade: 'authoritative-government',
          qualification: 'unavailable',
          source: 'USGS 3D Elevation Program (3DEP) — parcel-clipped terrain grid',
          sourceUrl: 'https://elevation.nationalmap.gov/arcgis/rest/services/3DEPElevation/ImageServer',
          retrievedAt,
        }],
        confidence: 0,
        anomalies: [],
        emptyReason: reason,
      };
      if (sed.siteProfile?.data && !sed.siteProfile.data.slopeMethod) {
        sed.siteProfile = stampReliefProxyEvidence(sed.siteProfile, retrievedAt);
      }
      console.warn('[SED-BOOTSTRAP] USGS 3DEP topography unavailable:', reason);
    }
  }

  // ── KSD2 — KMZ-derived onSiteInfrastructure ──
  // Project `site.siteLayers` (user-uploaded KMZ overlays) onto the
  // structured `onSiteInfrastructure` section. Always runs — when the
  // site has no layers the helper returns `status: 'unavailable'`
  // (mirrors every other provider-less section). Cross-binds the
  // closest substation distance into `sed.grid.data.substationDistKm`
  // with provenance stamping when HIFLD missed a closer BTM /
  // co-op / private substation that the KMZ surfaces.
  try {
    const {
      compileOnSiteInfrastructure,
      crossBindGridFromKmz,
      crossBindAllKmzCorridors,
      normalizeOnSiteInfrastructureAliases,
    } =
      await import('./siteEvidence/compileOnSiteInfrastructure');
    const { materializeSiteBoundaries } = await import('../lib/siteBoundaryGeometry');
    const parcelsForKsd2 = materializeSiteBoundaries({
      siteBoundaries: site.siteBoundaries,
      siteBoundary: site.siteBoundary,
      siteArea: site.siteArea,
      sitePerimeter: site.sitePerimeter,
      siteCentroid: site.siteCentroid,
    });
    const centroidForKsd2 = parcelsForKsd2.length > 0
      ? parcelsForKsd2[0].centroid
      : (site.siteCentroid ?? { lat, lng });
    // KSI-4 — best-effort `sourceKmzDocIds`. KSD8 will replace this
    // with per-layer authoritative ids; today we scan `site.documents`
    // for KMZ-typed uploads and pass their ids so PRV / EVL can
    // attribute the layer-derived facts back to a non-empty doc list.
    // A `.kmz` extension OR the canonical Google-Earth KMZ mimetype
    // counts; the legacy `.kml` (XML, single-doc) is also accepted
    // because the parser handles both.
    const kmzDocIds = (site.documents ?? [])
      .filter(d => {
        const name = (d.fileName ?? '').toLowerCase();
        const mime = (d.mimeType ?? '').toLowerCase();
        return (
          name.endsWith('.kmz') ||
          name.endsWith('.kml') ||
          mime.includes('google-earth') ||
          mime === 'application/vnd.google-earth.kmz' ||
          mime === 'application/vnd.google-earth.kml+xml'
        );
      })
      .map(d => d.id);
    sed.onSiteInfrastructure = compileOnSiteInfrastructure(
      canonicalSiteLayers,
      centroidForKsd2,
      parcelsForKsd2,
      kmzDocIds,
    );
    normalizeOnSiteInfrastructureAliases(sed.onSiteInfrastructure);
    if (sed.onSiteInfrastructure?.status === 'verified-from-kmz') {
      const grid = sed.grid as typeof sed.grid & {
        data: { substationDistKm: number; substationDistKmProvenance?: 'provider' | 'kmz-import' } | null;
      };
      const bound = crossBindGridFromKmz(grid, sed.onSiteInfrastructure);
      if (bound) {
        console.log(`[SED-BOOTSTRAP] KSD2 cross-bind: grid.substationDistKm <- KMZ (${sed.grid.data?.substationDistKm} km)`);
      }
      // KSI — fiber / transmission / pipeline corridor cross-binds.
      // Substations remain on `crossBindGridFromKmz` above (points, not
      // lines). The KSI helper writes connectivity.nearestFiberKm,
      // grid.nearestTransmissionKm, and surfaces the pipeline aggregate
      // already populated at compile time.
      const landed = crossBindAllKmzCorridors(sed);
      if (landed.length > 0) {
        console.log(`[SED-BOOTSTRAP] KSI corridor cross-bind: ${landed.join(', ')}`);
      }
    }
  } catch (e) {
    // Defensive — onSiteInfrastructure is additive; failures here
    // must not abort the bootstrap. Log and proceed with the legacy
    // SED shape (no onSiteInfrastructure field).
    console.warn('[SED-BOOTSTRAP] KSD2 onSiteInfrastructure compile failed:', (e as Error)?.message);
  }

  // ── v1.2.1 HR-5 P2-4 / P2-3 — fuelSupply + lcoe ──
  // Stamp the natural-gas fuel-supply section (EIA price + KMZ pipeline
  // proximity, read from the onSiteInfrastructure compiled just above) and the
  // LCOE economics anchor. Runs AFTER onSiteInfrastructure so the pipeline
  // proximity is available. ADDITIVE + best-effort — failures never abort.
  // NOTE (v1.3.1 review): `applyGroundedGasFuelCost` (grounding
  // financialAssumptions.fuelCostPerMwhUsd from the EIA price) is
  // DELIBERATELY not wired here — mutating financial assumptions from a
  // SED bootstrap needs the store-side provenance/persistence path, not a
  // SED-doc side effect. The SED fuelSupply/lcoe sections carry the
  // grounded price; FA grounding is a follow-up (see groundGasFuelCost.ts).
  try {
    const { stampFuelSupplyAndLcoe } = await import('./siteEvidence/compileFuelSupplyAndLcoe');
    stampFuelSupplyAndLcoe(sed, fastData?.naturalGas, fastData?.providerStatuses ?? []);
    // SED D2 — stamp the typed interconnection (ISO/RTO queue, provider-grounded
    // via isoQueueProvider.fetchChain) + incentives (pure IRA tax-credit
    // calculator off financialAssumptions) sections. ASYNC + awaited (it awaits
    // fetchChain). ADDITIVE + best-effort — the stamp degrades to intent-only on
    // a rate-limited ISO fetch and never aborts the bootstrap.
    const { stampInterconnectionAndIncentives } = await import('./siteEvidence/compileInterconnectionAndIncentives');
    const stateCode = inferStateCodeFromSed(sed);
    await stampInterconnectionAndIncentives(sed, {
      stateCode,
      lat: sed.location.lat,
      lng: sed.location.lng,
      intent: site.projectIntent,
      btmStrategy: site.typedPreferencesDefaults?.['power-availability']?.btmStrategy,
      financial: sed.financialAssumptions,
      providerStatuses: fastData?.providerStatuses,
    });
    // SED D2 (H-6) — stamp the typed multiHazard (FEMA National Risk Index,
    // provider-grounded — wildfire / strong-wind / hail / drought beyond the
    // existing flood + seismic sections; honest-unavailable off-territory) +
    // transmissionHeadroom + waterResource (honest-unavailable typed sections —
    // real sources named as follow-up, never fabricated). ASYNC + awaited
    // (multiHazard awaits the NRI fetch). ADDITIVE + best-effort — degrades to
    // honest unavailable on any fetch error and never aborts the bootstrap.
    const { stampMultiHazardAndResources } = await import('./siteEvidence/compileMultiHazardAndResources');
    await stampMultiHazardAndResources(sed, {
      lat: sed.location.lat,
      lng: sed.location.lng,
    });
    // Re-run cross-validation + recompute gaps/completeness so the HR-5
    // BTM-aware checks (cooling-sink vs water-stress, gas-fuel-cost grounding,
    // ELCC vs intermittent) fire now that cooling/power/firmCapacity/lcoe/
    // fuelSupply + interconnection/incentives + multiHazard/transmissionHeadroom/
    // waterResource are stamped (they didn't exist at compileSiteEvidence time).
    const { rerunCrossValidationAndMetadata } = await import('./siteEvidence');
    rerunCrossValidationAndMetadata(sed, fastData?.providerStatuses ?? []);
  } catch (e) {
    console.warn('[SED-BOOTSTRAP] HR-5 fuelSupply/lcoe/interconnection/incentives + D2 H-6 hazard/headroom/water stamp failed:', (e as Error)?.message);
  }
  // KSD9-style heartbeat point — informational only, not a milestone
  // (kept off PROGRESS_MILESTONE_STAGES to preserve the existing N/4
  // counter shape). The compile stage that follows below IS the
  // milestone.
  logProgress(siteId, 'compile-infra', 78);

    logProgress(siteId, 'compile', 75);
  } else {
    // R-98: checkpoint already carries a compiled SED; skip fetch+compile.
    sed = resumeSed as SiteEvidenceDocument;
    logProgress(siteId, 'compile', 75);
  }
  // Keep onSiteInfrastructure scalar aliases current for both fresh
  // compile and checkpoint-resume paths.
  try {
    const { normalizeOnSiteInfrastructureAliases } =
      await import('./siteEvidence/compileOnSiteInfrastructure');
    normalizeOnSiteInfrastructureAliases(sed.onSiteInfrastructure);
  } catch {
    // Additive safety net only; ignore if the helper import fails.
  }
  // R-98: checkpoint write is best-effort and follows milestone progress logging.
  await maybeWriteCheckpoint('compile');

  // ── Doc → section linkage (Documents_On_Site Prompt 5 + DM4) ──
  // For each chosen doc, score it against every SED section it claims
  // helpful and bucket the result into Primary / Supporting. The
  // ranked refs land on `slot.linkedDocumentRefs`; `linkedDocumentIds`
  // is the derived back-compat projection so existing readers (prompt
  // assembly, the text formatter) continue to see
  // the legacy id-list shape.
  //
  // DM5: any `(docId, sectionKey)` the user has disconnected is
  // omitted from scoring entirely — preserved via UD's
  // `mergePreservedUserDecisions` pattern in the SED save path.
  if (input.includeDocIds && input.includeDocIds.length > 0) {
    const { buildSectionLinksRanked, projectLinkedDocumentIds, buildSectionLinks } =
      await import('./knowledge/sedDocSuggestion');

    // Legacy linker always runs first — it's the back-compat floor:
    // user-tagged `sedSectionsHelpful` always produces an
    // `linkedDocumentIds` entry even if DM4 scoring is unavailable
    // (Gemini outage, Firestore outage, MODEL_VERSION cache miss
    // during a degraded run). The ranked path THEN augments with
    // `linkedDocumentRefs` (role + rationale) on top of the legacy
    // ids — no link the user has curated is ever lost.
    const legacyLinks = buildSectionLinks(site, input.includeDocIds);
    for (const [secId, docIds] of Object.entries(legacyLinks)) {
      const slot = (sed as unknown as Record<string, EvidenceSection<unknown> | undefined>)[secId];
      if (!slot || !docIds || docIds.length === 0) continue;
      slot.linkedDocumentIds = Array.from(new Set([...(slot.linkedDocumentIds ?? []), ...docIds]));
    }

    // DM4 ranked layer — best-effort. Any (doc, section) pair that
    // clears the supporting threshold gets a `linkedDocumentRefs`
    // entry with rationale + quotes. Scoring failures degrade
    // silently: the legacy id-list above stays in place.
    try {
      const disconnects = (site as unknown as {
        userDisconnectedLinks?: Array<{ docId: string; targetId: string }>;
      }).userDisconnectedLinks ?? [];
      const rankedLinks = await buildSectionLinksRanked(site, input.includeDocIds, {
        siteId,
        disconnectedLinks: disconnects,
      });
      for (const [secId, refs] of Object.entries(rankedLinks)) {
        const slot = (sed as unknown as Record<string, EvidenceSection<unknown> | undefined>)[secId];
        if (!slot || !refs || refs.length === 0) continue;
        const merged: import('../types/siteEvidence').LinkedDocumentRef[] = [
          ...(slot.linkedDocumentRefs ?? []).filter(r => !refs.some(n => n.docId === r.docId)),
          ...refs,
        ];
        slot.linkedDocumentRefs = merged;
        // Re-project so refs's primary-first ordering shows in the
        // legacy id-list view too. Union with whatever the legacy
        // linker stamped above so a doc the user tagged but the
        // scorer dropped (sub-threshold) is still listed.
        const refIds = projectLinkedDocumentIds(merged);
        slot.linkedDocumentIds = Array.from(new Set([...refIds, ...(slot.linkedDocumentIds ?? [])]));
      }
    } catch (e) {
      // Soft-fail — legacy ids already stamped, just skip refs.
      console.warn('[sed-bootstrap] DM4 ranked refs unavailable (legacy links retained):', (e as Error)?.message);
    }
  }

  // ── DM5: preserve userDisconnectedLinks across rebuild ──
  // Disconnects live on `site.userDisconnectedLinks` (not on the SED)
  // so they naturally survive a rebuild that doesn't touch the field.
  // We use `mergePreservedUserDecisions` here to detect orphans — a
  // disconnect targeting a doc that no longer exists on the site
  // (the user deleted it, e.g.) gets pruned + audit-logged via
  // `writePreservationAudit({ kind: 'evidence-link-disconnects' })`.
  //
  // Identity is `${docId}::${targetId}` — mirrors DM3 / DM4 / UD
  // pattern. Fresh = empty array because rebuild itself never creates
  // disconnects (only user action does); prior carries every user
  // decision, so the merge is effectively "carry prior forward minus
  // orphans".
  try {
    const priorDisconnects = (site.userDisconnectedLinks ?? []) as Array<{
      docId: string;
      targetId: string;
      [k: string]: unknown;
    }>;
    if (priorDisconnects.length > 0) {
      const liveDocIds = new Set((site.documents ?? []).map(d => d.id));
      // Treat each prior disconnect as "fresh" UNLESS its docId no
      // longer exists — that's the orphan case. We construct the
      // fresh list as the surviving prior entries; UD's merge then
      // preserves their identity rows and fires onOrphan for the
      // dropped ones.
      const surviving = priorDisconnects.filter(d => liveDocIds.has(d.docId));
      let orphanCount = 0;
      const { mergePreservedUserDecisions } = await import('../lib/preservedUserDecisions');
      const { writePreservationAudit } = await import('../lib/preservationAudit');
      const [, audit] = mergePreservedUserDecisions({
        prior: priorDisconnects,
        fresh: surviving,
        identity: d => `${d.docId}::${d.targetId}`,
        preserveFields: ['disconnectedAt', 'disconnectedBy', 'rationaleAtDisconnect'] as const,
        onOrphan: () => { orphanCount += 1; },
      });
      // If orphans were found, prune them out of the site's list. The
      // store update happens via the usual save path — we mutate the
      // in-memory `site` so the save below carries the pruned list.
      if (orphanCount > 0) {
        (site as { userDisconnectedLinks?: typeof priorDisconnects }).userDisconnectedLinks = surviving;
      }
      if (audit.length > 0 || orphanCount > 0) {
        console.log(
          `[sed-bootstrap] DM5 — preserved ${audit.length} prior disconnect(s)` +
          (orphanCount > 0 ? `; dropped ${orphanCount} orphaned disconnect(s)` : ''),
        );
        void writePreservationAudit({
          siteId,
          kind: 'evidence-link-disconnects',
          audit,
          orphanCount,
          context: { sedVersion: sed.version },
        });
      }
    }
  } catch (e) {
    console.warn('[sed-bootstrap] DM5 disconnect preservation skipped:', (e as Error)?.message);
  }

  // ── Provider → Doc fallback + boost (Documents_On_Site Prompt 11) ──
  // For every SED section, if the provider returned 'unavailable' AND a
  // qualifying uploaded doc exists, run a Gemini extraction pass; on
  // success, stamp the section as 'verified-from-doc' with
  // confidence=DOC_FALLBACK_CONFIDENCE. For sections the provider DID
  // fill, compute an adjustedConfidence boost based on the strongest
  // matching doc's relevance score so high-quality user evidence
  // bumps section confidence without overwriting provider data.
  if (!isAtOrAfterStage(effectiveResumeStage, 'doc-aware')) {
    await applyDocAwareConfidence(sed, site, { siteId, bodyLoader: createSiteDocumentBodyLoader({ siteId, ownerUid: siteLayerOwnerUid }) });
    // SD3 — non-milestone heartbeat through the silent 75→90 gap. The
    // user-visible N/4 counter stays anchored to the four milestone
    // stages (fetch-fast-done → compile → gcs-saved → done); this write
    // only updates `pct` + `lastProgressAt` so the watchdog (LB1-2) has
    // a heartbeat signal and the stall banner doesn't fire on a healthy
    // doc-aware pass. NOT added to PROGRESS_MILESTONE_STAGES.
    logProgress(siteId, 'doc-aware', 80);
    // R-98: checkpoint write is best-effort and follows milestone progress logging.
    await maybeWriteCheckpoint('doc-aware');
  } else {
    logProgress(siteId, 'doc-aware', 80);
  }

  // ── Data Completion Orchestrator DCO-3 — diligent per-slot research ──
  //
  // Carries the constitutional principle: "Never rush SED creation;
  // instruct the AI to diligently consider all sources." After
  // applyDocAwareConfidence has run (provider + uploaded-doc + authored
  // tiers), any slot still 'unavailable' gets a per-slot grounded AI
  // Research dispatch when a template exists for it.
  //
  // Gated behind `VITE_DCO_LIVE` (default OFF). Off → noop, the slot
  // stays 'unavailable'. On → the orchestrator dispatches the templated
  // slots and stamps `verified-from-research` with the source URL.
  //
  // Reproducibility invariant: this only runs during a LIVE SED
  // rebuild. Historical reports' `evidenceSnapshotAtGen` were captured
  // BEFORE this code shipped — they are not touched.
  try {
    const { isDcoLiveModeEnabled, fillSedSlotDiligently } = await import('./dataCompletionOrchestrator');
    // Resolve report type. `applyDocAwareConfidence` already accepts an
    // optional reportType; the top-up path (`topUpSiteSed`) passes
    // through `opts.reportType` when callers provide it. For
    // sedBootstrap's main path (this caller), we default to
    // 'feasibility' to match `applyDocAwareConfidence`'s default — the
    // dispatched research is reportType-aware but the templates are
    // typically state-scoped (e.g. `regulatory.nuclearStance` doesn't
    // change between report types).
    const reportType: ReportTypeId = 'feasibility';
    if (isDcoLiveModeEnabled(reportType)) {
      let damForBootstrap: import('../types/datacrateModel').DatacrateModel | null = null;
      try {
        const { loadCurrentDam } = await import('./dam/damStorage');
        damForBootstrap = await loadCurrentDam(reportType);
      } catch (e) {
        console.warn(
          `[dco:orchestrator] loadCurrentDam(${reportType}) failed; falling through to research-only:`,
          (e as Error)?.message,
        );
      }
      const stateCode = inferStateCodeFromSed(sed);
      const orchestratorResult = await fillSedSlotDiligently(sed, {
        reportType,
        site: {
          id: site.id,
          name: sed.siteName,
          address: sed.location.address,
          centroid: [sed.location.lat, sed.location.lng],
          locationLabel: sed.location.address,
          stateCode: stateCode ?? undefined,
          countryCode: 'US',
        },
        dam: damForBootstrap,
      });
      if (orchestratorResult.filled > 0 || orchestratorResult.damFilled > 0 ||
          orchestratorResult.attemptedAndRejected > 0) {
        console.log(
          `[dco:orchestrator] filled=${orchestratorResult.filled}` +
          ` dam=${orchestratorResult.damFilled}` +
          ` rejected=${orchestratorResult.attemptedAndRejected}` +
          ` skipped=${orchestratorResult.skippedNoTemplate}` +
          ` (site=${siteId}, damLoaded=${damForBootstrap != null})`,
        );
      }
    }
  } catch (e) {
    // Best-effort — never sink the bootstrap on a DCO failure.
    console.warn('[dco:orchestrator] dispatch failed:', (e as Error)?.message);
  }

  // ── RDX-4 — Report-derived tier ──
  //
  // After provider + uploaded-doc + authored + research tiers have run,
  // apply any prior-report claims for this site as the next tier down.
  // The merger refuses to overwrite a more-authoritative tier (UAE /
  // DCO research / DAM / SSM doc-override), so this composes safely
  // with everything that ran before.
  //
  // Reproducibility invariant: this only fires during a LIVE SED
  // rebuild and only mutates the in-memory SED that is about to be
  // persisted as the NEW version. Historical reports'
  // `evidenceSnapshotAtGen` are frozen and cannot be touched here.
  //
  // Best-effort: a Firestore read failure / a missing slot / a tier
  // guard rejection all degrade to "no-op for that claim" — the
  // bootstrap proceeds.
  try {
    const { applyReportClaimsToSed } = await import('./sedReportIngestion');
    // Use the in-progress SED's id. On the first-ever bootstrap this is
    // freshly minted and the claims sub-collection won't exist yet; the
    // resolver returns `applied=0` and we move on. On rebuilds and on
    // sites with `site.sedId` already set this matches the claims path
    // the post-gen ingestion hook writes to.
    const resolvableSedId = site.sedId ?? sed.id;
    if (user.uid && resolvableSedId) {
      const rdxResult = await applyReportClaimsToSed({
        uid: user.uid,
        sedId: resolvableSedId,
        sed: sed as unknown as Record<string, unknown>,
      });
      if (rdxResult.applied > 0 || rdxResult.guarded > 0 || rdxResult.noSlot > 0) {
        console.log(
          `[RDX:resolver] applied=${rdxResult.applied}` +
          ` guarded=${rdxResult.guarded}` +
          ` no-slot=${rdxResult.noSlot}` +
          ` superseded-dropped=${rdxResult.superseded}` +
          // SED D3.6 — resolution stamps written back this rebuild.
          ` resolutions-written=${rdxResult.resolutionsWritten}` +
          ` (site=${siteId})`,
        );
      }
    }
  } catch (e) {
    console.warn('[RDX:resolver] tier-apply failed:', (e as Error)?.message);
  }

  // ── CAR-6 — Coverage AutoFill SED slot wiring ──
  //
  // After every higher-priority tier has run (provider + user-doc +
  // authored + DCO research + DAM + report-derived), populate any
  // remaining `unavailable` slots from the CAR provider chains'
  // coverage transforms. Idempotent — safe to re-run on the same SED.
  //
  // The auto-fill tier is the LAST tier before honest-missing. It only
  // ever fills slots that no other tier could; doc uploads that arrive
  // later supersede an auto-fill stamp atomically because the next
  // bootstrap pass runs `applyDocAwareConfidence` BEFORE this block,
  // flipping the slot to `'verified-from-doc'` and pre-empting the
  // CAR-6 check (which short-circuits on non-`unavailable` slots).
  //
  // Input synthesis: the bootstrap doesn't yet drive the CAR chain
  // dispatchers (CAR-FOLLOWUP-DISPATCHER). For now we synthesise the
  // chain shapes from the SED's already-fetched legacy provider data
  // (`sed.grid.data` → GridData; `sed.flood.data` → FloodData). When
  // the dispatchers land, the input bundle expands to include
  // geotechnical / title / zoning chain responses.
  //
  // Best-effort: a per-chain helper failure logs + continues; the
  // bootstrap never aborts on a CAR-6 stamp error.
  try {
    const { applyAutoFillToSedSlots } = await import('../lib/coverage/applyAutoFillToSedSlots');
    const { parcelSedToZoningData } = await import('../lib/coverage/zoningCoverageFill');
    // Synthesise the input bundle from what's already on the SED.
    // GridData and FloodData live on `slot.data` after the dispatcher
    // pass; geotechnical / title / zoning chains are not yet fetched.
    const gridData = sed.grid?.data as
      | import('../types/siteRag').GridData
      | null
      | undefined;
    const floodLegacy = sed.flood?.data;
    // Bridge legacy FloodData (femaFlood.ts) → CAR-5 chain FloodData
    // shape (flood/types.ts). Only the zone is required by the CAR-6
    // helper; bfeFt / datum / panelId come from the chain when wired.
    const car5FloodData: import('../services/dataProviders/flood/types').FloodData | null =
      floodLegacy && typeof (floodLegacy as { floodZone?: unknown }).floodZone === 'string'
        ? { zone: (floodLegacy as { floodZone: string }).floodZone }
        : null;
    // GridData provider shape — we use the actual GridData type if the
    // dispatcher persisted it; otherwise lift from GridEvidence shape.
    const car2GridData: import('../types/siteRag').GridData | null = gridData
      ? gridData
      : sed.grid?.data
        ? {
            substationDist: (sed.grid.data as { substationDistKm?: number }).substationDistKm ?? 0,
            capacity: (sed.grid.data as { capacity?: 'High' | 'Limited' | 'Constrained' }).capacity ?? 'Limited',
            complexity: (sed.grid.data as { complexity?: 'Low' | 'Medium' | 'High' }).complexity ?? 'Medium',
            voltage: (sed.grid.data as { voltage?: string }).voltage ?? '',
            ...((sed.grid.data as { substationName?: string }).substationName
              ? { nearestSubstationName: (sed.grid.data as { substationName: string }).substationName }
              : {}),
          }
        : null;
    const car4ZoningData = parcelSedToZoningData(sed);
    const autoFillResult = applyAutoFillToSedSlots(sed, {
      ...(fastData?.geotechnical ? { geotechnical: fastData.geotechnical } : {}),
      ...(car2GridData ? { grid: car2GridData } : {}),
      ...(car5FloodData ? { flood: car5FloodData } : {}),
      ...(car4ZoningData
        ? { zoning: car4ZoningData, zoningContributingProviders: ['regrid-parcels'] as const }
        : {}),
    });
    if (autoFillResult.slotsStamped > 0) {
      console.log(
        `[CAR-6] auto-fill stamped ${autoFillResult.slotsStamped} slot(s)` +
          ` sections=${Object.keys(autoFillResult.perSection).join(',')}` +
          ` (site=${siteId})`,
      );
    }
    // Conflicts surfaced by CAR-6 cross-provider disagreement go into
    // the SED's evidenceConflicts list — the next block
    // (`detectEvidenceConflicts`) merges them with the doc-vs-provider
    // conflicts. Best-effort: an empty array is the no-disagreement
    // case (no-op).
    if (autoFillResult.conflicts.length > 0) {
      const sedAug = sed as SiteEvidenceDocument & {
        evidenceConflicts?: import('./evidenceConflict').EvidenceConflict[];
      };
      sedAug.evidenceConflicts = [
        ...(sedAug.evidenceConflicts ?? []),
        ...autoFillResult.conflicts,
      ];
    }
  } catch (e) {
    console.warn('[CAR-6] auto-fill SED slot wiring failed:', (e as Error)?.message);
  }

  // ── Provider/document conflict detection (Documents_On_Site Prompt 12) ──
  // Best-effort: a Gemini-call failure here doesn't abort the bootstrap.
  // The detector skips sections without supportingDocIds so no Gemini
  // call fires unless an actual provider-vs-document pair exists.
  if (!isAtOrAfterStage(effectiveResumeStage, 'conflict-detect')) {
    try {
    const { detectConflicts, SiteStateRequiredError } = await import('../lib/detectConflicts');
    const { inferSiteState } = await import('../lib/siteStateInference');
    const { applyConflictsToSed } = await import('./evidenceConflict');
    // KSD3 — honour the user's `includeInSed: false` exclusion alongside
  // the legacy `library-index` filter. Absent flag ≡ included (default
  // true). New code paths should mirror this two-clause filter.
  const userDocs = (site.documents ?? []).filter(d =>
    !((d.tags ?? []).includes('library-index')) && d.includeInSed !== false,
  );
    // ICR-FU-bootstrap — pass authored claims from siteContext so ICR-4
    // (authored-vs-provider) fires in real bootstrap runs. The detector
    // treats `authoredClaims` as an additive second pass; gate widened
    // to (userDocs.length > 0 || authoredClaims.length > 0).
    const authoredClaims = site.siteContext?.claims ?? [];
    if (userDocs.length > 0 || authoredClaims.length > 0) {
      // RDX-6 — also load prior-report-derived claims for the third
      // (report-vs-provider) detection pass. Best-effort: a Firestore
      // failure leaves `priorReportClaims = []` and the detector
      // behaves exactly as it did pre-RDX-6.
      let priorReportClaims: import('../types/reportClaim').ReportDerivedClaim[] = [];
      try {
        const { firestoreClaimsAdapter } = await import('./sedReportIngestion');
        const resolvableSedId = site.sedId ?? sed.id;
        if (user.uid && resolvableSedId) {
          const adapter = firestoreClaimsAdapter(user.uid, resolvableSedId);
          if (adapter.listAllClaims) {
            priorReportClaims = await adapter.listAllClaims();
          }
        }
      } catch (e) {
        console.warn('[RDX-6] priorReportClaims load failed:', (e as Error)?.message);
      }
      // R-7 / DAT-FOLLOWUP-2 — site-state prior for the cross-state
      // conflict pass. Prefer site identity (location.state / address)
      // over SED fields that may have been poisoned by doc extraction.
      let resolvedSiteState: string | null = inferSiteState(site);
      if (!resolvedSiteState) {
        try {
          const { inferSiteStateFromSed } = await import('../lib/inferSiteState');
          resolvedSiteState = inferSiteStateFromSed(sed);
        } catch (e) {
          console.warn('[DAT-FOLLOWUP-2] siteState resolve failed:', (e as Error)?.message);
        }
      }
      if (!resolvedSiteState) {
        throw new SiteStateRequiredError(
          'Could not infer site state from location or address — set state on the site or use a US address with a state code.',
        );
      }
      const freshConflicts = await detectConflicts(sed, userDocs, resolvedSiteState, {
        authoredClaims,
        priorReportClaims,
      });

      // UD2 — overlay prior resolutions on fresh conflicts by stable
      // identity. A conflict's identity is the (sectionId, field,
      // docId) triple — the same triple `applyConflictResolution`
      // at evidenceConflict.ts:369-374 uses to match a user's pick
      // to a specific conflict row. preserveFields is the explicit
      // allow-list of decision fields so freshly-computed severity /
      // explanation / suggestedResolution from the Gemini comparison
      // still win.
      const { loadPriorSedForSite } = await import('./sedLoader');
      const { mergePreservedUserDecisions } = await import('../lib/preservedUserDecisions');
      const { writePreservationAudit } = await import('../lib/preservationAudit');
      const { CONFLICT_PIPELINE_VERSION } = await import('../lib/conflictPrefilter');
      const priorSed = await loadPriorSedForSite(siteId);
      const priorAug = priorSed as SiteEvidenceDocument & {
        evidenceConflicts?: Array<import('./evidenceConflict').EvidenceConflict>;
        conflictsPipelineVersion?: number;
      };
      // ICR-FU-3 — discard prior conflicts when the cached pipeline
      // version is stale (or absent — pre-FU SEDs). The fresh
      // detector has already run with the new prefilter, so merging
      // against the old rows would re-surface garbage we just learnt
      // to suppress. User resolutions on identities the fresh
      // detector still emits are preserved through the next merge
      // pass; resolutions on identities the prefilter now eliminates
      // are intentionally dropped (the conflict no longer exists).
      const priorVersion = priorAug?.conflictsPipelineVersion;
      const versionStale = priorVersion == null || priorVersion < CONFLICT_PIPELINE_VERSION;
      const priorConflicts = versionStale ? [] : (priorAug?.evidenceConflicts ?? []);
      if (versionStale && (priorAug?.evidenceConflicts?.length ?? 0) > 0) {
        console.log(
          `[sed-bootstrap] ICR-FU-3 — discarding ${priorAug?.evidenceConflicts?.length ?? 0} cached conflict row(s)` +
            ` from pipeline v${priorVersion ?? '?'} (current v${CONFLICT_PIPELINE_VERSION}); fresh detector re-evaluates.`,
        );
      }

      let orphanCount = 0;
      const [mergedConflicts, audit] = mergePreservedUserDecisions({
        prior: priorConflicts as unknown as Array<Record<string, unknown>>,
        fresh: freshConflicts as unknown as Array<Record<string, unknown>>,
        identity: c => `${c.sectionId as string}::${c.field as string}::${c.docId as string}`,
        preserveFields: ['userResolution', 'resolvedAt', 'resolvedBy'] as const,
        onOrphan: () => { orphanCount += 1; },
      });

      if (mergedConflicts.length > 0) {
        const annotated = applyConflictsToSed(
          sed,
          mergedConflicts as unknown as Array<import('./evidenceConflict').EvidenceConflict>,
        );
        // applyConflictsToSed clones — copy back into the in-place ref.
        (sed as SiteEvidenceDocument & {
          evidenceConflicts?: Array<import('./evidenceConflict').EvidenceConflict>;
        }).evidenceConflicts = annotated.evidenceConflicts;
      }

      if (audit.length > 0 || orphanCount > 0) {
        console.log(
          `[sed-bootstrap] UD2 — preserved ${audit.length} prior conflict resolution(s)` +
          (orphanCount > 0 ? `; dropped ${orphanCount} orphaned prior decision(s)` : ''),
        );
        void writePreservationAudit({
          siteId,
          kind: 'sed-conflicts',
          audit,
          orphanCount,
          context: { sedVersion: sed.version },
        });
      }
    }
    } catch (e) {
      if ((e as Error)?.name === 'SiteStateRequiredError') throw e;
      console.warn('[evidenceConflict] detection skipped:', (e as Error)?.message);
    }
    // SD3 — second non-milestone heartbeat after conflict detection
    // (whether it succeeded, threw, or skipped because no docs are
    // attached). Ensures the gap between `doc-aware` and `gcs-saved` is
    // also ≤30s heartbeat for the watchdog.
    logProgress(siteId, 'conflict-detect', 85);
    // R-98: checkpoint write is best-effort and follows milestone progress logging.
    await maybeWriteCheckpoint('conflict-detect');
  } else {
    logProgress(siteId, 'conflict-detect', 85);
  }

  // ── LLW-1 — Live Literature retrieval ──
  // Call the externalLiteratureOrchestrator after the SED is fully
  // assembled so deriveSiteSeedTerms can read regulatory.state,
  // waterStress.bwsLabel, etc. Cost-bounded: per-report-type query
  // profile selects 1-3 free providers (Crossref + arXiv + PubMed),
  // ~6 queries × per-task timeout. NEVER throws — orchestrator
  // catches every per-provider error internally and returns a
  // shaped `ExternalLiteratureSection` so we always have something
  // to write.
  await maybeFetchExternalLiterature(sed, site, /* prior */ undefined, siteId);

  // ── PSE-3 — Public Sentiment retrieval ──
  // Call the publicSentimentOrchestrator after the SED is fully
  // assembled so `deriveLocalitySeedTerms` can read regulatory.state
  // + location.address. Cost-bounded by the Firestore scan-age cache
  // (`pse-cache` collection) — a cache HIT short-circuits the
  // orchestrator fan-out entirely. NEVER throws — orchestrator catches
  // every per-source error internally and returns a shaped
  // `PublicSentimentSection` so we always have something to write.
  await maybeFetchPublicSentiment(sed, /* prior */ undefined, siteId);

  // Shared fresh/resume reconciliation. Running here (after every section-
  // replacing path) prevents a valid checkpoint from bypassing the current
  // LandGate review state or a later graft from dropping its sidecar.
  const { reconcileReviewedLandGateEvidence } = await import('../lib/siteLayers/landGatePromotion');
  const reviewedLandGateReconciliation = reconcileReviewedLandGateEvidence(
    sed,
    canonicalSiteLayers,
  );
  if (reviewedLandGateReconciliation.validationIssues.length > 0) {
    console.warn(
      '[SED-BOOTSTRAP] invalid reviewed LandGate records were excluded:',
      reviewedLandGateReconciliation.validationIssues,
    );
  }

  const { finalizeSedEvidenceMetadata } = await import('./siteEvidence');
  if (fastData) {
    finalizeSedEvidenceMetadata(sed, fastData.providerStatuses);
  } else {
    finalizeSedEvidenceMetadata(sed);
  }

  // ── Persist ── Anchor the SED version to the site's persisted floor BEFORE
  // saving so the JSON `.version` (read by the coverage-card badge) bumps
  // monotonically on a re-bootstrap of an existing site (a brand-new site has
  // no `sedVersion`, so this maps to v1 — unchanged from before).
  sed.version = nextSedVersion(site);
  const baselinePointer = sedPointerOf(site);
  const save = await saveSedToGcs(user.uid, siteId, sed, undefined, site?.sedStoragePath, {
    ifGenerationMatch:
      (site as { sedStorageGeneration?: string } | undefined)?.sedStorageGeneration ?? undefined,
  });
  if (!save.ok) {
    const reason = formatGcsSaveFailureReason(save);
    await store.updateSite(siteId, {
      sedStatus: 'failed',
      sedFailureReason: reason,
    } as Partial<typeof site>);
    return null;
  }
  logProgress(siteId, 'gcs-saved', 90);

  if (
    typeof save.sedVersion !== 'number' ||
    !Number.isFinite(save.sedVersion) ||
    typeof save.sedContentHash !== 'string' ||
    !save.sedContentHash.trim()
  ) {
    const reason = 'Malformed or incomplete staging receipt from /api/storage/save.';
    await store.updateSite(siteId, {
      sedStatus: 'failed',
      sedFailureReason: reason,
    } as Partial<typeof site>);
    return null;
  }
  const stageSync = await syncSedWithServerAuthority(
    sed,
    save.stagedPath ?? save.path,
    save.sedVersion,
    save.sedContentHash,
  );
  if (!stageSync.ok) {
    await store.updateSite(siteId, {
      sedStatus: 'failed',
      sedFailureReason: stageSync.reason,
    } as Partial<typeof site>);
    return null;
  }
  const completeness = fastSectionCompleteness(sed);
  const nowIso = new Date().toISOString();
  const effectiveBootVersion = save.sedVersion;
  const effectiveBootHash = save.sedContentHash;
  const effectiveBootSections = save.sectionHashes ?? computeSedSectionHashMirrors(sed);
  const pointerPatch = {
    sedId: sed.id,
    sedVersion: effectiveBootVersion,
    sedContentHash: effectiveBootHash,
    ...effectiveBootSections,
    sedContentHashVersion: effectiveBootVersion,
    sedCollectedAt: sed.collectedAt,
    sedStoragePath: save.stagedPath ?? save.path,
    ...(save.generation ? { sedStorageGeneration: save.generation } : {}),
  };
  const statusPatch = {
    sedCompleteness: completeness,
    sedStatus: 'partial' as const,
    // SED_Stale_Data_Rebuild SD1 — stamp the build time so staleness
    // detection (sedStaleness.isStaleSed) is data-driven rather than
    // "every site created before date X".
    sedComputedAt: nowIso,
    // R-32 — bootstrap completion stamp for wizard freshness gate.
    lastBootstrapAt: nowIso,
  };
  const committed = await store.commitSedPointerIfUnchanged(
    siteId,
    baselinePointer,
    pointerPatch,
    {
      statusPatch,
      candidateOperationId: save.operationId,
      operationId: save.operationId ? `pub-${save.operationId}` : undefined,
    },
  );
  if (!committed.committed) {
    console.error(
      `[SED-BOOTSTRAP] bootstrap: staged candidate at ${save.path} was not committed (${committed.reason}); site ${siteId} unchanged`,
    );
    return null;
  }
  const commitSync = await syncSedWithServerAuthority(
    sed,
    typeof committed.sedStoragePath === 'string' && committed.sedStoragePath.trim()
      ? committed.sedStoragePath.trim()
      : (save.stagedPath ?? save.path),
    typeof committed.sedVersion === 'number' ? committed.sedVersion : effectiveBootVersion,
    typeof committed.sedContentHash === 'string' ? committed.sedContentHash : effectiveBootHash,
  );
  if (!commitSync.ok) {
    try {
      const { invalidateCachedSed } = await import('./sedLoader');
      invalidateCachedSed(sed.id);
    } catch { /* ignore */ }
    console.error(
      `[SED-BOOTSTRAP] bootstrap: publication committed for ${siteId}, but post-commit reload failed: ${commitSync.reason}`,
    );
    return null;
  }
  try {
    const { invalidateCachedSed } = await import('./sedLoader');
    invalidateCachedSed(sed.id);
  } catch (e) {
    console.warn('[SED-BOOTSTRAP] SED cache invalidation failed:', (e as Error)?.message);
  }
  try {
    const { acknowledgeReviewedLandGateEvidence } = await import('./siteLayers/siteLayerPersistence');
    await acknowledgeReviewedLandGateEvidence({
      db,
      uid: siteLayerOwnerUid,
      siteId,
      consumedInputSignature: reviewedLandGateReconciliation.inputSignature,
      acknowledgedSections: reviewedLandGateReconciliation.acknowledgedSections,
    });
  } catch (error) {
    // The SED itself is valid. A failed/mismatched acknowledgement deliberately
    // leaves the review dirty so a later rebuild retries it.
    console.warn('[SED-BOOTSTRAP] LandGate dirty acknowledgement deferred:', (error as Error)?.message);
  }
  logProgress(siteId, 'done', 100);
  // R-98: successful completion clears any stale checkpoint.
  await clearSedCheckpoint(user.uid, siteId);
  const notifyAuthMissingProviders = await loadAuthMissingProviderNotifier();
  if (fastData) {
    notifyAuthMissingProviders(fastData.providerStatuses, siteId);
  }
  return { sedId: sed.id, completeness };
}

/* ── topUpSiteSed ───────────────────────────────────────────────────── */

/**
 * #18 SELF-HEAL detector — true when a stored SED's climate section carries
 * a NON-PHYSICAL precipitation figure (the fingerprint of the pre-2026-06-11
 * multi-year sum-not-mean aggregation bug, e.g. NE Louisiana's 13,743 mm/yr
 * / ~1,145-1,606 mm-per-month 10-year raw sum).
 *
 * The top-up merge does `merged = { ...existing }`, carrying `existing.climate`
 * forward verbatim, and the default/report top-up paths never re-fetch a
 * VERIFIED climate slot (open-meteo is a FAST provider, and verified slots are
 * skipped). So without this detector a stale inflated series would persist
 * across every rebuild — the user keeps seeing the NaN'd ERROR. When this
 * returns true the caller forces a fresh open-meteo fetch (the FIXED provider
 * path), replacing the stale series with the correct ~1,374 mm/yr normal.
 *
 * Detected as non-physical when EITHER:
 *   - the stored `annualPrecipitation` is non-finite (already NaN'd by a prior
 *     compile) or above the annual physical ceiling, OR
 *   - any stored `monthlySummary[].precipitation` exceeds the monthly ceiling.
 *
 * A verified slot is required — an `unavailable` climate slot is already
 * scheduled for (re)fetch by the normal gap logic and carries no stale value.
 */
export function storedClimatePrecipIsNonPhysical(
  sed: Pick<SiteEvidenceDocument, 'climate'> | null | undefined,
): boolean {
  const slot = sed?.climate;
  if (!slot || slot.status !== 'verified' || !slot.data) return false;
  const data = slot.data;
  if (!isPhysicalAnnualPrecip(data.annualPrecipitation)) return true;
  const months = data.monthlySummary ?? [];
  return months.some(m => !isPhysicalMonthlyPrecip(m.precipitation ?? 0));
}

interface TopUpOptions {
  /**
   * Cancels provider/model work and prevents persistence from starting. Once
   * the upload has started the run finishes its pointer commit regardless, so
   * an abort can never leave new bytes behind an un-advanced site pointer.
   */
  abortSignal?: AbortSignal;
  /** Optional list of section keys to upgrade. Default: all DEEP sections. */
  requiredSections?: string[];
  /** Optional report type — convenience: pulls requiredSedSections from the registry. */
  reportType?: ReportTypeId;
  /**
   * IDs of `site.documents` to link onto the SED's per-section
   * `linkedDocumentIds`. Post-bootstrap notification flow: when a user
   * uploads a new doc that strengthens an existing section, the
   * "Refresh evidence" CTA threads its id here so report-time prompts
   * see it linked even though the section's underlying provider data
   * may not have changed (Documents_On_Site Prompt 5).
   */
  includeDocIds?: string[];
  /**
   * When set, restrict the top-up to ONLY these provider ids — skip
   * everything else even if it would otherwise be in scope. Used by
   * the section-scoped Refresh evidence flow on SiteLandingPage so
   * the user can re-fetch e.g. just `airnow-aqi` without paying for
   * a full deep-provider sweep. The merge is additive: untouched
   * sections retain their status, confidence, retrievedAt, and any
   * doc-aware boosts the doc layer applied earlier.
   */
  onlyProviders?: string[];
  /**
   * Skip a section's provider call when the existing slot was
   * retrieved within this many ms of `now`. Prevents back-to-back
   * topUp calls (eg. user clicks Refresh, then immediately
   * generates a report → generateTypedReport's own topUp would
   * otherwise re-fetch the just-refreshed section). Default 60s
   * when undefined; set to 0 to disable. Only applies to sections
   * whose existing slot is verified — unavailable / missing slots
   * always re-fetch.
   */
  skipIfFreshWithinMs?: number;
}

/**
 * Run the deep providers and merge their sections into the existing SED
 * for sites whose required sections are still 'unavailable'. Bumps
 * sedVersion and returns the list of section keys that were upgraded.
 *
 * Returns null if there's no site SED to top up, or {} (empty upgraded
 * list) if every required section is already verified — caller should
 * treat both as no-op.
 *
 * Stale-pointer guard: before any load, provider call or save the run reads
 * the site's LIVE SED pointer from the server and refuses when the tab's
 * store copy disagrees or a server build owns the evidence; it re-checks the
 * head after the provider fetch and before every save attempt, saves with
 * the GCS generation it loaded, and advances the site pointer only through a
 * compare-and-set against the head it started from. A refusal returns
 * `outcome: 'superseded'` and changes nothing on the site.
 */
export async function topUpSiteSed(
  siteId: string,
  options: TopUpOptions = {},
): Promise<TopUpResult | null> {
  try {
    return await runTopUp(siteId, options);
  } catch (error) {
    if (error instanceof SedTopUpSuperseded) {
      console.warn(`[SED-BOOTSTRAP] topUp superseded for site ${siteId}: ${error.reason}`);
      return { upgraded: [], sedVersion: error.sedVersion, outcome: 'superseded', supersededReason: error.reason };
    }
    throw error;
  }
}

async function runTopUp(
  siteId: string,
  options: TopUpOptions,
): Promise<TopUpResult | null> {
  throwIfAborted(options.abortSignal);
  const useStore = await loadAppStore();
  throwIfAborted(options.abortSignal);
  const store = useStore.getState();
  const sourceSite = store.siteRag.sites.find(s => s.id === siteId);
  const site = sourceSite ? { ...sourceSite } : undefined;
  if (!site || !site.sedId || !site.sedStoragePath) {
    console.warn(`[SED-BOOTSTRAP] topUp: site ${siteId} has no SED to upgrade`);
    return null;
  }
  const { auth, db } = await import('../firebase');
  const user = auth.currentUser;
  if (!user) {
    console.warn('[SED-BOOTSTRAP] topUp: not signed in');
    return null;
  }
  const { siteOwnerUid } = await import('../lib/siteOwnerPath');
  const siteLayerOwnerUid = siteOwnerUid(site, user.uid);

  // ── Live head (stale-pointer guard) ──
  // The tab's store copy can lag the site doc by days (a company-shared site
  // has no live listener). Everything below loads, saves and advances what the
  // pointer names, so take it from the server and refuse on any disagreement
  // before a single provider or storage call. Headless never reaches here: it
  // has no signed-in user and returned above.
  await store.assertSiteWritable(siteId);
  const head = await store.readLiveSedHead(siteId).catch((error: unknown) => {
    console.warn('[SED-BOOTSTRAP] topUp: live SED head read failed:', (error as Error)?.message);
    return null;
  });
  if (!head) throw new SedTopUpSuperseded('pointer-unverified', site.sedVersion ?? 0);
  if (!sameSedPointer(sedPointerOf(site), head.pointer)) {
    throw new SedTopUpSuperseded('stale-store-pointer', head.pointer.sedVersion ?? site.sedVersion ?? 0);
  }
  if (head.buildInProgress) throw new SedTopUpSuperseded('build-in-progress', head.pointer.sedVersion ?? 0);
  const start = head.pointer;
  const startPath = site.sedStoragePath;
  const startVersion = start.sedVersion ?? 0;
  /** Re-read the head; throw unless it is still the one this run started from. */
  const recheckLiveHead = async (): Promise<void> => {
    await store.assertSiteWritable(siteId);
    const live = await store.readLiveSedHead(siteId).catch(() => null);
    if (!live) throw new SedTopUpSuperseded('pointer-unverified', startVersion);
    if (!sameSedPointer(live.pointer, start)) throw new SedTopUpSuperseded('pointer-moved', startVersion);
    if (live.buildInProgress) throw new SedTopUpSuperseded('build-in-progress', startVersion);
  };

  const { listSiteLayers, acknowledgeReviewedLandGateEvidence } =
    await import('./siteLayers/siteLayerPersistence');
  let canonicalSiteLayers: import('../types/site').SiteLayer[];
  try {
    const canonical = await listSiteLayers(db, siteLayerOwnerUid, siteId);
    const { resolveCanonicalSiteLayers } = await import('../lib/siteLayers/canonicalLayerResolution');
    const resolved = resolveCanonicalSiteLayers(
      canonical,
      site.siteLayers,
      site.siteLayersCanonicalVersion,
    );
    if (resolved.requiresMigration) {
      console.warn('[SED-BOOTSTRAP] topUp: legacy non-LandGate layer fallback active; canonical migration required');
    }
    canonicalSiteLayers = resolved.layers;
    Object.assign(site, applySiteStudyArea({ ...site, siteLayers: canonicalSiteLayers }));
  } catch (error) {
    console.warn('[SED-BOOTSTRAP] topUp: canonical layer read failed:', (error as Error)?.message);
    return null;
  }

  // Resolve the section list to consider.
  let candidateSections: string[];
  let reportRequiredSections: ReadonlySet<string> | null = null;
  if (options.onlyProviders && options.onlyProviders.length > 0) {
    // Section-scoped refresh — derive sections from the provider list
    // so a single source of truth (SECTION_TO_PROVIDER_ID) drives both
    // the in-scope section walk AND the providersToRun list below.
    const wantedProviders = new Set(options.onlyProviders);
    candidateSections = Object.entries(SECTION_TO_PROVIDER_ID)
      .filter(([, pid]) => wantedProviders.has(pid))
      .map(([sec]) => sec);
  } else if (options.requiredSections && options.requiredSections.length > 0) {
    candidateSections = options.requiredSections;
  } else if (options.reportType) {
    const { REPORT_TYPE_REGISTRY } = await import('../config/reportTypes');
    const required = REPORT_TYPE_REGISTRY[options.reportType]?.requiredSedSections ?? [];
    candidateSections = required;
    reportRequiredSections = new Set(required);
  } else {
    // Default: every section a deep provider can fill.
    candidateSections = Object.entries(SECTION_TO_PROVIDER_ID)
      .filter(([, pid]) => (DEEP_PROVIDER_IDS as readonly string[]).includes(pid))
      .map(([sec]) => sec);
  }

  // ── Load existing SED ── (the object the live head names, with the GCS
  // generation that was read, so the save can require it is unchanged)
  const loaded = await loadSedFromGcsWithGeneration(startPath);
  const existing = loaded?.sed ?? null;
  if (!loaded || !existing) {
    console.warn(`[SED-BOOTSTRAP] topUp: could not load SED for site ${siteId}`);
    return null;
  }
  // Refuse to build on bytes the pointer does not name. Equal versions are
  // required: a top-up must not "heal" a pointer/body split by bumping past
  // it — that would certify bytes nobody checked. Rebuild repairs a split.
  if (existing.id !== start.sedId || (start.sedVersion !== null && existing.version !== start.sedVersion)
    || (start.sedContentHash !== null && computeSedContentHashV2(existing) !== start.sedContentHash)) {
    throw new SedTopUpSuperseded('body-mismatch', startVersion);
  }
  assertPlanningSedAssessmentIdentity(site.generationAssessmentSelection, existing);
  const activeIntent = deriveIntentForSelectedAssessment(site.projectIntent, site.generationAssessmentSelection);
  if ((existing.studyAreaScope?.selectionHash ?? null) !== (site.studyAreaScope?.selectionHash ?? null)) {
    throw new Error('Study area changed. Rebuild site evidence before a document or provider top-up.');
  }

  /**
   * Save `body` over the object the live head names and advance the pointer
   * through the compare-and-set. The last abort point is the first line:
   * once the upload starts, the run always finishes the pointer commit so a
   * cancelled run can never leave new bytes behind an old pointer.
   * Throws SedTopUpSuperseded when the save or the commit is refused.
   */
  const commitTopUpSave = async (
    uid: string,
    body: SiteEvidenceDocument,
    statusPatch?: Partial<typeof site>,
  ): Promise<
    | { ok: true }
    | {
        ok: false;
        reason: string;
        publicationCommitted?: boolean;
        committedSedVersion?: number;
        committedStoragePath?: string;
      }
  > => {
    const priorLocalVersion = startVersion;
    throwIfAborted(options.abortSignal);
    // Only the plain nested object is rewritten in place. A Document Retry
    // revision or a legacy flat pointer would be saved somewhere the pointer
    // does not name — refuse, and let a rebuild re-home the evidence.
    if (
      resolveSedSavePath(uid, siteId, body.id, startPath) !== startPath &&
      !isPublishedStagedSedRevisionPath(startPath, siteId, body.id)
    ) {
      throw new SedTopUpSuperseded('save-path-mismatch', startVersion);
    }
    const save = await saveSedToGcs(uid, siteId, body, undefined, startPath, {
      beforeAttempt: recheckLiveHead,
      ifGenerationMatch: loaded.generation ?? undefined,
    });
    if (save.status === 412) throw new SedTopUpSuperseded('bytes-changed', startVersion);
    if (save.status === 409 && save.code === 'sed-not-current') throw new SedTopUpSuperseded('pointer-moved', startVersion);
    if (save.status === 409 && save.code === 'sed-body-id-mismatch') throw new SedTopUpSuperseded('body-mismatch', startVersion);
    if (save.status === 409 && save.code === 'sed-build-in-progress') throw new SedTopUpSuperseded('build-in-progress', startVersion);
    if (!save.ok) {
      // Surface the persistence failure instead of returning null silently —
      // but never over a build that now owns the site's status.
      const reason = formatGcsSaveFailureReason(save);
      await store.commitSedPointerIfUnchanged(siteId, start, {}, {
        statusPatch: { sedStatus: 'failed', sedFailureReason: reason } as Partial<typeof site>,
        requireIdle: true,
      }).catch((error: unknown) => {
        console.warn('[SED-BOOTSTRAP] topUp: failure stamp skipped:', (error as Error)?.message);
      });
      return { ok: false, reason };
    }
    if (
      typeof save.sedVersion !== 'number' ||
      !Number.isFinite(save.sedVersion) ||
      typeof save.sedContentHash !== 'string' ||
      !save.sedContentHash.trim()
    ) {
      const reason = 'Malformed or incomplete staging receipt from /api/storage/save.';
      return { ok: false, reason, publicationCommitted: false };
    }
    const effectiveTopUpVersion = save.sedVersion;
    const effectiveTopUpHash = save.sedContentHash;
    const effectiveTopUpSections = save.sectionHashes ?? computeSedSectionHashMirrors(body);
    const committed = await store.commitSedPointerIfUnchanged(siteId, start, {
      sedId: body.id,
      sedVersion: effectiveTopUpVersion,
      sedStoragePath: save.stagedPath ?? save.path,
      ...(save.generation ? { sedStorageGeneration: save.generation } : {}),
      sedContentHash: effectiveTopUpHash,
      ...effectiveTopUpSections,
      sedContentHashVersion: effectiveTopUpVersion,
      sedCollectedAt: body.collectedAt,
    }, {
      ...(statusPatch ? { statusPatch } : {}),
      candidateOperationId: save.operationId,
      operationId: save.operationId ? `pub-${save.operationId}` : undefined,
    });
    if (!committed.committed) {
      console.error(
        `[SED-BOOTSTRAP] topUp: bytes saved to ${save.path} but the site pointer moved (${committed.reason}); `
        + `site ${siteId} still names the evidence it named before`,
      );
      throw new SedTopUpSuperseded(committed.reason === 'build-in-progress' ? 'build-in-progress' : 'pointer-moved', startVersion);
    }
    const committedPath =
      typeof committed.sedStoragePath === 'string' && committed.sedStoragePath.trim()
        ? committed.sedStoragePath.trim()
        : (save.stagedPath ?? save.path);
    const committedVersion =
      typeof committed.sedVersion === 'number' ? committed.sedVersion : effectiveTopUpVersion;
    const committedHash =
      typeof committed.sedContentHash === 'string' && committed.sedContentHash.trim()
        ? committed.sedContentHash.trim()
        : effectiveTopUpHash;
    const syncResult = await syncSedWithServerAuthority(
      body,
      committedPath,
      committedVersion,
      committedHash,
    );
    try {
      const { invalidateCachedSed } = await import('./sedLoader');
      invalidateCachedSed(body.id);
    } catch (e) {
      console.warn('[SED-BOOTSTRAP] SED cache invalidation failed:', (e as Error)?.message);
    }
    if (!syncResult.ok) {
      body.version = priorLocalVersion;
      return {
        ok: false,
        reason: syncResult.reason,
        publicationCommitted: true,
        committedSedVersion: committedVersion,
        committedStoragePath: committedPath,
      };
    }
    return { ok: true };
  };

  // Reconcile review state before any provider-work short circuit. A reviewer
  // promotion or unpromotion can be the only outstanding SED change, so it
  // must be allowed to reach the common persistence path even when every
  // provider-backed section is already fresh.
  const { reconcileReviewedLandGateEvidence } = await import('../lib/siteLayers/landGatePromotion');
  let reviewedLandGateReconciliation = reconcileReviewedLandGateEvidence(
    existing,
    canonicalSiteLayers,
  );
  const initiallyAppliedReviewedSections = reviewedLandGateReconciliation.changedSections;
  const acknowledgeCurrentReviewedState = async (): Promise<void> => {
    try {
      await acknowledgeReviewedLandGateEvidence({
        db,
        uid: siteLayerOwnerUid,
        siteId,
        consumedInputSignature: reviewedLandGateReconciliation.inputSignature,
        acknowledgedSections: reviewedLandGateReconciliation.acknowledgedSections,
      });
    } catch (error) {
      console.warn('[SED-BOOTSTRAP] topUp: LandGate dirty acknowledgement deferred:', (error as Error)?.message);
    }
  };

  // #18 SELF-HEAL — when the stored SED's climate carries a non-physical
  // precipitation total (the pre-fix multi-year sum-not-mean fingerprint, e.g.
  // NE Louisiana's 13,743 mm/yr), force a fresh open-meteo fetch this rebuild.
  // Otherwise the merge (`merged = { ...existing }`) carries the stale series
  // forward verbatim and the verified-slot skip below would never re-fetch it,
  // so the user keeps seeing the NaN'd ERROR. Re-fetching open-meteo (the FIXED
  // provider path) replaces the inflated series with the correct ~1,374 mm/yr
  // normal — recovered, never invented (North Star).
  const climateNeedsRefetch = storedClimatePrecipIsNonPhysical(existing);
  if (climateNeedsRefetch) {
    console.warn(
      `[SED-BOOTSTRAP] topUp: site ${siteId} has non-physical stored climate ` +
      `precipitation (annual=${existing.climate?.data?.annualPrecipitation ?? 'n/a'}mm) — ` +
      `forcing a fresh open-meteo fetch to self-heal.`,
    );
  }

  // Section selection differs by mode:
  //   - default top-up: only sections whose deep provider is missing or
  //     unavailable (the original behaviour the report-gen path relies
  //     on — don't pay to re-fetch already-verified data).
  //   - scoped refresh (onlyProviders set): the user explicitly asked
  //     for these sections; re-fetch even when verified, but honour
  //     skipIfFreshWithinMs so a refresh-then-generate doesn't
  //     double-bump.
  const skipMs = options.skipIfFreshWithinMs ?? 60_000;
  const nowMs = Date.now();
  const isScoped = !!(options.onlyProviders && options.onlyProviders.length > 0);
  const pureInputRefresh = isScoped && options.onlyProviders!.every(
    provider => provider === 'project-intent' || provider === 'financial-assumptions',
  );
  const hasSiteContextProp = Object.prototype.hasOwnProperty.call(site, 'siteContext');
  const hasFinancialAssumptionsProp = Object.prototype.hasOwnProperty.call(site, 'financialAssumptions');
  const existingHasSiteContext = Object.prototype.hasOwnProperty.call(existing, 'siteContext') && existing.siteContext !== undefined;
  const nextHasSiteContext = hasSiteContextProp && site.siteContext !== undefined;
  const siteContextNeedsSync = hasSiteContextProp && (
    existingHasSiteContext !== nextHasSiteContext
    || JSON.stringify(existing.siteContext ?? null) !== JSON.stringify(site.siteContext ?? null)
  );
  const intentNeedsSync = JSON.stringify(existing.fullProjectIntent ?? null) !== JSON.stringify(activeIntent ?? null);
  // Pure producers are valid scoped work even when there is no live provider.
  // Also run intent stampers on an unscoped top-up when projectIntent changed (e.g. screeningMode toggled).
  const runIntentStampers = Boolean(
    (isScoped && options.onlyProviders?.includes('project-intent'))
    || (!isScoped && intentNeedsSync),
  );
  const runFinancialStampers = isScoped && options.onlyProviders?.includes('financial-assumptions');
  const sectionsNeedingUpgrade = candidateSections.filter(sec => {
    const providerId = SECTION_TO_PROVIDER_ID[sec];
    if (!providerId) return false;
    // LLW-2 — `external-literature` is NOT a `fetchAllSiteData`-driven
    // provider; it runs through the orchestrator below. Skip the
    // standard sweep so providersToRun never carries `'external-literature'`.
    if (providerId === 'external-literature') return false;
    if (isScoped) {
      // Scoped refresh stays within onlyProviders.
      if (!options.onlyProviders!.includes(providerId)) return false;
    } else {
      const isDeep = (DEEP_PROVIDER_IDS as readonly string[]).includes(providerId);
      const reportRequiresSection = reportRequiredSections?.has(sec) === true;
      // Default top-up only runs DEEP providers; report-gen passes
      // `reportType` so FAST gaps (e.g. waterStress) still get a fetch.
      if (!isDeep && !reportRequiresSection) return false;
    }
    const slot = existing[sec as keyof SiteEvidenceDocument] as EvidenceSection<unknown> | undefined;
    if (!slot || slot.status === 'unavailable') return true;
    // Verified slot — for the default path this means "already
    // covered, skip"; for scoped refresh this means "re-fetch unless
    // we just refreshed it".
    if (!isScoped) return false;
    if (skipMs > 0 && slot.retrievedAt) {
      const ageMs = nowMs - Date.parse(slot.retrievedAt);
      if (Number.isFinite(ageMs) && ageMs >= 0 && ageMs < skipMs) return false;
    }
    return true;
  });

  // #18 SELF-HEAL — splice `climate` into the upgrade set when the stored slot
  // is non-physical, even though the verified-slot skip above (and the FAST-
  // provider gate, since open-meteo is not a DEEP provider) would otherwise
  // omit it. This routes open-meteo into `providersToRun` so the graft loop
  // replaces the stale climate section with a freshly-fetched physical one.
  if (climateNeedsRefetch && !sectionsNeedingUpgrade.includes('climate')) {
    sectionsNeedingUpgrade.push('climate');
  }

  // F5 — EPA designation refresh without a USFWS rebuild. The designation
  // rides the environmental section as a separate lookup, so it is refreshed
  // on its own when the stored block predates the shipped Green Book table (a
  // new NAYRO export shipped; any top-up mode, like the climate self-heal) or
  // when the caller asks for `epa-attainment` alone. Only the EPA lookup runs
  // and only `naaqsDesignations` is swapped into the stored section. When the
  // environmental section is already being refreshed through usfws-env, its
  // companion pairing re-runs EPA anyway. With no stored section (missing or
  // unavailable) there is nothing to swap into: the request is a no-op, as
  // before F5, and the default top-up still fetches USFWS for it rather than
  // finding an EPA-only screens slot in its place.
  const storedEpa = existing.environmental?.data?.naaqsDesignations;
  const hasStoredEnvironmental = !!existing.environmental?.data && existing.environmental.status !== 'unavailable';
  const epaVintageStale = epaDesignationPredates(storedEpa);
  const epaRequestedAlone = isScoped && options.onlyProviders!.includes('epa-attainment');
  const epaOnlyRefresh = hasStoredEnvironmental
    && (epaVintageStale || epaRequestedAlone)
    && !sectionsNeedingUpgrade.includes('environmental');
  if (epaOnlyRefresh) {
    console.info(
      `[SED-BOOTSTRAP] topUp epa-attainment: ${epaVintageStale
        ? `coverage ${storedEpa?.coverage ?? 'none'} -> ${EPA_TABLE_COVERAGE}`
        : 'requested alone'}; refreshing the designation without USFWS`,
    );
    sectionsNeedingUpgrade.push('environmental');
  }
  /** The provider a section's refresh runs: the EPA lookup alone for an EPA-only environmental refresh. */
  const providerForSection = (sec: string): string | undefined =>
    (sec === 'environmental' && epaOnlyRefresh ? 'epa-attainment' : SECTION_TO_PROVIDER_ID[sec]);

  // LLW-2 — External Literature refresh decision. Independent of the
  // standard provider sweep because `external-literature` is the
  // synthetic orchestrator, not a `fetchAllSiteData` provider.
  // Refresh-eligible when:
  //   - scoped refresh (`onlyProviders` includes `'external-literature'`), OR
  //   - default top-up AND the slot is missing/unavailable/older than
  //     the 14-day TTL.
  // When eligible we always go through `maybeFetchExternalLiterature`,
  // which itself re-checks TTL + handles the prior-result reuse on the
  // bootstrap-style path.
  const wantsExternalLiteratureRefresh = (() => {
    if (isScoped) {
      return options.onlyProviders!.includes('external-literature');
    }
    // Default top-up: refresh whenever a `requiredSections` /
    // `reportType` / default scan happened to include the section.
    // The TTL guard inside `maybeFetchExternalLiterature` keeps repeat
    // top-ups within the same 14-day window from re-firing the call.
    return candidateSections.includes('externalLiterature');
  })();

  // PSE-3 — same decision shape as LLW-2 but for the public-sentiment
  // synthetic orchestrator. Independent of the standard provider sweep;
  // gated on the shared sentiment-scan age window plus the Firestore
  // `pse-cache` collection (cache HIT short-circuits the call entirely).
  const wantsPublicSentimentRefresh = (() => {
    if (isScoped) {
      return options.onlyProviders!.includes('public-sentiment');
    }
    return candidateSections.includes('publicSentiment');
  })();

  // Documents_On_Site Prompt 5 — even when no provider section needs an
  // upgrade, the user may want to link a freshly-uploaded doc into an
  // already-verified section. Stamp linkedDocumentIds before deciding
  // whether to bail; if provider work is also empty, persist the link
  // and bump version so report-gen sees the new pointer.
  if (options.includeDocIds && options.includeDocIds.length > 0) {
    const { buildSectionLinks } = await import('./knowledge/sedDocSuggestion');
    const links = buildSectionLinks(site, options.includeDocIds);
    let stampedAny = false;
    for (const [secId, docIds] of Object.entries(links)) {
      const slot = (existing as unknown as Record<string, EvidenceSection<unknown> | undefined>)[secId];
      if (!slot || !docIds || docIds.length === 0) continue;
      const before = slot.linkedDocumentIds ?? [];
      const merged = Array.from(new Set([...before, ...docIds]));
      if (merged.length !== before.length) {
        slot.linkedDocumentIds = merged;
        stampedAny = true;
      }
    }
    if (
      stampedAny
      && sectionsNeedingUpgrade.length === 0
      && !runIntentStampers
      && !runFinancialStampers
      && !siteContextNeedsSync
      && initiallyAppliedReviewedSections.length === 0
    ) {
      // Persist the link-only update and bail.
      existing.version = nextSedVersion(site, existing.version);
      existing.collectedAt = new Date().toISOString();
      const { auth } = await import('../firebase');
      const u = auth.currentUser;
      let linkOnlyOutcome: TopUpResult['outcome'] = 'save-failed';
      let linkOnlySaveFailReason: string | undefined = 'Not signed in.';
      if (u) {
        // Guarded save + pointer compare-and-set; no status patch on this path.
        const committed = await commitTopUpSave(u.uid, existing);
        if (committed.ok) {
          linkOnlyOutcome = 'saved';
          linkOnlySaveFailReason = undefined;
          await acknowledgeCurrentReviewedState();
        } else if (committed.publicationCommitted) {
          linkOnlyOutcome = 'sync-unresolved';
          linkOnlySaveFailReason = committed.reason;
          console.error('[SED-BOOTSTRAP] topUp link-only: post-commit reload failed —', committed.reason);
        } else {
          linkOnlySaveFailReason = committed.reason;
          console.error('[SED-BOOTSTRAP] topUp link-only: GCS save failed —', committed.reason);
        }
      }
      logProgress(siteId, 'topup-link-only', 100);
      return {
        upgraded: [],
        sedVersion: existing.version,
        outcome: linkOnlyOutcome,
        ...(linkOnlySaveFailReason ? { saveFailReason: linkOnlySaveFailReason } : {}),
      };
    }
  }

  // DCO-3 — when DCO is live, the orchestrator at the tail of this
  // function may still have work to do (Tier 3 DAM fill of a slot the
  // providers don't cover, e.g. `regulatory.nuclearStance` from a
  // curated LA DAM). Resolve liveness BEFORE the no-provider short-
  // circuit so a pure DCO-only topUp doesn't bail at this gate.
  const dcoReportTypeForGate: ReportTypeId =
    options.reportType ?? site.latestReportType ?? 'feasibility';
  let dcoLiveForThisTopUp = false;
  try {
    const { isDcoLiveModeEnabled } = await import('./dataCompletionOrchestrator');
    dcoLiveForThisTopUp = !pureInputRefresh && isDcoLiveModeEnabled(dcoReportTypeForGate);
  } catch {
    // Module load failure here is non-fatal; treat as gate-off.
    dcoLiveForThisTopUp = false;
  }

  if (
    sectionsNeedingUpgrade.length === 0
    && !wantsExternalLiteratureRefresh
    && !wantsPublicSentimentRefresh
    && !dcoLiveForThisTopUp
    && !runIntentStampers
    && !runFinancialStampers
    && !siteContextNeedsSync
    && initiallyAppliedReviewedSections.length === 0
  ) {
    await acknowledgeCurrentReviewedState();
    logProgress(siteId, 'topup-noop', 100);
    return { upgraded: [], sedVersion: existing.version, outcome: 'no-change' };
  }

  const candidateProviders = Array.from(
    new Set(sectionsNeedingUpgrade.map(providerForSection)),
  ).filter((id): id is string => !!id);

  // Apply workload-profile gating: drop deep providers the site's
  // projectIntent doesn't recommend (e.g. NREL ATB on enterprise/colocation
  // workloads). The site doc carries projectIntent — when absent,
  // run every candidate provider (legacy sites stay unaffected).
  //
  // SCOPED REFRESH EXCEPTION: when the user explicitly asked for a
  // provider via onlyProviders, honour their pick over the workload-
  // profile heuristic — they're spending money on a deliberate
  // re-fetch.
  const { recommendProviders } = await import('./dataProviders');
  const recommended = (!isScoped && activeIntent)
    ? new Set(recommendProviders(activeIntent, existing.location.lat))
    : null;
  const intentFiltered = recommended
    ? candidateProviders.filter(id => recommended.has(id))
    : candidateProviders;

  // Report-gen safety net: required sections for this report type must
  // attempt a fetch even when workload gating omitted the provider.
  const reportRequiredProviderIds = reportRequiredSections
    ? Array.from(new Set(
        Array.from(reportRequiredSections)
          .map(sec => SECTION_TO_PROVIDER_ID[sec])
          .filter((id): id is string =>
            !!id
            && id !== 'external-literature'
            && id !== 'public-sentiment'
            && id !== 'kmz-derived'
            // v1.2.1 HR-5 — synthetic (non-fetchable) section producers.
            // `eia-natural-gas` is a REAL provider and is intentionally NOT
            // excluded here so the fuelSupply section can be re-fetched.
            && id !== 'project-intent'
            && id !== 'financial-assumptions'
            // SED D2 (H-6) — multiHazard (`fema-nri`) is stamped via
            // `stampMultiHazardAndResources`, not the `fetchAllSiteData`
            // sweep; transmissionHeadroom + waterResource (`honest-unavailable`)
            // have no real fetcher yet. None is a `fetchAllSiteData`-registered
            // provider, so exclude them here (mirrors kmz-derived / project-
            // intent) to keep them out of `providersToRun`.
            && id !== 'fema-nri'
            && id !== 'honest-unavailable'),
      ))
    : [];
  const requiredStillNeeded = reportRequiredProviderIds.filter(pid =>
    sectionsNeedingUpgrade.some(sec => providerForSection(sec) === pid),
  );
  const providersToRun = Array.from(new Set([
    ...intentFiltered,
    ...requiredStillNeeded,
    // #18 SELF-HEAL — `open-meteo` must run unconditionally when the stored
    // climate is non-physical, regardless of workload-profile gating or scoped
    // refresh. recommendProviders already always includes it, but this makes
    // the self-heal independent of that contract.
    ...(climateNeedsRefetch ? ['open-meteo'] : []),
    // F5 — the EPA-only refresh is a local table lookup; like the climate
    // self-heal it runs regardless of workload-profile gating.
    ...(epaOnlyRefresh ? ['epa-attainment'] : []),
  ]))
    .filter(pid => pid !== 'project-intent' && pid !== 'financial-assumptions');

  // The jurisdiction result is nested inside RegulatoryEvidence. Whenever a
  // scoped regulatory refresh runs the state-stance provider, refresh the
  // boundary companion too (when its rollout flag is enabled) so grafting the
  // section cannot silently discard or strand the city/ETJ screen.
  if (providersToRun.includes('regulatory-db')) {
    const { isJurisdictionScreeningEnabled } = await import('./dataProviders/jurisdiction');
    if (isJurisdictionScreeningEnabled() && !providersToRun.includes('jurisdiction-boundaries')) {
      providersToRun.push('jurisdiction-boundaries');
    }
  }

  if (
    providersToRun.length === 0
    && !wantsExternalLiteratureRefresh
    && !wantsPublicSentimentRefresh
    && !dcoLiveForThisTopUp
    && !runIntentStampers
    && !runFinancialStampers
    && !siteContextNeedsSync
    && initiallyAppliedReviewedSections.length === 0
  ) {
    await acknowledgeCurrentReviewedState();
    logProgress(siteId, 'topup-gated-out', 100);
    return { upgraded: [], sedVersion: existing.version, outcome: 'no-change' };
  }

  // Standard provider sweep — runs only when we actually have providers
  // to call. When the top-up was scoped purely to `external-literature`,
  // providersToRun is empty here and we skip straight to the orchestrator
  // block below.
  let merged: SiteEvidenceDocument = { ...existing };
  if (hasSiteContextProp) {
    if (site.siteContext === undefined) {
      delete merged.siteContext;
    } else {
      merged.siteContext = site.siteContext;
    }
  }
  if (hasFinancialAssumptionsProp) {
    if (site.financialAssumptions === undefined) {
      delete merged.financialAssumptions;
    } else {
      merged.financialAssumptions = site.financialAssumptions;
    }
  }
  if (!isScoped && intentNeedsSync) {
    if (activeIntent === undefined) {
      delete merged.fullProjectIntent;
    } else {
      merged.fullProjectIntent = activeIntent;
    }
  }
  const upgraded: string[] = [...initiallyAppliedReviewedSections];
  if (!isScoped && intentNeedsSync && !upgraded.includes('projectIntent')) {
    upgraded.push('projectIntent');
  }
  if (siteContextNeedsSync && !upgraded.includes('siteContext')) {
    upgraded.push('siteContext');
  }
  // Amendments the refreshed evidence overtook (it moved to a value that is
  // neither the amendment's baseline nor the operator's correction). Surfaced
  // on the result so the caller can persist them as evidence conflicts instead
  // of the decision vanishing between two silent overwrites.
  let graftAmendmentConflicts: InBandAmendmentConflict[] = [];
  if (providersToRun.length > 0) {
    logProgress(siteId, `topup-run:${providersToRun.join(',')}`, 20);

    const { fetchAllSiteData } = await import('./dataProviders');
    const deepData = await fetchAllSiteData(
      {
        lat: existing.location.lat,
        lng: existing.location.lng,
        // The SED's `location` is the geocoded address; the site doc carries the
        // drawn-boundary centroid/polygon — pass them so the deep providers
        // re-query at the parcel centre too (defaults to address when absent).
        studyAreaScope: site.studyAreaScope,
        centroid: site.siteCentroid,
        boundary: site.siteBoundary,
        boundaries: site.siteBoundaries?.map(parcel => ({
          id: parcel.id,
          polygon: parcel.polygon,
          ...(parcel.holes?.length ? { holes: parcel.holes } : {}),
        })),
      },
      store.userApiKeys,
      providersToRun,
      options.reportType,
      options.abortSignal,
    );
    throwIfAborted(options.abortSignal);
    logProgress(siteId, 'topup-fetched', 70);
    // The provider sweep is the long wait: confirm the head this run started
    // from is still live before compiling, document passes or DCO work.
    await recheckLiveHead();

    // Build a deep-only SED through the same compiler so the EvidenceSection
    // shape is identical to a fresh build, then graft each upgraded section
    // onto the existing SED.
    const { compileSiteEvidence } = await import('./siteEvidence');
    const deepSed = compileSiteEvidence(
      existing.siteName,
      existing.location,
      deepData,
      {
        /* siteProfile already on existing — no need to recompute */
        // CA6 — the deep providers re-queried at the centroid too (CA3);
        // pass the boundary so the grafted sections carry `queryPoint`.
        siteBoundary: site.siteBoundary,
        siteCentroid: site.siteCentroid,
        // DUH — keep the doc projection consistent across base + top-up
        // compiles. topUpSiteSed only grafts the upgraded sections onto
        // the existing SED, but if a future caller ever reads deepSed
        // wholesale we don't want it carrying an empty doc array.
        uploadedDocuments: projectDocsForSed(site),
        // SOC — same rationale as the base compile in bootstrapSiteSed:
        // keep all three SOC channels consistent. topUpSiteSed only grafts
        // upgraded provider sections onto the existing SED today, but if
        // a future caller reads `deepSed` wholesale (or if we ever change
        // top-up to graft these fields too) they need to be present.
        fullProjectIntent: activeIntent,
        promotedGenerationScenarioSnapshot: site.generationAssessmentSelection?.kind === 'grid-led-standby-planning' ? undefined : site.promotedGenerationScenarioSnapshot,
        siteContext: site.siteContext,
        financialAssumptions: site.financialAssumptions,
      },
    );

    for (const sec of sectionsNeedingUpgrade) {
      let newSlot = deepSed[sec as keyof SiteEvidenceDocument] as EvidenceSection<unknown> | undefined;
      // F5 — an EPA-only refresh swaps the designation into the stored section
      // and keeps everything else; it never grafts the screens-only slot an
      // EPA-only compile produces. A failed lookup returns the stored section
      // itself: nothing is written (no hashed anomaly, no version bump, no
      // save), so neither the environmental hash nor the Stage 2 basis moves
      // without new evidence, and the still-stale block retries next top-up.
      if (sec === 'environmental' && epaOnlyRefresh) {
        const stored = existing.environmental as EvidenceSection<unknown>;
        const refreshed = newSlot ? withRefreshedNaaqsDesignations(stored, newSlot) : stored;
        if (refreshed === stored) {
          const epaStatus = deepData.providerStatuses?.find(status => status.id === 'epa-attainment');
          console.warn(
            `[SED-BOOTSTRAP] topUp epa-attainment: no designation returned (${epaStatus?.error ?? epaStatus?.status ?? 'provider not run'}); `
            + 'the stored environmental section is kept unchanged and the next top-up retries',
          );
        } else if ((refreshed.status as EvidenceStatus) !== 'unavailable') {
          (merged as unknown as Record<string, unknown>).environmental = refreshed;
          upgraded.push(sec);
        }
        continue;
      }
      // A deployment may disable jurisdiction refresh after an earlier SED
      // captured it. A regulatory-db-only refresh must not silently erase that
      // frozen screen. Explicit provider failures compile an `unknown` result
      // and therefore do replace the old value; this fallback applies only
      // when the companion was not attempted at all.
      if (sec === 'regulatory' && newSlot?.data && existing.regulatory?.data?.jurisdiction) {
        const regulatoryData = newSlot.data as import('../types/siteEvidence').RegulatoryEvidence;
        if (!regulatoryData.jurisdiction) {
          newSlot = {
            ...newSlot,
            data: {
              ...regulatoryData,
              jurisdiction: existing.regulatory.data.jurisdiction,
            },
          };
        }
      }
      // Same rule for the separate lookups riding the environmental section
      // (EPA designation, PAD-US screen): a USFWS refresh that did not attempt
      // one keeps its prior value; a failed attempt is recorded as not assessed.
      if (sec === 'environmental' && newSlot) {
        for (const path of ['naaqsDesignations', 'protectedAreas']) {
          newSlot = carryForwardContributedField(newSlot, existing.environmental as EvidenceSection<unknown> | undefined, path);
        }
        // A refresh whose USFWS lookup failed compiles a screens-only
        // 'anomalous' slot; it must never erase verified habitat evidence.
        const kept = keepPriorHabitatEvidence(newSlot, existing.environmental as EvidenceSection<unknown> | undefined);
        if (kept !== newSlot) {
          console.info('[SED-BOOTSTRAP] topUp environmental: USFWS evidence unavailable on this refresh; prior habitat/wetlands evidence kept, fresh designation screens carried in');
          newSlot = kept;
        }
      }
      if (newSlot && (newSlot.status as EvidenceStatus) !== 'unavailable') {
        // A provider refresh must not destroy user evidence living inside the
        // section it replaces. `graftProviderSection` carries forward the
        // authored-only fields the compile cannot produce (grid
        // `capacityDetail` / `interconnectionClass` / `utilityName`) and
        // re-applies this section's amendments over the refreshed provider
        // values. Without it, a scoped hifld-grid refresh reverted the
        // operator's amended substation name AND dropped the utility letter's
        // stated MW — which demoted the report's headroom fact from the
        // tier-1 letter to an unverified site-context claim.
        const graft = graftProviderSection({
          sectionId: sec,
          existingSlot: existing[sec as keyof SiteEvidenceDocument] as EvidenceSection<unknown> | undefined,
          providerSlot: newSlot,
        });
        if (graft.preservedFields.length > 0 || graft.preservedLinkFields.length > 0) {
          console.info(
            `[SED-BOOTSTRAP] topUp graft ${sec}: preserved=[${graft.preservedFields.join(',')}] `
            + `links=[${graft.preservedLinkFields.join(',')}]`,
          );
        }
        (merged as unknown as Record<string, unknown>)[sec] = graft.slot;
        upgraded.push(sec);
      }
    }

    // F5 — the EPA cross-checks (`epa-attainment-fips` /
    // `epa-nonattainment-fips`) read the designation just refreshed.
    if (epaOnlyRefresh && upgraded.includes('environmental')) {
      const { rerunCrossValidationAndMetadata } = await import('./siteEvidence');
      rerunCrossValidationAndMetadata(merged, deepData.providerStatuses ?? []);
    }

      // KSD2 / KSI — re-bind the operator's KMZ ground truth.
    //
    // `crossBindGridFromKmz` / `crossBindAllKmzCorridors` write
    // grid.substationDistKm and grid.nearestTransmissionKm from the operator's
    // own map pins, paired with a `kmz-import` provenance stamp. They run in
    // `doBootstrap` only — so a scoped provider refresh replaced those values
    // with the HIFLD numbers and nothing re-bound them, leaving
    // `onSiteInfrastructure` still asserting `verified-from-kmz` at one
    // distance while `grid.data` reported another. The graft cannot fix this
    // by preserving the stamp (that would annotate a provider number as user
    // ground truth); the value and its stamp have to be rewritten together,
    // which is exactly what these do.
    if (merged.onSiteInfrastructure?.status === 'verified-from-kmz') {
      try {
        const { crossBindGridFromKmz, crossBindAllKmzCorridors } =
          await import('./siteEvidence/compileOnSiteInfrastructure');
        const gridForBind = merged.grid as typeof merged.grid & {
          data: { substationDistKm: number; substationDistKmProvenance?: 'provider' | 'kmz-import' } | null;
        };
        if (crossBindGridFromKmz(gridForBind, merged.onSiteInfrastructure)) {
          console.info(
            `[SED-BOOTSTRAP] topUp KSD2 re-bind: grid.substationDistKm <- KMZ (${merged.grid.data?.substationDistKm} km)`,
          );
        }
        const landed = crossBindAllKmzCorridors(merged);
        if (landed.length > 0) {
          console.info(`[SED-BOOTSTRAP] topUp KSI corridor re-bind: ${landed.join(', ')}`);
        }
      } catch (e) {
        // Additive — a re-bind failure must never abort the top-up.
        console.warn('[SED-BOOTSTRAP] topUp KMZ re-bind failed:', (e as Error)?.message);
      }
    }

  // v1.3.1 HR-5 review fix — fuelSupply has NO compileSiteEvidence
    // producer (stampFuelSupplyAndLcoe is a post-compile bootstrap step),
    // so the graft loop above can never carry it: the fetched EIA price
    // was silently discarded and the 'rerun providers' remediation could
    // never succeed. When the sweep ran the gas provider, stamp fuelSupply
    // + lcoe directly from the fetched data and refresh cross-validation
    // so C4/C5 reflect the new grounding.
    if (providersToRun.includes('eia-natural-gas')) {
      try {
        const { stampFuelSupplyAndLcoe } = await import('./siteEvidence/compileFuelSupplyAndLcoe');
        stampFuelSupplyAndLcoe(merged, deepData.naturalGas ?? null, deepData.providerStatuses ?? []);
        const { rerunCrossValidationAndMetadata } = await import('./siteEvidence');
        rerunCrossValidationAndMetadata(merged, deepData.providerStatuses ?? []);
        for (const sec of ['fuelSupply', 'lcoe'] as const) {
          const slot = merged[sec] as EvidenceSection<unknown> | undefined;
          if (slot && (slot.status as EvidenceStatus) !== 'unavailable' && !upgraded.includes(sec)) {
            upgraded.push(sec);
          }
        }
      } catch (e) {
        console.warn('[SED-TOPUP] HR-5 fuelSupply/lcoe stamp failed:', (e as Error)?.message);
      }
    }

    // SED D2 — interconnection has NO compileSiteEvidence producer (it is a
    // post-compile stamp), so the graft loop above can never carry it. When
    // the sweep ran the iso-queue provider, stamp the typed interconnection
    // section directly from the fetched ISO queue + declared intent status.
    if (providersToRun.includes('iso-queue')) {
      try {
        const { stampInterconnectionAndIncentives } = await import('./siteEvidence/compileInterconnectionAndIncentives');
        const stateCode = inferStateCodeFromSed(merged);
        await stampInterconnectionAndIncentives(merged, {
          stateCode,
          lat: merged.location.lat,
          lng: merged.location.lng,
          intent: activeIntent,
          btmStrategy: site.typedPreferencesDefaults?.['power-availability']?.btmStrategy,
          financial: merged.financialAssumptions,
          providerStatuses: deepData.providerStatuses,
          signal: options.abortSignal,
        });
        const { rerunCrossValidationAndMetadata } = await import('./siteEvidence');
        rerunCrossValidationAndMetadata(merged, deepData.providerStatuses ?? []);
        for (const sec of ['interconnection', 'incentives'] as const) {
          const slot = merged[sec] as EvidenceSection<unknown> | undefined;
          if (slot && (slot.status as EvidenceStatus) !== 'unavailable' && !upgraded.includes(sec)) {
            upgraded.push(sec);
          }
        }
      } catch (e) {
        rethrowIfAborted(options.abortSignal, e);
        console.warn('[SED-TOPUP] D2 interconnection/incentives stamp failed:', (e as Error)?.message);
      }
    }

    // SED D2 (H-6) — multiHazard / transmissionHeadroom / waterResource have NO
    // compileSiteEvidence producer (they are post-compile stamps), so the graft
    // loop can never carry them. Stamp them when ANY is still absent — the FEMA
    // NRI fetch only runs when `multiHazard` was never stamped, so a top-up that
    // already carries the hazard section costs no extra network call.
    if (!merged.multiHazard || !merged.transmissionHeadroom || !merged.waterResource) {
      try {
        const { stampMultiHazardAndResources } = await import('./siteEvidence/compileMultiHazardAndResources');
        await stampMultiHazardAndResources(merged, {
          lat: merged.location.lat,
          lng: merged.location.lng,
          signal: options.abortSignal,
        });
        const { rerunCrossValidationAndMetadata } = await import('./siteEvidence');
        rerunCrossValidationAndMetadata(merged, deepData.providerStatuses ?? []);
        for (const sec of ['multiHazard', 'transmissionHeadroom', 'waterResource'] as const) {
          const slot = merged[sec] as EvidenceSection<unknown> | undefined;
          if (slot && (slot.status as EvidenceStatus) !== 'unavailable' && !upgraded.includes(sec)) {
            upgraded.push(sec);
          }
        }
      } catch (e) {
        rethrowIfAborted(options.abortSignal, e);
        console.warn('[SED-TOPUP] D2 H-6 hazard/headroom/water stamp failed:', (e as Error)?.message);
      }
    }

  }

  // Pure intent/financial producers must also run without a live provider
  // sweep. Unknown answers remain unavailable, but clearing a prior chosen
  // value is still a real change that must be saved.
  if (runIntentStampers) {
    try {
      const { stampTechnologyFromIntent } = await import('../lib/stampTechnologyFromIntent');
      const { stampBtmGroundingFromIntent } = await import('../lib/stampBtmGroundingFromIntent');
      const before = new Map(['technology', 'cooling', 'power', 'firmCapacity'].map(sec =>
        [sec, JSON.stringify(merged[sec as keyof SiteEvidenceDocument])],
      ));
      stampTechnologyFromIntent(merged, activeIntent);
      stampBtmGroundingFromIntent(merged, activeIntent);
      for (const sec of ['technology', 'cooling', 'power', 'firmCapacity'] as const) {
        const slot = merged[sec] as EvidenceSection<unknown> | undefined;
        if (slot && JSON.stringify(slot) !== before.get(sec)) {
          merged.sectionIntentInputHashes = { ...merged.sectionIntentInputHashes,
            [sec]: sedSectionIntentInputHash(sec, activeIntent) };
          if (!upgraded.includes(sec)) upgraded.push(sec);
        }
      }
    } catch (e) {
      console.warn('[SED-TOPUP] Intent stampers failed:', (e as Error)?.message);
    }
  }

  if (runFinancialStampers) {
    try {
      const { buildLcoeEvidence } = await import('./siteEvidence/compileFuelSupplyAndLcoe');
      const priorLcoe = JSON.stringify(merged.lcoe);
      const priorIncentives = JSON.stringify(merged.incentives);
      merged.lcoe = buildLcoeEvidence(merged.financialAssumptions, merged.fuelSupply);
      const slot = merged.lcoe as EvidenceSection<unknown> | undefined;
      if (slot && JSON.stringify(slot) !== priorLcoe) {
        merged.sectionIntentInputHashes = { ...merged.sectionIntentInputHashes,
          lcoe: sedSectionIntentInputHash('lcoe', activeIntent) };
        if (!upgraded.includes('lcoe')) upgraded.push('lcoe');
      }
      // SED D2 — incentives derives from financialAssumptions (pure IRA
      // calculator). Re-stamp on a financial refresh so the credit route
      // tracks the updated technology / in-service date / capacity.
      const { buildIncentivesEvidence } = await import('./siteEvidence/compileInterconnectionAndIncentives');
      merged.incentives = buildIncentivesEvidence(activeIntent, merged.financialAssumptions);
      const incSlot = merged.incentives as EvidenceSection<unknown> | undefined;
      if (incSlot && JSON.stringify(incSlot) !== priorIncentives) {
        merged.sectionIntentInputHashes = { ...merged.sectionIntentInputHashes,
          incentives: sedSectionIntentInputHash('incentives', activeIntent) };
        if (!upgraded.includes('incentives')) upgraded.push('incentives');
      }
    } catch (e) {
      console.warn('[SED-TOPUP] LCOE/incentives stamper failed:', (e as Error)?.message);
    }
  }

  // LLW-2 — External Literature refresh. Runs through the orchestrator
  // (the synthetic `external-literature` provider) so the result lands
  // on `merged.externalLiterature` regardless of whether the standard
  // sweep ran. Reuses the prior section when still inside the 14-day
  // TTL so a refresh-all click within the window doesn't re-hit
  // Crossref/arXiv/PubMed for free.
  if (wantsExternalLiteratureRefresh) {
    const priorLit = existing.externalLiterature;
    const litBefore = merged.externalLiterature;
    await maybeFetchExternalLiterature(merged, site, priorLit, siteId, {
      reportType: options.reportType,
      abortSignal: options.abortSignal,
      ...(isScoped ? {
        source: 'refresh',
        forceRefresh: true,
        rateLimitMs: 0,
        providerCacheTtlMs: 0,
      } : {}),
    });
    // Only treat as an upgrade if the slot actually changed (status
    // flipped from unavailable→ok/partial or fetchedAt advanced past
    // the TTL window). When `maybeFetchExternalLiterature` reused the
    // prior section verbatim, the slot is unchanged and we don't
    // report an upgrade.
    const litAfter = merged.externalLiterature;
    if (litAfter && litAfter !== litBefore) {
      upgraded.push('externalLiterature');
    }
  }

  // PSE-3 — Public Sentiment refresh. Mirror of LLW-2 above for the
  // sibling synthetic orchestrator. Cache HIT (Firestore `pse-cache`,
  // shared scan-age TTL) short-circuits the orchestrator call entirely; MISS
  // runs the orchestrator and writes the result back to Firestore.
  if (wantsPublicSentimentRefresh) {
    const priorPse = existing.publicSentiment;
    const pseBefore = merged.publicSentiment;
    await maybeFetchPublicSentiment(merged, priorPse, siteId, {
      abortSignal: options.abortSignal,
      ...(isScoped ? {
        source: 'refresh',
        forceRefresh: true,
        rateLimitMs: 0,
        cacheTtlMs: 0,
      } : {}),
    });
    const pseAfter = merged.publicSentiment;
    if (pseAfter && pseAfter !== pseBefore) {
      upgraded.push('publicSentiment');
    }
  }

  // ── DCO-3 — Tier 3 (DAM) + Tier 4 (grounded research) for topUp ──
  //
  // Mirrors the DCO block in the main bootstrap path (~line 1577) so
  // sections still 'unavailable' after the provider sweep + the
  // external-literature pass get a chance to be filled from the
  // curated DAM (cheap, one Firestore read per topUp) and then via
  // grounded research as a last resort.
  //
  // DCO-3 wiring (this block) — loads the per-report-type DAM exactly
  // once per topUp call into a local cache (`damForTopUp`), then hands
  // the orchestrator the DAM through `opts.dam`. The orchestrator
  // itself enforces:
  //   - PROTECTED_STATUSES → only `unavailable` slots are touched, so
  //     archived SED snapshots and verified slots are never mutated;
  //   - `applyDamToSlot` short-circuits when the DAM has no entry that
  //     matches `(reportType, sectionId, fieldKey, geoScope)`.
  //
  // Reproducibility invariant (per the DCO-3 contract): when
  // `VITE_DCO_LIVE` is off the orchestrator returns immediately, and
  // when on it ONLY fills slots that were `unavailable` AND match a
  // DAM entry — slots without a DAM match fall through to research,
  // which is the pre-DCO-3 behaviour of the bootstrap path.
  //
  // Cost envelope (per VC-2 hard rule 9): one `loadCurrentDam` read
  // per (reportType, topUp call). The orchestrator does NOT fan out
  // DAM loads — `opts.dam` is the single resolved value.
  let dcoFilledThisTopUp = 0;
  try {
    const { fillSedSlotDiligently } = await import('./dataCompletionOrchestrator');
    // `dcoReportTypeForGate` + `dcoLiveForThisTopUp` were resolved above
    // (before the no-provider short-circuit) so a pure DCO-only topUp
    // — providers had nothing to refresh but DCO has a DAM-fillable
    // unavailable slot — doesn't bail at the earlier guard. Reuse them
    // here so report-type + liveness are evaluated exactly once per
    // topUp call.
    const dcoReportType = dcoReportTypeForGate;
    if (dcoLiveForThisTopUp) {
      // Load the DAM once per topUp call. `loadCurrentDam` does at
      // most 2 Firestore reads (pointer doc + latest version fallback);
      // we cache the resolved value in `damForTopUp` so a future
      // multi-pass extension of this block (e.g. per-section refresh)
      // can reuse it without re-reading.
      let damForTopUp: import('../types/datacrateModel').DatacrateModel | null = null;
      try {
        const { loadCurrentDam } = await import('./dam/damStorage');
        damForTopUp = await loadCurrentDam(dcoReportType);
      } catch (e) {
        // Best-effort — a DAM load failure must not sink the topUp.
        // The orchestrator simply runs with `dam: null` (Wave-A
        // behaviour: Tier 3 skipped, Tier 4 still fires).
        console.warn(
          `[dco:topup] loadCurrentDam(${dcoReportType}) failed; falling through to research-only:`,
          (e as Error)?.message,
        );
      }

      const stateCode = inferStateCodeFromSed(merged);
      const orchestratorResult = await fillSedSlotDiligently(merged, {
        reportType: dcoReportType,
        site: {
          id: site.id,
          name: merged.siteName,
          address: merged.location.address,
          centroid: [merged.location.lat, merged.location.lng],
          locationLabel: merged.location.address,
          stateCode: stateCode ?? undefined,
          countryCode: 'US',
        },
        dam: damForTopUp,
      });

      dcoFilledThisTopUp = orchestratorResult.filled + orchestratorResult.damFilled;

      // Reflect DCO-filled slots in the topUp's `upgraded` return list
      // so the caller (e.g. `generateTypedReport`) sees the SED moved
      // forward even when the provider sweep itself upgraded nothing.
      // De-dup against the provider/literature pushes above — a slot
      // can be filled by both tiers if a provider returned data the
      // DCO orchestrator had already stamped, though in practice the
      // orchestrator only runs on slots still `unavailable` post-merge.
      for (const row of orchestratorResult.perSlot) {
        if (row.outcome === 'filled' || row.outcome === 'filled-from-dam') {
          if (!upgraded.includes(row.sectionId)) upgraded.push(row.sectionId);
        }
      }

      if (orchestratorResult.filled > 0 || orchestratorResult.damFilled > 0 ||
          orchestratorResult.attemptedAndRejected > 0) {
        console.log(
          `[dco:topup] filled=${orchestratorResult.filled}` +
          ` dam=${orchestratorResult.damFilled}` +
          ` rejected=${orchestratorResult.attemptedAndRejected}` +
          ` skipped=${orchestratorResult.skippedNoTemplate}` +
          ` (site=${siteId}, reportType=${dcoReportType}, damLoaded=${damForTopUp != null})`,
        );
      }
    }
  } catch (e) {
    // Best-effort — DCO is opt-in via VITE_DCO_LIVE; even when on, a
    // failure here must not abort the topUp. The provider-side
    // upgrades stay valid.
    console.warn('[dco:topup] orchestrator dispatch failed:', (e as Error)?.message);
  }

  // Rebuild reviewed LandGate contributions after provider grafts. The helper
  // is authoritative for promotion/unpromotion and returns exactly the SED
  // sections whose allowlisted screening facts changed, including the case
  // where a canonical provider returned unavailable.
  {
    reviewedLandGateReconciliation = reconcileReviewedLandGateEvidence(
      merged,
      canonicalSiteLayers,
    );
    for (const sectionId of reviewedLandGateReconciliation.changedSections) {
      if (!upgraded.includes(sectionId)) upgraded.push(sectionId);
    }
  }

  // Project authored claims from "What we know about this site" (`site.siteContext`)
  // onto the SED before the no-upgrade check so user-authored site knowledge and
  // structured field overlays always take effect even on scoped input refreshes.
  if (site.siteContext?.claims && site.siteContext.claims.length > 0) {
    try {
      const { applyAuthoredEvidenceToSed } = await import('./sedAuthoredEvidence');
      const { applyAuthoredStructuredFieldsToSed } = await import('../lib/coverage/authoredSedOverlay');
      const flipped = applyAuthoredEvidenceToSed(merged, site.siteContext.claims, options.reportType ?? 'feasibility');
      const patched = applyAuthoredStructuredFieldsToSed(merged, site.siteContext.claims);
      if ((flipped > 0 || patched > 0) && !upgraded.includes('siteContext')) {
        upgraded.push('siteContext');
      }
    } catch (e) {
      console.warn('[UAE-4] topUp applyAuthoredEvidenceToSed failed:', (e as Error)?.message);
    }
  }

  if (site.typedPreferencesDefaults?.environmental) {
    try {
      const { applyEnvironmentalPreferencesToSed } = await import('../lib/coverage/applyTypedPreferencesToSed');
      const envPatched = applyEnvironmentalPreferencesToSed(merged, site.typedPreferencesDefaults.environmental);
      if (envPatched > 0 && !upgraded.includes('typedPreferences')) {
        upgraded.push('typedPreferences');
      }
    } catch (e) {
      console.warn('[UAE-4] topUp applyEnvironmentalPreferencesToSed failed:', (e as Error)?.message);
    }
  }

  // `upgraded` carries provider sweep, externalLiterature, AND DCO-3
  // fills (the orchestrator block above pushes its `perSlot` filled
  // rows). The `dcoFilledThisTopUp` counter is a defensive belt-and-
  // braces guard for the equivalent invariant — if either signal says
  // the topUp produced no change, short-circuit without bumping
  // version or paying for the doc-aware re-pass + GCS save.
  if (upgraded.length === 0 && dcoFilledThisTopUp === 0) {
    await acknowledgeCurrentReviewedState();
    logProgress(siteId, 'topup-no-upgrades', 100);
    return { upgraded: [], sedVersion: existing.version, outcome: 'no-change' };
  }

  // Documents_On_Site Prompt 5 — replay link stamping on the merged SED
  // so freshly-upgraded sections retain (and gain) their doc links.
  if (options.includeDocIds && options.includeDocIds.length > 0) {
    const { buildSectionLinks } = await import('./knowledge/sedDocSuggestion');
    const links = buildSectionLinks(site, options.includeDocIds);
    for (const [secId, docIds] of Object.entries(links)) {
      const slot = (merged as unknown as Record<string, EvidenceSection<unknown> | undefined>)[secId];
      if (!slot || !docIds || docIds.length === 0) continue;
      slot.linkedDocumentIds = Array.from(new Set([...(slot.linkedDocumentIds ?? []), ...docIds]));
    }
  }

  // Documents_On_Site Prompt 11 — re-apply doc-aware confidence on the
  // merged SED so newly-upgraded sections pick up boosts and any
  // still-unavailable sections get a fallback extraction pass.
  // Recomputing declared inputs does not ask the document/provider models to
  // retry unrelated unavailable evidence. Stored source receipts stay intact.
  if (!pureInputRefresh) await applyDocAwareConfidence(merged, site, { siteId, bodyLoader: createSiteDocumentBodyLoader({ siteId, ownerUid: siteLayerOwnerUid }) });

  // ── Operator decisions win, and they win LAST ──
  //
  // Every write above can clobber an amended field: the provider-section
  // graft, the whole-slot `merged.lcoe` / `merged.incentives` assignments, the
  // interconnection / hazard / fuel stampers, and `applyDocAwareConfidence`
  // right before this (a utility service letter owns grid.capacity /
  // utilityName / voltage via DOC_OVERRIDE_RULES). Re-applying amendments
  // anywhere earlier meant a later pass could silently undo them inside this
  // same call, so the replay runs once here, after everything.
  //
  // Sequential over the recorded chain — NOT last-row-wins. The SED variable
  // browser stamps `oldValue` from the value ON SCREEN, so a second edit to a
  // field records the FIRST correction as its "original". Only replaying the
  // chain in order lands both edits (and makes a revert row work for free).
  {
    const replay = reapplyInBandAmendments(merged);
    if (replay.reappliedPaths.length > 0) {
      console.info(
        `[SED-BOOTSTRAP] topUp amendments reapplied: ${replay.reappliedPaths.join(', ')}`,
      );
    }
    if (replay.orphanedPaths.length > 0) {
      console.warn(
        `[SED-BOOTSTRAP] topUp amendment paths no longer resolve: ${replay.orphanedPaths.join(', ')}`,
      );
    }
    if (replay.conflicts.length > 0) {
      console.warn(
        `[SED-BOOTSTRAP] topUp evidence superseded ${replay.conflicts.length} amendment(s) — `
        + replay.conflicts
          .map(c => `${c.path} user=${JSON.stringify(c.userValue)} now=${JSON.stringify(c.providerValueNow)}`)
          .join(' | '),
      );
      graftAmendmentConflicts = replay.conflicts;
    }
  }

  const { finalizeSedEvidenceMetadata } = await import('./siteEvidence');
  finalizeSedEvidenceMetadata(merged);

  // Bump version + save. Anchor to the site's persisted floor so a stale
  // `existing` (loaded at a lower version than the site doc records) can't
  // regress the badge.
  merged.version = nextSedVersion(site, existing.version);
  merged.collectedAt = new Date().toISOString();

  // sedStatus check — measure against ALL provider-backed sections, not
  // just the scoped subset. Without this, a scoped refresh of one
  // already-verified section would flip a partial site to 'complete'
  // (because every section in candidateSections is verified by
  // definition for scoped runs).
  const sectionsForStatusCheck = isScoped
    ? Object.keys(SECTION_TO_PROVIDER_ID)
    : candidateSections;
  const allRequiredVerified = sectionsForStatusCheck.every(sec => {
    const slot = merged[sec as keyof SiteEvidenceDocument] as EvidenceSection<unknown> | undefined;
    return slot && slot.status !== 'unavailable';
  });
  // Save, then advance the pointer through the compare-and-set. The status
  // patch is dropped there if a build took the site after the upload.
  const committed = await commitTopUpSave(user.uid, merged, {
    sedStatus: allRequiredVerified ? 'complete' : 'partial',
    // SED_Stale_Data_Rebuild SD1 — a topUp is a successful rebuild of
    // (part of) the SED; refresh the build stamp so it doesn't read as
    // stale after a section refresh.
    sedComputedAt: new Date().toISOString(),
  });
  if (!committed.ok) {
    if (committed.publicationCommitted) {
      console.error('[SED-BOOTSTRAP] topUp: publication committed but reload failed —', committed.reason);
      return {
        upgraded,
        sedVersion: merged.version,
        committedSedVersion: committed.committedSedVersion,
        committedStoragePath: committed.committedStoragePath,
        publicationCommitted: true,
        outcome: 'sync-unresolved',
        saveFailReason: committed.reason,
        ...(graftAmendmentConflicts.length > 0 ? { graftAmendmentConflicts } : {}),
      };
    }
    // Surface the persistence failure instead of returning null silently —
    // otherwise a failed rebuild looks like a no-op ("ran the rebuild, nothing
    // updated") and the stale SED keeps rendering. The helper stamped a
    // 'failed' status + reason (unless a build owns the site) so the UI can
    // surface it.
    console.error('[SED-BOOTSTRAP] topUp: GCS save failed —', committed.reason);
    return null;
  }
  logProgress(siteId, 'topup-saved', 95);
  await acknowledgeCurrentReviewedState();
  // No abort check past this point: the pointer already names these bytes,
  // so the truthful report is 'saved'.
  logProgress(siteId, 'done', 100);
  return {
    upgraded,
    sedVersion: merged.version,
    outcome: 'saved',
    ...(graftAmendmentConflicts.length > 0 ? { graftAmendmentConflicts } : {}),
  };
}

/* ── KSD5 — per-doc re-extract + targeted partial rebuild ─────────── */

export interface ReExtractDocResult {
  /** Sections actually re-extracted (the doc was linked to them). */
  affectedSections: SedSectionId[];
  /** Sections whose values changed as a result. */
  changedSections: SedSectionId[];
  /**
   * D3 — requirements whose channel-B (authoredSchema) extraction stamped
   * autoFillFields during this scan. A stamp-only scan (no section value
   * changed) still persists: the stamps ARE evidence now (D4 overlay).
   */
  stampedRequirements: string[];
  /** New SED version after the partial rebuild. Equals prior version
   *  when nothing changed (no-op save skipped). */
  sedVersion: number;
}

/** Selected-document receipt only; never changes source grade or requirement fulfillment. */
async function stampDocumentScanStatus(
  siteId: string,
  doc: SiteDocument,
  sourceRevision: string,
  sed: SiteEvidenceDocument,
  targets: DocumentExtractionOutcomeV1['targets'],
): Promise<void> {
  try {
    const useStore = await loadAppStore();
    const live = useStore.getState().siteRag.sites.find(site => site.id === siteId)?.documents?.find(row => row.id === doc.id);
    if (!live) return;
    // A late scan must not label a replaced, archived or retargeted source,
    // but must clear a stranded 'extracting' state if selection changed mid-scan.
    if (manualScanSourceRevision(live) !== sourceRevision) {
      if (live.extractionStatus === 'extracting') {
        await useStore.getState().patchSiteDocument(siteId, doc.id, {
          extractionStatus: 'pending',
          extractionStatusAt: Date.now(),
          extractionStatusError: undefined,
        });
      }
      return;
    }
    const counts = documentMappingCounts(sed, doc.id);
    const at = Date.now();
    const extractionOutcome: DocumentExtractionOutcomeV1 = { version: 1, at,
      retainedFieldCount: counts.retainedFieldCount, linkedSectionCount: counts.linkedSectionCount, targets };
    const summary = documentMappingSummary(extractionOutcome);
    const failure = targets.find(target => target.status === 'failed');
    const failureCode = failure ? manualScanFailureCode(failure.reason) : undefined;
    const { status, errorMsg } = resolveForcedScanStatus({
      attempts: targets.filter(target => target.attempts > 0).length,
      successes: targets.filter(target => target.status === 'extracted').length,
    }, doc.textContent);
    const allCompletedCleanly =
      summary.failed === 0 &&
      summary.deferred === 0 &&
      targets.length > 0 &&
      hasUsableExtractedText(doc.textContent);
    const cleanTerminalStatus: import('../types/siteRag').ExtractionStatus =
      counts.populatedSlotCount > 0 && summary.empty === 0 && summary.rejected === 0
        ? 'success-full'
        : 'success-partial';
    await useStore.getState().patchSiteDocument(siteId, doc.id, {
      extractionStatus: summary.failed ? failureCode ?? 'failed-unknown'
        : summary.deferred ? 'success-partial'
        : allCompletedCleanly ? cleanTerminalStatus : status,
      extractionStatusAt: at,
      extractionStatusError: summary.failed ? 'Some field-mapping targets failed after bounded attempts.'
        : summary.deferred ? 'Some field-mapping targets remain deferred.'
        : allCompletedCleanly ? undefined : errorMsg,
      populatedSlotCount: counts.populatedSlotCount,
      extractionOutcome,
    });
  } catch (e) {
    console.warn(`[DLS-1] document scan receipt patch failed for ${doc.id}:`, (e as Error)?.message);
  }
}

function manualScanFailureCode(error: unknown): import('../types/siteRag').ExtractionStatus {
  const text = (error instanceof Error ? error.message : String(error)).toLowerCase();
  if (text.includes('timeout') || text.includes('timed out')) return 'failed-timeout';
  if (text.includes('quota') || text.includes('429') || text.includes('rate limit')) return 'failed-quota';
  if (text.includes('parse') || text.includes('json') || text.includes('schema')) return 'failed-parse';
  return 'failed-unknown';
}

/** Status telemetry is excluded; source bytes and extraction selection are not. */
function manualScanSourceRevision(doc: SiteDocument): string {
  return JSON.stringify([doc.contentHash, doc.storagePath, doc.updatedAt,
    doc.pdfTextReprocessedAt, doc.officeReprocessedAt, doc.textContent, doc.fullExtractedText,
    doc.includeInSed, doc.archived, doc.deletedAt, doc.intendedRequirementIds, doc.intentSetAt]);
}

/**
 * KSD5 — Re-extract a single document against every SED section it
 * already contributes to, then persist the resulting partial rebuild.
 *
 * Scope of work:
 *   1. Load the SED from GCS (or local cache via the loader helper).
 *   2. Find every section whose `linkedDocumentIds` or
 *      `supportingDocIds` includes the target doc.
 *   3. Re-invoke `memoisedExtractWithOutcome({ bypassCache: true })`
 *      for each — the bypass forces a fresh Gemini call so the
 *      re-extract sees any document edits the user has made.
 *   4. Apply the fresh data into the SED with the same surface as
 *      `applyDocAwareConfidence`: stamp `verified-from-doc` with
 *      DOC_FALLBACK_CONFIDENCE, supportingDocIds, etc.
 *   5. Bump version + save to GCS + write audit event.
 *
 * UD-preservation: this USED to claim `mergePreservedUserDecisions` kept
 * user-amended fields safe here. It did not — that helper is not called on
 * this path at all, and `slot.data = fresh.data` replaced the section
 * wholesale, so an operator amendment (and grid `capacityDetail`) vanished
 * whenever a doc was re-scanned. Preservation is now explicit: the section
 * data goes through `graftProviderSection` (authored-only fields + human
 * field-review rulings survive), and `reapplyInBandAmendments` replays the
 * amendment chain over the sections this pass rewrote.
 *
 * Best-effort: per-section extraction failures log and skip; the
 * caller sees the affected-but-unchanged set in `affectedSections`
 * minus `changedSections`.
 *
 * DLS-1 — `opts.force` (the "Scan now" escape hatch).
 *
 * Doc extraction is section-driven: `applyDocAwareConfidence` only
 * extracts the single best-matching doc per UNAVAILABLE section, then
 * stamps every non-winning doc `extractionStatus: 'pending'`. Such a
 * doc contributes to ZERO sections, so the default (non-force) path
 * above returns a no-op — a "Scan now" that did nothing.
 *
 * In FORCE mode, when the doc is on no section we derive CANDIDATE
 * sections from the doc's own classifier hints — `sedSectionsHelpful`
 * plus any `intendedRequirementIds` mapped through
 * REQUIREMENT_TO_SECTION_MAP — and extract against each, regardless of
 * the relevance threshold that gated the doc out of the section-driven
 * path. We only FILL sections that are still `unavailable` /
 * `verified-from-doc` (never clobber a verified-provider value), exactly
 * like the unavailable-branch of `applyDocAwareConfidence`. The doc's
 * `extractionStatus` is then stamped to a terminal value
 * (success-full / success-partial / failed-*) so the chip leaves the
 * `pending` dead-end whether or not any section actually changed.
 *
 * Force mode also stamps the status when the doc DID contribute to
 * sections but every re-extract returned null (so a forced scan never
 * silently leaves the doc at pending).
 */
export interface ReExtractDocOptions {
  /**
   * DLS-1 — force extraction even when the doc is on zero SED sections.
   * Derives candidate sections from the doc's `sedSectionsHelpful` /
   * `intendedRequirementIds`, extracts against each, and stamps a
   * terminal `extractionStatus` so the doc leaves `'pending'`. Default
   * false preserves section retry selection and reuses completed requirement receipts.
   */
  force?: boolean;
}

export async function reExtractDocForSed(
  siteId: string,
  docId: string,
  opts?: ReExtractDocOptions,
): Promise<ReExtractDocResult | null> {
  const force = opts?.force === true;
  const useStore = await loadAppStore();
  const store = useStore.getState();
  const site = store.siteRag.sites.find(s => s.id === siteId);
  if (!site) {
    console.warn(`[KSD5] reExtract: site ${siteId} not in store`);
    return null;
  }
  if (!site.sedStoragePath) {
    console.warn(`[KSD5] reExtract: site ${siteId} has no SED yet`);
    return null;
  }
  let doc = (site.documents ?? []).find(d => d.id === docId);
  if (!doc) {
    console.warn(`[KSD5] reExtract: doc ${docId} not on site ${siteId}`);
    return null;
  }
  if (doc.includeInSed === false) {
    if (doc.extractionStatus === 'extracting') {
      await store.patchSiteDocument(siteId, docId, {
        extractionStatus: 'pending',
        extractionStatusAt: Date.now(),
        extractionStatusError: undefined,
      });
    }
    return { affectedSections: [], changedSections: [], stampedRequirements: [], sedVersion: site.sedVersion ?? 1 };
  }

  const sourceRevision = manualScanSourceRevision(doc);
  const baselinePointer = sedPointerOf(site);
  const loadedSedWithGen = await loadSedFromGcsWithGeneration(site.sedStoragePath);
  const sed = loadedSedWithGen?.sed ?? null;
  const loadedSedGeneration = loadedSedWithGen?.generation ?? null;
  if (!sed) {
    console.warn(`[KSD5] reExtract: failed to load SED for ${siteId}`);
    return null;
  }

  // Site records retain only a preview. Never scan a truncated source or
  // absent topic index when the maintained body loader knows more exists.
  const { hydrateDocumentBodies, documentNeedsBodyHydration, documentNeedsTopicHydration } = await import('../lib/documentBody');
  const { auth: bodyAuth } = await import('../firebase');
  const { siteOwnerUid: bodyOwnerUid } = await import('../lib/siteOwnerPath');
  const [hydrated] = await hydrateDocumentBodies([doc], { loader: createSiteDocumentBodyLoader({
    siteId, ownerUid: bodyOwnerUid(site, bodyAuth.currentUser?.uid ?? ''),
  }) });
  if (hydrated) doc = hydrated;
  if (documentNeedsBodyHydration(doc) || documentNeedsTopicHydration(doc)) {
    await stampDocumentScanStatus(siteId, doc, sourceRevision, sed, [{ channel: 'requirement', targetId: 'source-hydration',
      status: 'deferred', attempts: 0, reason: 'complete-source-unavailable' }]);
    return { affectedSections: [], changedSections: [], stampedRequirements: [], sedVersion: sed.version };
  }

  // Step 2 — find affected sections.
  const affected: SedSectionId[] = [];
  const { SED_SECTION_IDS: SECTION_IDS } = await import('../types/siteEvidence');
  for (const sid of SECTION_IDS) {
    const slot = (sed as unknown as Record<string, { linkedDocumentIds?: string[]; supportingDocIds?: string[] } | undefined>)[sid];
    if (!slot) continue;
    const linked = new Set([
      ...(slot.linkedDocumentIds ?? []),
      ...(slot.supportingDocIds ?? []),
    ]);
    if (linked.has(docId)) affected.push(sid);
  }

  // DLS-1 — force mode: when the doc is on no existing section, derive
  // candidate sections from the doc's own classifier hints so a "Scan
  // now" actually extracts a section-unmatched doc instead of no-opping.
  // Candidates = `sedSectionsHelpful` ∪ (intendedRequirementIds mapped
  // through REQUIREMENT_TO_SECTION_MAP). We restrict to sections still
  // safe to fill (`unavailable` / `verified-from-doc`) below so a forced
  // scan never clobbers a verified-provider value.
  const forcedCandidates: SedSectionId[] = [];
  if (force && affected.length === 0) {
    const candidateSet = new Set<SedSectionId>();
    const validSectionIds = new Set<string>(SECTION_IDS);
    for (const sid of doc.sedSectionsHelpful ?? []) {
      // Guard against stale section ids on the doc's classifier hint.
      if (validSectionIds.has(sid)) candidateSet.add(sid);
    }
    if (doc.intendedRequirementIds && doc.intendedRequirementIds.length > 0) {
      const { REQUIREMENT_TO_SECTION_MAP } = await import('../config/requirementToSection');
      for (const reqId of doc.intendedRequirementIds) {
        for (const sid of REQUIREMENT_TO_SECTION_MAP[reqId] ?? []) {
          candidateSet.add(sid);
        }
      }
    }
    // Only force-fill sections that are still safe to overwrite with a
    // doc value (unavailable or already doc-derived). Skip verified-
    // provider / anomalous sections so a forced scan can't stomp a
    // provider-grounded value.
    for (const sid of SECTION_IDS) {
      if (!candidateSet.has(sid)) continue;
      const slot = (sed as unknown as Record<string, EvidenceSection<unknown> | undefined>)[sid];
      if (!slot) continue;
      if (slot.status === 'unavailable' || slot.status === 'verified-from-doc') {
        forcedCandidates.push(sid);
      }
    }
  }

  // D3 — no early return on zero sections any more: the channel-B
  // (requirement-scoped) pass below can still stamp autoFillFields for a
  // doc that maps to no extractable SECTION, and those stamps are evidence
  // (D4 overlay). The no-op decision now happens after both passes.

  // In force mode the candidate sections ARE the sections we extract
  // against. Merge them into `affected` (dedup) so the existing
  // per-section loop below picks them up unchanged.
  if (force && forcedCandidates.length > 0) {
    const merged = new Set<SedSectionId>([...affected, ...forcedCandidates]);
    affected.length = 0;
    affected.push(...merged);
  }

  // Step 3+4 — re-extract per section. bypassCache forces a fresh
  // Gemini call (KSD6 cache is otherwise hit-on-second-call which is
  // the wrong behaviour for an explicit user-driven re-extract).
  const { memoisedExtractWithOutcome, memoisedExtractRequirementFields } = await import('./knowledge/extractionCache');
  const { DOC_FALLBACK_CONFIDENCE, sectionSchemaFieldNames, isExtractionRejection } = await import('./knowledge/extractFromDocument');
  const { withTimeout } = await import('../lib/withTimeout');
  // DAT-FOLLOWUP-1 — site-state prior for the explicit per-doc
  // re-extract. Same probe order as the main bootstrap path; passed
  // through so the locus-binding guard fires on a Re-extract action
  // just as it does on the first ingestion.
  const { inferSiteStateFromSed: inferSiteStateForReExtract } = await import('../lib/inferSiteState');
  const reExtractSiteState = inferSiteStateForReExtract(sed);
  const reExtractSiteName = sed.siteName;
  const changed: SedSectionId[] = [];
  type Target = DocumentExtractionOutcomeV1['targets'][number];
  const targets = new Map<string, Target>();
  const record = (channel: Target['channel'], targetId: string, status: Target['status'], reason?: string, attempted = true) => {
    const key = `${channel}:${targetId}`;
    targets.set(key, { channel, targetId, status,
      attempts: (targets.get(key)?.attempts ?? 0) + (attempted ? 1 : 0), ...(reason ? { reason } : {}) });
  };
  for (const sid of affected) {
    if (sectionSchemaFieldNames(sid).length === 0) {
      record('section', sid, 'unsupported', 'no-schema', false);
      continue;
    }
    try {
      const fresh = await withTimeout(memoisedExtractWithOutcome({
        doc,
        section: sid,
        siteId,
        bypassCache: true,
        siteState: reExtractSiteState,
        siteName: reExtractSiteName,
      }), DOC_EXTRACTION_TIMEOUT_MS, `doc-rescan:${sid}:${docId}`);
      if (isExtractionRejection(fresh)) {
        record('section', sid, fresh.reason === 'no-schema' ? 'unsupported'
          : fresh.reason === 'all-required-missing' ? 'empty'
            : fresh.reason === 'call-failed' ? 'failed' : 'rejected',
          fresh.reason === 'call-failed' ? manualScanFailureCode(fresh.detail) : fresh.reason);
        continue;
      }
      const nonAdoption = documentNonAdoptionReason(sid, fresh);
      if (nonAdoption) { record('section', sid, 'rejected', nonAdoption); continue; }
      record('section', sid, 'extracted');
      const slot = (sed as unknown as Record<string, EvidenceSection<unknown> | undefined>)[sid];
      if (!slot) continue;
      // Only count as 'changed' when the section is unavailable OR
      // verified-from-doc — for a verified-provider section we keep
      // the provider value and just refresh supportingDocIds.
      if (slot.status === 'unavailable' || slot.status === 'verified-from-doc') {
        slot.status = 'verified-from-doc';
        // Same discipline as the top-up graft: a re-extraction owns the fields
        // it produces, but must not delete the authored-only ones it cannot
        // (grid `capacityDetail` / `interconnectionClass` / `utilityName`) or
        // the operator's field-review rulings. The docstring above USED to
        // claim `mergePreservedUserDecisions` handled this; it never ran on
        // this path. Amendments are replayed after the loop.
        slot.data = graftProviderSection({
          sectionId: sid,
          existingSlot: slot,
          providerSlot: { ...slot, data: fresh.data } as EvidenceSection<unknown>,
        }).slot.data as never;
        slot.source = fresh.source;
        slot.confidence = fresh.confidence;
        stampDocumentFieldSources(slot, doc, fresh);
        slot.adjustedConfidence = Math.min(DOC_FALLBACK_CONFIDENCE, fresh.confidence);
        slot.supportingDocIds = Array.from(new Set([...(slot.supportingDocIds ?? []), docId]));
        slot.linkedDocumentIds = Array.from(new Set([...(slot.linkedDocumentIds ?? []), docId]));
        changed.push(sid);
      } else {
        // Verified-provider section — touch supporting + boost.
        slot.supportingDocIds = Array.from(new Set([...(slot.supportingDocIds ?? []), docId]));
        // Boost score is recomputed lazily by the next full bootstrap
        // via computeAdjustedConfidence; here we just record the doc
        // is still on the linked set.
      }
    } catch (e) {
      record('section', sid, 'failed', manualScanFailureCode(e));
      console.warn(`[KSD5] reExtract section=${sid} doc=${docId} failed:`, (e as Error)?.message);
    }
  }

  // Operator decisions win over a re-extraction too. Scoped to the sections
  // this pass actually rewrote so an unrelated amendment elsewhere is not
  // touched by a per-document action.
  if (changed.length > 0) {
    const replay = reapplyInBandAmendments(sed, { sectionIds: changed });
    if (replay.reappliedPaths.length > 0) {
      console.info(`[KSD5] reExtract amendments reapplied: ${replay.reappliedPaths.join(', ')}`);
    }
    if (replay.conflicts.length > 0) {
      console.warn(
        `[KSD5] reExtract superseded ${replay.conflicts.length} amendment(s) — `
        + replay.conflicts.map(c => `${c.path} user=${JSON.stringify(c.userValue)}`).join(' | '),
      );
    }
  }

  // D8 — classifier repair. A doc whose relevance classification never ran
  // (`relevance` undefined — upload-time Gemini outage, pre-classifier
  // upload) was PERMANENTLY blocked from relevance-gated extraction with no
  // repair path. A forced scan is the user saying "look at this doc again",
  // so it re-runs the classifier when a summary exists to classify from.
  if (force && doc.relevance == null && (doc.summary ?? '').trim().length >= 10) {
    try {
      const { classifyDocumentRelevance } = await import('./knowledge/relevanceClassifier');
      const { REPORT_TYPE_REGISTRY } = await import('../config/reportTypes');
      const { SED_SECTION_IDS: ALL_SECTION_IDS } = await import('../types/siteEvidence');
      const relevance = await classifyDocumentRelevance({
        summary: doc.summary,
        tags: doc.tags ?? [],
        fileName: doc.fileName,
        reportTypes: Object.keys(REPORT_TYPE_REGISTRY) as import('../config/reportTypes').ReportTypeId[],
        sedSections: ALL_SECTION_IDS,
        ...(doc.intendedRequirementIds && doc.intendedRequirementIds.length > 0
          ? { requirementHints: doc.intendedRequirementIds }
          : {}),
      });
      const relevancePatch = {
        relevance: relevance.byReportType,
        sedSectionsHelpful: relevance.sedSections,
        relevanceUpdatedAt: new Date().toISOString(),
      } as Partial<import('../types/siteRag').SiteDocument>;
      Object.assign(doc, relevancePatch);
      await store.patchSiteDocument(siteId, docId, relevancePatch);
      console.log(`[D8] forced reExtract repaired missing relevance for doc ${docId} (fallback=${relevance.usedFallback})`);
    } catch (e) {
      console.warn(`[D8] relevance repair failed for ${docId}:`, (e as Error)?.message);
    }
  }

  // Drain only this document's finite requirement queue. Current source-keyed
  // empty receipts are reused on Retry; an explicit forced scan can replace them.
  const stampedRequirements: string[] = [];
  const beforeRequirementMapping = JSON.stringify(sed);
  try {
    const { requirementsRelevantToDoc } = await import('../lib/coverage/applyRequirementDocAutoFill');
    const { runRequirementDocExtractionContinuation } = await import('./knowledge/requirementDocSweep');
    await runRequirementDocExtractionContinuation({
      sed, documents: [doc], siteId, requirementIds: requirementsRelevantToDoc(doc),
      extract: input => memoisedExtractRequirementFields({ ...input, bypassCache: force }),
      onPair: pair => {
        const status = pair.status === 'ineligible' ? 'unsupported' : pair.status;
        record('requirement', pair.requirementId, status,
          pair.status === 'ineligible' ? 'no-applicable-source-or-schema'
            : pair.status === 'failed' ? manualScanFailureCode(pair.error)
              : pair.status === 'rejected' ? 'source-or-field-policy' : undefined,
          pair.status !== 'ineligible' && pair.status !== 'deferred');
        if (pair.stamped && !stampedRequirements.includes(pair.requirementId)) stampedRequirements.push(pair.requirementId);
      },
    });
  } catch (e) {
    record('requirement', 'process', 'deferred', 'requirement-pass-incomplete', false);
    console.warn(`[req-doc-rescan] channel-B pass failed for ${docId}:`, (e as Error)?.message);
  }
  // Source-policy pruning is scoped to [doc]. Persist it too, without claiming
  // new populated fields or a newly fulfilled requirement.
  const requirementMappingChanged = JSON.stringify(sed) !== beforeRequirementMapping;
  const stampReceipt = () => stampDocumentScanStatus(siteId, doc, sourceRevision, sed, [...targets.values()]);

  // Never promote an excluded document into SED if the user excluded it mid-scan.
  const latestDocBeforeSave = useStore.getState().siteRag.sites.find(s => s.id === siteId)?.documents?.find(d => d.id === docId);
  if (latestDocBeforeSave?.includeInSed === false) {
    if (latestDocBeforeSave.extractionStatus === 'extracting') {
      await store.patchSiteDocument(siteId, docId, {
        extractionStatus: 'pending',
        extractionStatusAt: Date.now(),
        extractionStatusError: undefined,
      });
    }
    return { affectedSections: affected, changedSections: [], stampedRequirements: [], sedVersion: sed.version };
  }

  // Step 5 — persist when something changed. A stamp-only scan persists
  // too: autoFillFields are read by the D4 overlay, so an unpersisted
  // stamp would be work the gauges never see.
  if (changed.length === 0 && stampedRequirements.length === 0 && !requirementMappingChanged) {
    if (force || targets.size > 0) await stampReceipt();
    console.log(`[KSD5] reExtract: doc ${docId} — no section changed, nothing stamped — no-op`);
    return { affectedSections: affected, changedSections: [], stampedRequirements: [], sedVersion: sed.version };
  }

  // Monotonic version. Anchor to the site's persisted floor (MAX of
  // site.sedVersion and the loaded SED's own .version) rather than the loaded
  // SED's internal counter alone — a loaded SED whose `.version` disagrees with
  // the denormalized site.sedVersion (the live drift) would otherwise produce a
  // non-monotonic bump here. nextSedVersion enforces strictly-increasing.
  sed.version = nextSedVersion(site, sed.version);
  sed.collectedAt = new Date().toISOString();

  qualifyFictionalDocumentEvidence(sed, site.documents ?? []);
  const { finalizeSedEvidenceMetadata } = await import('./siteEvidence');
  finalizeSedEvidenceMetadata(sed);

  const { auth } = await import('../firebase');
  const user = auth.currentUser;
  if (!user) return null;
  const save = await saveSedToGcs(user.uid, siteId, sed, undefined, site?.sedStoragePath, {
    ifGenerationMatch:
      loadedSedGeneration ??
      (site as { sedStorageGeneration?: string } | undefined)?.sedStorageGeneration ??
      undefined,
  });
  if (!save.ok) {
    // Fail loud: the GCS bytes didn't land, so the pointer below must NOT move
    // (otherwise the site would advertise a version whose bytes are stale).
    // Mirror the bootstrap/topUp failure surface so the UI can show it.
    const reason = formatGcsSaveFailureReason(save);
    await store.updateSite(siteId, {
      sedStatus: 'failed',
      sedFailureReason: reason,
    } as Partial<typeof site>);
    console.error('[KSD5] reExtract: GCS save failed —', reason);
    return null;
  }
  if (
    typeof save.sedVersion !== 'number' ||
    !Number.isFinite(save.sedVersion) ||
    typeof save.sedContentHash !== 'string' ||
    !save.sedContentHash.trim()
  ) {
    const reason = 'Malformed or incomplete staging receipt from /api/storage/save.';
    await store.updateSite(siteId, {
      sedStatus: 'failed',
      sedFailureReason: reason,
    } as Partial<typeof site>);
    return null;
  }
  const reExtractNowIso = new Date().toISOString();
  const effectiveReExtractVersion = save.sedVersion;
  const effectiveReExtractHash = save.sedContentHash;
  const effectiveReExtractSections = save.sectionHashes ?? computeSedSectionHashMirrors(sed);
  const reExtractPointerPatch = {
    // sed is loaded from site.sedStoragePath so sed.id === site.sedId; assert
    // the pointer explicitly so the site doc, the saved bytes, and the badge all
    // agree on (sedId, sedVersion) after the re-extract.
    sedId: sed.id,
    sedVersion: effectiveReExtractVersion,
    sedStoragePath: save.stagedPath ?? save.path,
    ...(save.generation ? { sedStorageGeneration: save.generation } : {}),
    sedContentHash: effectiveReExtractHash,
    ...effectiveReExtractSections,
    sedContentHashVersion: effectiveReExtractVersion,
    sedCollectedAt: sed.collectedAt,
  };
  const committed = await store.commitSedPointerIfUnchanged(
    siteId,
    baselinePointer,
    reExtractPointerPatch,
    {
      statusPatch: { sedComputedAt: reExtractNowIso },
      candidateOperationId: save.operationId,
      operationId: save.operationId ? `pub-${save.operationId}` : undefined,
    },
  );
  if (!committed.committed) {
    console.error(
      `[KSD5] reExtract: staged candidate at ${save.path} was not committed (${committed.reason}); site ${siteId} unchanged`,
    );
    return null;
  }
  const reExtractSync = await syncSedWithServerAuthority(
    sed,
    typeof committed.sedStoragePath === 'string' && committed.sedStoragePath.trim()
      ? committed.sedStoragePath.trim()
      : (save.stagedPath ?? save.path),
    typeof committed.sedVersion === 'number' ? committed.sedVersion : effectiveReExtractVersion,
    typeof committed.sedContentHash === 'string' ? committed.sedContentHash : effectiveReExtractHash,
  );
  try {
    const { invalidateCachedSed } = await import('./sedLoader');
    invalidateCachedSed(sed.id);
  } catch (e) {
    console.warn('[KSD5] reExtract: SED cache invalidation failed:', (e as Error)?.message);
  }
  if (!reExtractSync.ok) {
    console.error('[KSD5] reExtract: publication committed, but post-commit reload failed:', reExtractSync.reason);
    return null;
  }
  await stampReceipt();
  // D3 — refresh the denormalized coverage snapshot so the Coverage
  // Matrix / readiness gauges reflect the new stamps without waiting for
  // the next CAF run. Best-effort: a failure leaves `site.coverage` stale
  // (the BEST-of sticky problem), never blocks the scan result.
  if (stampedRequirements.length > 0) {
    try {
      const { writeSiteCoverage } = await import('./cafService');
      await writeSiteCoverage(siteId);
    } catch (e) {
      console.warn(`[req-doc-rescan] coverage refresh failed for ${siteId}:`, (e as Error)?.message);
    }
  }
  // Audit-log: caller is best-effort; never throws.
  try {
    const { emitAuditEvent } = await import('../lib/auditLogClient');
    void emitAuditEvent({
      action: 'document.classify_version_bump',
      target: { type: 'document', id: docId, label: doc.fileName },
      metadata: {
        siteId,
        ksd5: true,
        affectedSections: affected,
        changedSections: changed,
        stampedRequirements,
      },
    });
  } catch {
    // Soft-fail: audit is non-blocking.
  }
  return { affectedSections: affected, changedSections: changed, stampedRequirements, sedVersion: sed.version };
}
